import * as THREE from './three/three.module.js';

import { generateTangents, ready } from './three/examples/jsm/libs/mikktspace.module.js';

import { Transformation2, Transformation3} from './transform.js';
import { BufferAllocator} from './allocator.js';
import { MaterialPool , SimpleBone, SimpleSkeleton, RT_SPEC, RT_DIFF, RT_METALCOAT, RT_CARCOAT, RT_REFR, EngineBase, smoke_params, drift_params, candle_params,sand_params, firework_params, carColors } from './engine_base';
import { ParticleEngine } from './three/ParticleEngine.js';
import { TextGeometry } from './three/examples/jsm/geometries/TextGeometry.js';

const transformSize = 15;
const boneSize = 16;
const packedBoneSize = 12;

const uv3_attr  = ",\n@location(11) uv3: vec2<f32>";

const vertex_color_attr = ",\n@location(12) vcolor: vec4<f32>";
const vertex_color_on  = ` output.color = vcolor;`;
const vertex_color_off = ` output.color = vec4(0.0, 0.0, 0.0, 1.0);`;

const vertex_uv3_on  = ` output.uv3 = uv3;`;
const vertex_uv3_off = ` output.uv3 = vec2(0.0, 0.0);`;

const secmap_on = `    
    @group(1) @binding(5) var driftmap : texture_2d<f32>;
    @group(1) @binding(6) var secmap : texture_2d<f32>;
    @group(1) @binding(7) var secnmap : texture_2d<f32>;
    
`

const skinning_uniform = `
    @group(2) @binding(0) var<uniform> bonesRest : array<Bone, 32>;
    @group(2) @binding(1) var<storage,read> bones : array<Bone>;

`

const skinning_attr = `
@location(1) bonesIndex: u32, 
@location(2) weight: u32,
`

const skinning_vtx_off  = `var skinnedPosition = position;`
const skinning_norm_off = `var skinnedNormal = normal;`

const skinning_vtx_on = `
    
    var idx1 = bonesIndex & 0xFF;
    var idx2 = (bonesIndex >> 8) & 0xFF;
    var idx3 = (bonesIndex >> 16) & 0xFF;
    var idx4 = (bonesIndex >> 24) & 0xFF;
    
    var boneRest1 = bonesRest[idx1];
    var boneRest2 = bonesRest[idx2];
    var boneRest3 = bonesRest[idx3];
    var boneRest4 = bonesRest[idx4];

    var bone_cnt = u32(size.y);

    var bone1 = bones[idx1 + instance_idx * bone_cnt];
    var bone2 = bones[idx2 + instance_idx * bone_cnt];
    var bone3 = bones[idx3 + instance_idx * bone_cnt];
    var bone4 = bones[idx4 + instance_idx * bone_cnt];

    var skinnedPosition = vec3(0.0, 0.0, 0.0);
   
    var fweight = vec4<f32>(  f32(weight & 0xFF), f32((weight >> 8) & 0xFF), f32((weight >> 16) & 0xFF), f32((weight >> 24) & 0xFF)) / 255.0;

    var tb1 = transpose(bone1.rot * boneRest1.rot);
    var tb2 = transpose(bone2.rot * boneRest2.rot);
    var tb3 = transpose(bone3.rot * boneRest3.rot);
    var tb4 = transpose(bone4.rot * boneRest4.rot);

    skinnedPosition +=  (((position + boneRest1.pos ) * tb1) + bone1.pos) * fweight.x;
    skinnedPosition +=  (((position + boneRest2.pos ) * tb2) + bone2.pos) * fweight.y;
    skinnedPosition +=  (((position + boneRest3.pos ) * tb3) + bone3.pos) * fweight.z;
    skinnedPosition +=  (((position + boneRest4.pos ) * tb4) + bone4.pos) * fweight.w;
`

const skinning_norm_on = `
var skinnedNormal = vec3(0.0, 0.0, 0.0);

skinnedNormal +=  normal * tb1 * fweight.x;
skinnedNormal +=  normal * tb2 * fweight.y;
skinnedNormal +=  normal * tb3 * fweight.z;
skinnedNormal +=  normal * tb4 * fweight.w;
`

const driftmap_mix = `

    const DriftMapFlag = 7;
    var th = textureSample(driftmap, Sampler, uv3).r;

    if((DriftMapFlag & 1)!=0){
        texelFx.r = max(0.0, min(1.0, texelFx.r - th));
    }
    if((DriftMapFlag & 2)!=0){
        texelFx.g = max(0.0, min(1.0, texelFx.g + th));
    }
    if((DriftMapFlag & 4)!=0){
        texelFx.b = max(0.0, min(1.0, texelFx.b - th));
    }
`


const secmap_mix = `    

    var texel2 = textureSample(secmap, Sampler, buv);

    if((texelFx.g > 0.55)&&(texelFx.g < 0.65)){
        texel = vec4(0.0, 1.0, 1.0, 1.0);
    }else{
        texel = mix(texel2, texel, texelFx.g) ;
    }

    var normal2 = normalize((textureSample(secnmap, Sampler, buv).xyz - 0.5) * 2.0);
    mapNormal = mix(normal2, mapNormal, texelFx.g) ;
`

const v_shadow_on  =` 
    // XY is in (-1, 1) space, Z is in (0, 1) space 
    var posFromLight = global.lightMat * vec4(output.worldPos, 1.0);
    // Convert XY to (0, 1)
    // Y is flipped because texture coords are Y-down.
    output.shadowPos = vec3( posFromLight.xy * vec2(0.5, -0.5) + vec2(0.5), posFromLight.z );`;

const v_shadow_off  = ` output.shadowPos = vec3(0.0); `
const f_shadow_on  =` 
    var visibility = 0.0;

    if(global.shadowParams.y>0){

        var shadowDepthTextureSize = global.shadowParams.z;
        var oneOverShadowDepthTextureSize = 1.0 / shadowDepthTextureSize;

        var size = i32(global.shadowParams.y);
        for (var y = -size; y <= size; y++) {
            for (var x = -size; x <= size; x++) {
                let offset = vec2<f32>(vec2(x, y)) * oneOverShadowDepthTextureSize;
                visibility += textureSampleCompare(shadowMap, shadowSampler,  shadowPos.xy + offset, shadowPos.z - global.shadowParams.x);
            }
        }
        visibility /= ((global.shadowParams.y * 2 + 1)*(global.shadowParams.y * 2 + 1));;
    }else{
        visibility = textureSampleCompare(shadowMap, shadowSampler,  shadowPos.xy, shadowPos.z - global.shadowParams.x);
    }
    
`

const f_shadow_off  =` var visibility = 1.0; `;

function parseSecMap(type, enable, data){

    if(type === 0){
        return data;
    }
    if(type === 1){
        let ret = data.replace('{{secmap}}', enable ? secmap_on : '');
        ret = ret.replace('{{secmap_mix}}', enable ? secmap_mix : '');
       return ret;
    }
}



function parseDriftMap(type, enable, data){

    if(type === 0){
        let ret = data.replace('{{vertex_uv3}}', enable ? vertex_uv3_on : vertex_uv3_off);
        ret = ret.replace('{{uv3_attr}}', enable ? uv3_attr : '');
        return ret;
    }
    if(type === 1){
        let ret = data.replace('{{driftmap_mix}}', enable ? driftmap_mix : '');
       return ret;
    }
}

function parseSkinning(type, enable, data){

    if(type === 0){

        let ret = data.replace('{{skinning_attr}}', enable ? skinning_attr : '');
        ret = ret.replace('{{skinning_vtx}}', enable ? skinning_vtx_on : skinning_vtx_off);
        ret = ret.replace('{{skinning_norm}}', enable ? skinning_norm_on : skinning_norm_off);
        ret = ret.replace('{{skinning_uniform}}', enable ? skinning_uniform : '');
        return ret;
    }

    if(type === 1)
        return data;

}

function parseVColor(type, enable, data){

    if(type === 0){
        let ret = data.replace('{{vertex_color}}', enable ? vertex_color_on : vertex_color_off);
        ret = ret.replace('{{vertex_color_attr}}', enable ? vertex_color_attr : '');
        return ret;
    }
    if(type === 1){
       return data;
    }
}

function parseShadow(type, enable, data){

    if(type === 0 ){
        return data.replace('{{shadow}}', enable ? v_shadow_on : v_shadow_off);
    }

    if(type === 1 ){
       return data.replace('{{shadow}}', enable ? f_shadow_on : f_shadow_off);
    }
}

function parseParallax(type, enable, data){

    if(type === 1 ){

        const parallax_on_1  =`  let pout = parallaxMapping(normalize(toCam), buv); buv = pout.uv; bh = pout.bh; `;
        const parallax_on_2  =`  shadow = parallaxSoftShadowMultiplier(normalize(radiance.dir * tbnW), buv, bh); `;

        const step1 = data.replace('{{parallax_1}}', enable ? parallax_on_1 : '');
        return step1.replace('{{parallax_2}}', enable ? parallax_on_2 : '');
    }

}


const SHADER_GLOBAL = `
    struct Light{
        vec : vec4<f32>,
        vec2 : vec4<f32>,
        color : vec4<f32>,
    };

    struct LightRadiance{
        radiance : vec3<f32>,
        dir : vec3<f32>
    };

    struct Global{
        projMat : mat4x4<f32>,
        viewMat : mat3x3<f32>,
        viewPos : vec3<f32>,
        lightMat : mat4x4<f32>,
        fogParams : vec4<f32>,
        lights : array<Light, 16>,
        shadowParams : vec4<f32>
    };

    struct Bone {
        rot : mat3x3<f32>,
        pos : vec3<f32>
    }

    fn reinhard2(x : vec3<f32> ) -> vec3f {
        let L_white: f32  = 4.0;
        return (x * (1.0 + x / (L_white * L_white))) / (1.0 + x);
    }

  fn Uncharted2Tonemap(color : vec3<f32>) -> vec3f {
    let A : f32 = 0.15;
    let B : f32 = 0.50;
    let C : f32 = 0.10;
    let D : f32 = 0.20;
    let E : f32 = 0.02;
    let F : f32 = 0.30;
    let W : f32 = 11.2;
    return ((color*(A*color+C*B)+D*E)/(color*(A*color+B)+D*F))-E/F;
  }

  /* fn mod289(x:f32) -> f32 {return x - floor(x * (1.0 / 289.0)) * 289.0;} */
  fn mod289(x:vec4<f32>) -> vec4<f32> {return x - floor(x * (1.0 / 289.0)) * 289.0;}
  fn perm(x:vec4<f32>) -> vec4<f32> {return mod289(((x * 34.0) + 1.0) * x);} 
  fn snoise(ip:vec3<f32>) -> f32{ 

    var p = ip;
    var a = floor(p); 
    var d = p - a; 
    d = d * d * (3.0 - 2.0 * d); 
    var b = a.xxyy + vec4<f32>(0.0, 1.0, 0.0, 1.0); 
    var k1 = perm(b.xyxy); 
    var k2 = perm(k1.xyxy + b.zzww); 
    var c = k2 + a.zzzz; 
    var k3 = perm(c); 
    var k4 = perm(c + 1.0); 
    var o1 = fract(k3 * (1.0 / 41.0)); 
    var o2 = fract(k4 * (1.0 / 41.0)); 
    var o3 = o2 * d.z + o1 * (1.0 - d.z); 
    var o4 = o3.yw * d.x + o3.xz * (1.0 - d.x); 
    return o4.y * d.y + o4.x * (1.0 - d.y);
  }
  
  fn FMB(ip:vec3<f32>) -> f32
   {
        var p = ip;
       var value = 0.0;
       var amplitude = 0.5;
       var frequency = 0.0;
       for(var i = 0; i<6; i++)
       {
          value+= amplitude * snoise(p);
          p*=2.0;
          amplitude *= 0.5;
      }
      return value;
  }
  
    fn getFog(ivp : vec3<f32>, depth: f32, color: vec3<f32>) -> vec3f {

        if(global.fogParams.x < 0.00001){
            return color;
        }

        var vp = vec3<f32>(ivp.x, ivp.y -670.0, ivp.z); 
        var fogDir = normalize(vp - global.viewPos); 
        var nsc = vp * 0.025; 
        var ns = FMB(nsc + FMB(nsc) * 0.5 + 0.5); 
        var fogDepth = depth * ns; 
        //fogDepth *= mix(ns, 1.0, saturate(fogDepth - 5000.0)/ 5000.0);
        
        var fogFactor =  0.025* (exp(-global.viewPos.y * global.fogParams.x * global.fogParams.x))*(1.0 - exp( - fogDir.y * global.fogParams.x * global.fogParams.x * fogDepth * fogDepth )/fogDir.y); 
        fogFactor=clamp(fogFactor, 0.0, 0.95); 
        return mix(color, global.fogParams.yzw, fogFactor);

        /*    
        var vp = vec3<f32>(ivp.x, ivp.y - 70.0, ivp.z);
        var fogDir = normalize(vp - global.viewPos); 
        var nsc = vp * 0.025; 
        var ns = FMB(nsc + FMB(nsc) * 0.5 + 0.5); 
        var fogDepth = depth * ns; 
        //fogDepth *= mix(ns, 1.0, saturate(fogDepth - 5000.0)/ 5000.0);
        var fogFactor = 0.035* (exp(-global.viewPos.y * global.fogParams.x * global.fogParams.x))*(1.0 - exp( - fogDir.y * global.fogParams.x * global.fogParams.x * fogDepth * fogDepth )/fogDir.y); 
        fogFactor = clamp(fogFactor, 0.0, 0.9);
        return mix(color, global.fogParams.yzw, fogFactor);
        */

    }



    const RECIPROCAL_PI = 0.318309886183790671537767526745028724068919291480;
    const PI = 3.1459;
    const EPSILON = 0.000001;

    

    @group(0) @binding(0) var<uniform> global : Global;
    @group(0) @binding(1) var Sampler : sampler;
    @group(0) @binding(2) var envmap : texture_2d<f32>;
    @group(0) @binding(3) var SamplerN : sampler;
    @group(0) @binding(4) var shadowMap: texture_depth_2d;
    @group(0) @binding(5) var shadowSampler: sampler_comparison;

`

const SHADER_LIGHTNINH_GLOBAL = `
    /*
    fn getSpecular(NdotH:f32, NdotV: f32,NdotL: f32, F: vec3<f32>, roughness:f32 ) -> vec3<f32>
    {
        var a      = roughness;
        var a2     = a*a;
        var NdotH2 = NdotH*NdotH;
        
        var num   = a2;
        var denom = (NdotH2 * (a2 - 1.0) + 1.0);
        denom = PI * denom * denom;
        
        var NDF = num / denom;

        var r = (roughness + 1.0);
        var k = a / 8.0;
    
        num   = NdotV * NdotL;
        denom = (NdotV * (1.0 - k) + k) * (NdotL * (1.0 - k) + k);
        var G  = num / denom;

        var numerator   = NDF * G * F;
        var denominator = 4.0 * NdotV * NdotL + 0.0001;
        return numerator * model.specular.rgb / denominator;
    }
    */

    fn fresnelSchlick(cosTheta:f32, F0: vec3<f32> ) -> vec3<f32>
    {
        return saturate(F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 2.0));
    }

    fn getLightRadiance(light : Light, worldPos: vec3<f32>, visibility: f32) -> LightRadiance
    {
        var radiance: LightRadiance;
        var vecToLight: vec3<f32>;
        var distToLight: f32;
        var attenuation: f32;

        var ldir = normalize(light.vec2.xyz);

        //directional 
        if(light.color.a < 1.5) {
            vecToLight = light.vec.xyz - worldPos;
            distToLight = length(vecToLight);
            attenuation = visibility;
            radiance.dir = -normalize( light.vec.xyz) ;
        } else if(light.color.a < 2.5) {
            vecToLight = light.vec.xyz - worldPos;
            distToLight = length(vecToLight);
            radiance.dir = normalize(vecToLight);
            attenuation =  visibility  / ( distToLight * distToLight * light.vec.w) ;
        } else if(light.color.a < 3.5) {
            vecToLight = light.vec.xyz - worldPos;
            distToLight = length(vecToLight);
            radiance.dir = normalize(vecToLight);
            var angleDot = max(0.0, dot(radiance.dir, ldir));
            attenuation =  pow(angleDot, light.vec2.w) / ( distToLight * distToLight * light.vec.w);
        }

        radiance.radiance = attenuation * light.color.rgb;

        return radiance;
    }

    fn D_GGX(cos_theta_m : f32, ai : f32) -> f32{
        let alpha = ai;
        var a = cos_theta_m * alpha;
        var k = alpha / (1.0 - cos_theta_m * cos_theta_m + a * a);
        return k * k * (1.0 / PI);
    }
    
    // From Earl Hammon, Jr. "PBR Diffuse Lighting for GGX+Smith Microsurfaces" https://www.gdcvault.com/play/1024478/PBR-Diffuse-Lighting-for-GGX
    fn V_GGX(NdotL : f32, NdotV  : f32, alpha : f32) -> f32{
        return 0.5 / mix(2.0 * NdotL * NdotV, NdotL + NdotV, alpha);
    }
    
    fn SchlickFresnel(u: f32) -> f32{
        var m = 1.0 - u;
        var m2 = m * m;
        return m2 * m2 * m; // pow(m,5)
    }
    
    fn F0(mt:f32, spc:f32, cl:vec3<f32>) -> vec3<f32>{
        var dielectric = 0.16 * spc * spc;
        // use albedo * metallic as colored specular reflectance at 0 angle for metallic materials;
        // see https://google.github.io/filament/Filament.md.html
        return mix(vec3(dielectric), cl.rgb, vec3(mt));
    }

    struct lightRet{
        diff : vec3<f32>,
        spec : vec3<f32>
    }

    fn specularContribution(L : vec3<f32>, V : vec3<f32>, N : vec3<f32>, ALBEDO : vec3<f32> , LIGHT_COLOR :vec3<f32> ,  f0 : vec3<f32>, metallic : f32, ROUGHNESS : f32, shadow : f32) -> lightRet {

        /*vec3 light_dir = -L * tbn;*/
        var NdotL = min(dot(N, L), 1.0);
        var cNdotL = max(NdotL, 0.0);
        var NdotV = dot(N, V);
        var cNdotV = max(NdotV, 1e-4);
        var H = normalize(V + L);
        var cLdotH = clamp(dot(L, H), 0.0, 1.0);
        var cNdotH = clamp(dot(N, H), 0.0, 1.0);
        var cLdotH5 = SchlickFresnel(cLdotH);
        /*let shadow = 1.0;*/
        /*float shadow = GetParallaxShadow(base_uv, light_dir);*/

        var diffuse_brdf_NL = cNdotL * (1.0 ); //built-in Godot lambert diffuse solution
        var D = D_GGX(cNdotH, ROUGHNESS);
        var G = V_GGX(cNdotL, cNdotV, ROUGHNESS);
        /*var f0 = F0(metallic, specular, ALBEDO);*/
        var f90 = clamp(dot(f0, vec3(50.0 * 0.33)), metallic, 1.0);
        var F = f0 + (f90 - f0) * cLdotH5;
        var specular_brdf_NL = cNdotL * D * F * G; //built-in Godot specular SchlickGGX solution
        
        let SPECULAR_AMOUNT = 0.4;
        let DIFFUSE_LIGHT = diffuse_brdf_NL * ALBEDO * LIGHT_COLOR * shadow;
        let SPECULAR_LIGHT = specular_brdf_NL * LIGHT_COLOR * SPECULAR_AMOUNT;

        return lightRet(DIFFUSE_LIGHT , SPECULAR_LIGHT);
    }

    fn F_SchlickR(cosTheta : f32, F0 : vec3<f32>, roughness : f32) -> vec3f {
        return F0 + (max(vec3(1.0 - roughness * roughness), F0) - F0) * pow(1.0 - cosTheta, 2.0);
    }


    /*
    fn D_GGX(dotNH : f32, roughness : f32) -> f32 {
        let alpha : f32 = roughness * roughness;
        let alpha2 : f32 = alpha;
        let denom : f32 = dotNH * dotNH * (alpha2 - 1.0) + 1.0;
        return (alpha2)/(PI * denom*denom);
      }
      fn G_SchlicksmithGGX(dotNL : f32, dotNV : f32, roughness : f32) -> f32 {
        let r : f32 = (roughness + 1.0);
        let k : f32 = (r*r) / 8.0;
        let GL : f32 = dotNL / (dotNL * (1.0 - k) + k);
        let GV : f32 = dotNV / (dotNV * (1.0 - k) + k);
        return GL * GV;
      }
      
      fn F_Schlick(cosTheta : f32, materialcolor : vec3<f32>, metallic : f32) -> vec3f {
        let F0 : vec3<f32> = mix(vec3(0.04), materialcolor, metallic);
        let F : vec3<f32> = F0 + (1.0 - F0) * pow(1.0 - cosTheta, 5.0);
        return F;
      }
      fn F_SchlickR(cosTheta : f32, F0 : vec3<f32>, roughness : f32) -> vec3f {
        return F0 + (max(vec3(1.0 - roughness * roughness), F0) - F0) * pow(1.0 - cosTheta, 2.0);
      }
      fn specularContribution(L : vec3<f32>, V : vec3<f32>, N : vec3<f32>, ALBEDO : vec3<f32> , lightColor :vec3<f32> ,  F0 : vec3<f32>, metallic : f32, roughness : f32) -> vec3f {
        // Precalculate vectors and dot products
        let H : vec3<f32> = normalize(V + L);
        let dotNH : f32 = max(dot(N, H), 0.0);//, 1.0);
        let dotNV : f32 = max(dot(N, V), 0.0);//, 1.0);
        let dotNL : f32 = max(dot(N, L), 0.0);//, 1.0);
        let dotLH : f32 = max(dot(L, H), 0.0);//, 1.0);

        var color : vec3<f32> = vec3(0.0);

        if (dotNL > 0.0) {
            // D = Normal distribution (Distribution of the microfacets)
            let D : f32 = D_GGX(dotNH, roughness);
            // G = Geometric shadowing term (Microfacets shadowing)
            let G : f32 = G_SchlicksmithGGX(dotNL, dotNV, roughness);

            let F = F_SchlickR(dotNV, F0,  roughness) * ALBEDO;

            // F = Fresnel factor (Reflectance depending on angle of incidence)
            //let F : vec3<f32> = F_Schlick(dotNV, ALBEDO, metallic);
            let spec : vec3<f32> = lightColor * D * F * G / (4.0 * dotNL * dotNV + 0.001);
            let kD : vec3<f32> = (1.0 - F) * (1.0 - metallic);
            color += (ALBEDO * lightColor * kD ) * dotNL  + spec;
        }

        return color;
    }
    */
`

const PARALLAX_GLOBAL = `

struct ParalaxOut{
    uv : vec2<f32>,
    bh : f32
}

@group(1) @binding(5) var tBumpMap : texture_2d<f32>;

fn parallaxMapping( V:vec3<f32>, T : vec2<f32>) -> ParalaxOut
{
  let bumpMapScale = 1.0 / model.texBox.zw;
   // determine optimal number of layers 
  const minLayers = 5.0;
  const maxLayers = 20.0;
  var numLayers = mix(maxLayers, minLayers, abs(dot(vec3(0, 0, 1), V)));
  // height of each layer
  var layerHeight = 1.0 / numLayers;
  // current depth of the layer
  var curLayerHeight = 0.0;
  // shift of texture coordinates for each layer
  var dtex = model.params.y * V.xy / V.z / numLayers;
  // current texture coordinates
  var currentTextureCoords = T;

  // depth from heightmap
  var heightFromTexture = 1.0-textureSampleLevel(tBumpMap, Sampler, currentTextureCoords * bumpMapScale, 0.0).r;
  var numIter = 0;

 // while point is above the surface
  while((heightFromTexture > curLayerHeight) &&(numIter<32))
  {
       // to the next layer
       curLayerHeight += layerHeight; 
       // shift of texture coordinates
       currentTextureCoords -= dtex;
       // new depth from heightmap
       heightFromTexture = 1.0-textureSampleLevel(tBumpMap, Sampler, currentTextureCoords * bumpMapScale, 0.0).r;
       numIter++;
   }

  ///////////////////////////////////////////////////////////
  // previous texture coordinates
  var prevTCoords = currentTextureCoords + dtex;
  // heights for linear interpolation
  var nextH = heightFromTexture - curLayerHeight;
  var prevH = 1.0-textureSampleLevel(tBumpMap, Sampler, prevTCoords * bumpMapScale, 0.0).r - curLayerHeight + layerHeight;
  // proportions for linear interpolation
  var weight = nextH / (nextH - prevH);

  // interpolation of texture coordinates
  var finalTexCoords = prevTCoords * weight + currentTextureCoords * (1.0-weight);
  // interpolation of depth values
  // return result
  return ParalaxOut(finalTexCoords, curLayerHeight + prevH * weight + nextH * (1.0 - weight));
  
}

// FUNCTION I ADDED FOR SHADOW CALCULATION
fn parallaxSoftShadowMultiplier( lightDir : vec3<f32>, texCoord: vec2<f32>, initialHeight: f32 ) -> f32
{
    
   let bumpMapScale = 1.0 / model.texBox.zw;
   const minLayers = 5.0;
   const maxLayers = 32.0;

   var numLayers = mix(maxLayers, minLayers, abs(dot(vec3(0.0, 0.0, 1.0), lightDir)));

   var layerDepth = 1.0 / numLayers;

   var currentLayerDepth = 0.0;

   var P = lightDir.xy / lightDir.z * model.params.y;

   var deltaTexCoords = P / numLayers;
   var currentTexCoords = texCoord;
   var currentDepthMapValue = 1.0 - textureSampleLevel(tBumpMap, Sampler, currentTexCoords * bumpMapScale, 0.0).r;

   var numIter = 0;

   while ((currentLayerDepth < currentDepthMapValue)&&(numIter<32))
   {
      currentTexCoords -= deltaTexCoords;
      currentDepthMapValue = 1.0 - textureSampleLevel(tBumpMap, Sampler, currentTexCoords* bumpMapScale, 0.0).r;
      currentLayerDepth += layerDepth;
      numIter++;
   }

   if(currentDepthMapValue > currentLayerDepth){
        return 0.0;
    }else{
        return 1.0;
    }
}
`

const SIMPLE_MESH_VERTEX_SHADER = `

    struct Model {
        params : vec4<f32>,
        diffuse : vec4<f32>,
        emissive : vec4<f32>,
        ambiant : vec4<f32>,
        specular : vec4<f32>,
    };

    @group(1) @binding(0) var<uniform> model : Model;

    {{skinning_uniform}}

    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) uv: vec2<f32>,
        @location(1) worldPos: vec3<f32>,
        @location(2) eyePos: vec3<f32>,
        @location(3) worldNormal: vec3<f32>,
        @location(4) eyeNormal: vec3<f32>,
        @location(5) shadowPos: vec3<f32>
    };

    @vertex
    fn main(
    @builtin(instance_index) instance_idx: u32,
    @location(0) position: vec3<f32>,
    {{skinning_attr}}
    @location(3) normal: vec3<f32>,
    @location(4) uv: vec2<f32>,

    @location(5) m0: vec3<f32>,
    @location(6) m1: vec3<f32>,
    @location(7) m2: vec3<f32>,
    @location(8) pos: vec3<f32>,
    @location(9) size: vec3<f32>

    ) -> VertexOutput {
    
    var output: VertexOutput;

    {{skinning_vtx}}
    {{skinning_norm}}

     var rot = mat3x3<f32>(m0, m1, m2);
   
    output.worldPos = (rot * (skinnedPosition * size.x)) + pos;
    output.eyePos = global.viewMat *  (output.worldPos + global.viewPos);
    output.Position = global.projMat * vec4(output.eyePos, 1.0);

    output.worldNormal = normalize(rot * skinnedNormal);
    output.eyeNormal = normalize(output.worldNormal * global.viewMat);
    output.uv = uv;
        
    {{shadow}}

    return output;
}`;


const SIMPLE_MESH_FRAGMENT_SHADER = `

    struct Model {
        params : vec4<f32>,
        diffuse : vec4<f32>,
        emissive : vec4<f32>,
        ambiant : vec4<f32>,
        specular : vec4<f32>,
    };

    @group(1) @binding(0) var<uniform> model : Model;
    @group(1) @binding(1) var roughness: texture_2d<f32>;

    @fragment
    fn main(
    @builtin(position) Position: vec4<f32>,
    @location(0) uv: vec2<f32>,
    @location(1) worldPos: vec3<f32>,
    @location(2) eyePos: vec3<f32>,
    @location(3) wNormal: vec3<f32>,
    @location(4) eNormal: vec3<f32>,
    @location(5) shadowPos: vec3<f32>
    ) -> @location(0) vec4<f32> {

        var eyeNormal = normalize(eNormal);
        var worldNormal = normalize(wNormal);

        var viewDir = normalize((-global.viewPos) - worldPos);
        var wReflectDir = reflect(viewDir, worldNormal);



        var rtex = textureSample(roughness, Sampler, uv).r;

        //return vec4(vec3(rtex), 1.0);

        var roughness = model.specular.a;

        //wReflectDir = normalize( mix( wReflectDir, worldNormal, roughness) );
        
        var euv = vec2<f32>(atan2( wReflectDir.z, wReflectDir.x ) * 0.15915494309189535 + 0.5, 1.0 - asin( clamp( wReflectDir.y, - 1.0, 1.0 ) ) * 0.3183098861837907 + 0.5);
        var env = textureSample(envmap, Sampler, euv).rgb;

        {{shadow}}

        var vis: f32;
        var lightCol = vec3(0.0, 0.0, 0.0);
        var lightSpecular  = vec3(0.0, 0.0, 0.0);
        var lightDiffuse  = vec3(0.0, 0.0, 0.0);
    
        var metallic = model.ambiant.a;
       
        var F0 = vec3(0.04);
        F0 = mix(F0 , model.diffuse.rgb, metallic);

        for(var n:i32 =0;n<16;n++)
        {
            var ret : lightRet;
            var attenuation = 1.0;
            var NdL : f32;
            var NdV : f32;
            var NdH : f32;
            var HdV : f32;
            var halfDir : vec3<f32>;
            var radiance : LightRadiance;
               
            if (global.lights[n].color.a < 0.5) {
                continue;
            }
    
            if((global.lights[n].color.a < 1.5)&&(n>0)){
                vis = visibility * 0.8 + 0.2;
            }else{
                vis = visibility * 0.5 + 0.5;
            }
            radiance = getLightRadiance(global.lights[n], worldPos, vis);

            ret = specularContribution(radiance.dir, viewDir, worldNormal, model.diffuse.rgb,  radiance.radiance,  F0, metallic,  1.0-roughness, 1.0);

            lightSpecular += vis * ret.spec;
            lightDiffuse += vis * ret.diff;
        }

        F0 = vec3(0.04);
        F0 = mix(F0 , model.diffuse.rgb, metallic);
        let F : vec3<f32> = F_SchlickR(max(dot(worldNormal, viewDir), 0.0), F0,  roughness * roughness ) ;
       
         /*
        vis = visibility * 0.8 + 0.2;
        // Specular reflectance
        let specular : vec3<f32> = vis * env.rgb * F;
        // Ambient part
        var kD : vec3<f32> = 1.0 - F;
        kD *= 1.0 - metallic;
        let ambient : vec3<f32> = (kD * model.diffuse.rgb + specular);
        */
    
        var color : vec3<f32> = model.ambiant.rgb + lightSpecular + mix(lightDiffuse, env * 0.8 , metallic);

        color =  color / (1.0f + color);
        color += model.emissive.rgb *  model.emissive.a;
            
        return vec4( getFog(worldPos, eyePos.z,  color),  model.diffuse.a);
    }
`;

const UI3DMESH_VERTEX_SHADER = `

    struct Model {
        diffuse : vec4<f32>,
        params :  vec4<f32>
    };

    @group(1) @binding(0) var<uniform> model : Model;
    
    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) worldPos: vec3<f32>,
        @location(1) eyePos: vec3<f32>,
        @location(2) worldNormal: vec3<f32>,
        @location(3) eyeNormal: vec3<f32>,
        @location(4) shadowPos: vec3<f32>
    };
      
    @vertex
    fn main(
    @location(0) position: vec3<f32>,
    @location(1) normal: vec3<f32>,
    
    @location(2) m0: vec3<f32>,
    @location(3) m1: vec3<f32>,
    @location(4) m2: vec3<f32>,
    @location(5) pos: vec3<f32>,
    @location(6) size: vec3<f32>
    ) -> VertexOutput {
    
    var output: VertexOutput;
 
    var rot = mat3x3<f32>(m0, m1, m2);
    
    output.worldPos = (rot * (position * vec3(size.x, size.y, 1.0) )) + pos;
    output.eyePos = global.viewMat *  (output.worldPos + global.viewPos);
    output.Position = global.projMat * vec4(output.eyePos, 1.0);

    output.worldNormal = normalize(rot * normal);
    output.eyeNormal = normalize(output.worldNormal * global.viewMat);

     {{shadow}}

    return output;
}`;

const UI3DMESH_FRAGMENT_SHADER = ` 

   struct Model {
        diffuse : vec4<f32>,
        params :  vec4<f32>
    };


@group(1) @binding(0) var<uniform> model : Model;
@group(1) @binding(1) var texture : texture_2d<f32>;

@fragment
fn main(
    @builtin(position) Position: vec4<f32>,
    @location(0) worldPos: vec3<f32>,
    @location(1) eyePos: vec3<f32>,
    @location(2) wNormal: vec3<f32>,
    @location(3) eNormal: vec3<f32>,
    @location(4) shadowPos: vec3<f32>
    
    ) -> @location(0) vec4<f32> {


        var eyeNormal = normalize(eNormal);
        var worldNormal = normalize(wNormal);

        var viewDir = normalize((-global.viewPos) - worldPos);
      
        {{shadow}}

        var vis: f32;
        var lightCol = vec3(0.0, 0.0, 0.0);
        var lightSpecular  = vec3(0.0, 0.0, 0.0);
        var lightDiffuse  = vec3(0.0, 0.0, 0.0);
    
        var F0 = vec3(0.04);
        F0 = mix(F0 , model.diffuse.rgb, 0.02);

        for(var n:i32 =0;n<16;n++)
        {
            var ret : lightRet;
            var attenuation = 1.0;
            var NdL : f32;
            var NdV : f32;
            var NdH : f32;
            var HdV : f32;
            var halfDir : vec3<f32>;
            var radiance : LightRadiance;
               
            if (global.lights[n].color.a < 0.5) {
                continue;
            }
    
            if((global.lights[n].color.a < 1.5)&&(n>0)){
                vis = visibility * 0.8 + 0.2;
            }else{
                vis = visibility * 0.5 + 0.5;
            }
            radiance = getLightRadiance(global.lights[n], worldPos, vis);

            ret = specularContribution(radiance.dir, viewDir, worldNormal, model.diffuse.rgb,  radiance.radiance,  F0, 0.0,  0.6, 1.0);

            lightSpecular += vis * ret.spec;
            lightDiffuse += vis * ret.diff;
        }

        var color : vec3<f32> = lightDiffuse + lightSpecular+ model.diffuse.rgb * 0.3;
        return vec4(color,  model.diffuse.a);
    }
`;


const SIMPLE_TEXTURED_MESH_VERTEX_SHADER = `

    struct Model {
        diffuse : vec4<f32>,
        params :  vec4<f32>,
        texBox :  vec4<f32>,
        texMat :  mat2x2<f32>,
        texPos :  vec2<f32>
    };

    @group(1) @binding(0) var<uniform> model : Model;
    
    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) worldPos: vec3<f32>,
        @location(1) eyePos: vec3<f32>,
        @location(2) uv: vec2<f32>
    };

    @vertex
    fn main(
    @location(0) position: vec3<f32>,
    @location(1) uv: vec2<f32>,
    
    @location(2) m0: vec3<f32>,
    @location(3) m1: vec3<f32>,
    @location(4) m2: vec3<f32>,
    @location(5) pos: vec3<f32>,
    @location(6) size: vec3<f32>
    ) -> VertexOutput {
    
    var output: VertexOutput;
 
    var rot = mat3x3<f32>(m0, m1, m2);
    
    output.worldPos = (rot * (position * vec3(size.x, size.x, 1.0) )) + pos;
    output.eyePos = global.viewMat *  (output.worldPos + global.viewPos);
    output.Position = global.projMat * vec4(output.eyePos, 1.0);
    output.uv = uv;
    
    return output;
}`;

const SIMPLE_TEXTURED_MESH_FRAGMENT_SHADER = ` 

struct Model {
    diffuse : vec4<f32>,
    params :  vec4<f32>,
    texBox :  vec4<f32>,
    texMat :  mat2x2<f32>,
    texPos :  vec2<f32>
};

@group(1) @binding(0) var<uniform> model : Model;
@group(1) @binding(1) var texture : texture_2d<f32>;

@fragment
fn main(
    @builtin(position) Position: vec4<f32>,
    @location(0) worldPos: vec3<f32>,
    @location(1) eyePos: vec3<f32>,
    @location(2) uv: vec2<f32>
    
    ) -> @location(0) vec4<f32> {

        /* move the uv into 'real' 0.0 - 1.0 coordinates in the texture */
        var fracuv = fract(uv);

        if(fracuv.x < 0.0) { fracuv.x += 1.0; }
        if(fracuv.y < 0.0) { fracuv.y += 1.0; }

        /* apply texture tranformation */
        var tuv = (fracuv - vec2(0.5)) * model.texMat + vec2(0.5) + model.texPos;

        /* apply texture box */
        var buv = model.texBox.xy + tuv * model.texBox.zw;

        var texel =  textureSample(texture, Sampler, buv);

        return vec4(getFog(worldPos, eyePos.z, model.diffuse.rgb * texel.rgb), model.diffuse.a * texel.a);
    }
`;

const TEXTURED_MESH_VERTEX_SHADER = `
    struct Model {
        params :  vec4<f32>,
        diffuse : vec4<f32>,
        ambiant : vec4<f32>,
        specular : vec4<f32>,
        texBox :  vec4<f32>,
        texMat :  mat2x2<f32>,
        texPos :  vec2<f32>
    };

    @group(1) @binding(0) var<uniform> model : Model;

    {{skinning_uniform}}
    
    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) worldPos: vec3<f32>,
        @location(1) eyePos: vec3<f32>,
        @location(2) worldNormal: vec3<f32>,
        @location(3) eyeNormal: vec3<f32>,
        @location(4) shadowPos: vec3<f32>,
        @location(5) uv: vec2<f32>
    };

    @vertex
    fn main(
    @builtin(instance_index) instance_idx: u32,
    @location(0) position: vec3<f32>,
    {{skinning_attr}}
    @location(3) uv: vec2<f32>,
    @location(4) normal: vec3<f32>,

    @location(5) m0: vec3<f32>,
    @location(6) m1: vec3<f32>,
    @location(7) m2: vec3<f32>,
    @location(8) pos: vec3<f32>,
    @location(9) size: vec3<f32>
    ) -> VertexOutput {
    
    var output: VertexOutput;
   
    {{skinning_vtx}}
    {{skinning_norm}}

    var rot = mat3x3<f32>(m0, m1, m2);

    output.worldPos = (rot * (skinnedPosition * size.x)) + pos;
    output.eyePos = global.viewMat *  (output.worldPos + global.viewPos);
    output.Position = global.projMat * vec4(output.eyePos, 1.0);

    var angle = model.params.z * size.z;

    var ruv = vec2( (uv.x - 0.5) * cos(angle) + (uv.y - 0.5) * sin(angle) + 0.5, 
                    (uv.y - 0.5) * cos(angle) - (uv.x - 0.5) * sin(angle) + 0.5 );

    output.uv =  vec2(ruv.x + size.z % 2  * model.params.x, ruv.y + size.z / 2 * model.params.y);

    output.worldNormal = rot * skinnedNormal;
    output.eyeNormal = global.viewMat * output.worldNormal;
        
    {{shadow}}
    
    return output;
}`;

const TEXTURED_MESH_FRAGMENT_SHADER = `

    struct Model {
        params :  vec4<f32>,
        diffuse : vec4<f32>,
        ambiant : vec4<f32>,
        specular : vec4<f32>,
        texBox :  vec4<f32>,
        texMat :  mat2x2<f32>,
        texPos :  vec2<f32>
    };

    @group(1) @binding(0) var<uniform> model : Model;
    @group(1) @binding(1) var texture : texture_2d<f32>;
    @group(1) @binding(2) var etex : texture_2d<f32>;

    @fragment
    fn main(
    @builtin(position) Position: vec4<f32>,
    @location(0) worldPos: vec3<f32>,
    @location(1) eyePos: vec3<f32>,
    @location(2) worldNormal: vec3<f32>,
    @location(3) eyeNormal: vec3<f32>,
    @location(4) shadowPos: vec3<f32>,
    @location(5) uv: vec2<f32>,
    
    ) -> @location(0) vec4<f32> {

        var viewDir = normalize(eyePos);
        var wReflectDir = reflect(viewDir, worldNormal);

        var euv = vec2<f32>(atan2( wReflectDir.z, wReflectDir.x ) * 0.15915494309189535 + 0.5, asin( clamp( wReflectDir.y, - 1.0, 1.0 ) ) * 0.3183098861837907 + 0.5);
        var env = textureSample(envmap, Sampler, euv).rgb;

        /* move the uv into 'real' 0.0 - 1.0 coordinates in the texture */
        var fracuv = fract(uv);

        if(fracuv.x < 0.0) { fracuv.x += 1.0; }
        if(fracuv.y < 0.0) { fracuv.y += 1.0; }

        /* apply texture tranformation */
        var tuv = (fracuv - vec2(0.5)) * model.texMat + vec2(0.5) + model.texPos;

        /* apply texture box */
        var buv = model.texBox.xy + tuv * model.texBox.zw;

        var texel =  textureSample(texture, Sampler, buv);
        var emxel =  textureSample(etex, Sampler, buv);

        {{shadow}}
        var metallic = model.ambiant.a;
        var F0 = vec3(0.04);
        F0 = mix(F0 , model.diffuse.rgb,  metallic);
    
        var lightCol = vec3(0.0, 0.0, 0.0);
        var lightSpecular = vec3(0.0, 0.0, 0.0);
        var lightDiffuse = vec3(0.0, 0.0, 0.0);
        var vis: f32;

        let modelDiffuse = model.diffuse.rgb * texel.rgb;
       
       
        for(var n:i32 =0;n<16;n++) {
            var attenuation = 1.0;
            var ret : lightRet;
            var NdL : f32;
            var NdV : f32;
            var NdH : f32;
            var HdV : f32;
            var halfDir : vec3<f32>;
            var radiance : LightRadiance;
           
            if (global.lights[n].color.a < 0.5) {
                continue;
            }
         
            if((global.lights[n].color.a < 1.5)&&(n==0)){
                vis = visibility * 0.8 + 0.2;
            }else{
                vis = visibility * 0.5 + 0.5;
            }
                
            radiance = getLightRadiance(global.lights[n], worldPos, vis);

            ret = specularContribution(radiance.dir, viewDir, worldNormal, modelDiffuse, radiance.radiance ,  F0, metallic, model.specular.a, 1.0);

            lightSpecular += vis * ret.spec;
            lightDiffuse += vis * ret.diff;
        }

        /*
        vis = visibility * 0.8 + 0.2;
        F0 = mix(vec3(0.04), lightDiffuse,   model.ambiant.a);
        let F : vec3<f32> = F_SchlickR(max(dot(worldNormal, viewDir), 0.0), F0, model.specular.a * model.specular.a);
        
        // Specular reflectance
        let specular : vec3<f32> = env.rgb * F;
        // Ambient part
        var kD : vec3<f32> = 1.0 - F;
        kD *= 1.0 - model.ambiant.a;
        let ambient : vec3<f32> = (kD * modelDiffuse + specular);
        */
    
        var color : vec3<f32> =  lightSpecular + mix(lightDiffuse, env * 0.8 ,  metallic);
        color += emxel.rgb * model.params.w ;

        let gamma = 1.2;
        let exposure = 4.4;
        color = Uncharted2Tonemap(color * exposure);
        color = color * (1.0f / Uncharted2Tonemap(vec3<f32>(11.2f)));
        color = pow(color, vec3<f32>(1.0f / gamma));

        return vec4(  getFog(worldPos, eyePos.z, color.rgb),  model.diffuse.a * texel.a);
    }
`;



const PIXEL_LIGHTNING_MESH_VERTEX_SHADER = `

    struct Model {
        params : vec4<f32>,
        diffuse : vec4<f32>,
        specular : vec4<f32>,
        coat: vec4<f32>,
        texBox :  vec4<f32>,
        texMat :  mat2x2<f32>,
        texPos :  vec2<f32>,
    };

    @group(1) @binding(0) var<uniform> model : Model;
    

    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) worldPos: vec3<f32>,
        @location(1) eyePos: vec3<f32>,

        @location(2) toCam: vec3<f32>,

        @location(3) worldNormal: vec3<f32>,
        @location(4) worldTangent: vec3<f32>,
        @location(5) worldBinormal: vec3<f32>,

        @location(6) eyeNormal: vec3<f32>,
        @location(7) eyeTangent: vec3<f32>,
        @location(8) eyeBinormal: vec3<f32>,

        @location(9) shadowPos: vec3<f32>,
        @location(10) uv: vec2<f32>,
        @location(11) uv2: vec2<f32>,
        @location(12) uv3: vec2<f32>,
        @location(13) color: vec4<f32>
    };

    @vertex
    fn main(
    @location(0) position: vec3<f32>,
    @location(1) uv: vec2<f32>,
    @location(2) normal: vec3<f32>,
    @location(3) tangent: vec3<f32>,
    @location(4) binormal: vec3<f32>,
    @location(5) uv2: vec2<f32>,

    @location(6) m0: vec3<f32>,
    @location(7) m1: vec3<f32>,
    @location(8) m2: vec3<f32>,
    @location(9) pos: vec3<f32>,
    @location(10) size: vec3<f32>

    {{uv3_attr}}
    {{vertex_color_attr}}
    ) -> VertexOutput {
    
    
    var output: VertexOutput;

    var rot = mat3x3<f32>(m0, m1, m2);

    var totMat = global.viewMat * rot;
   
    output.worldPos = (rot * (position * size.x)) + pos;
    output.worldNormal = normalize(rot * normal);
    output.worldTangent = normalize(rot * tangent);
    output.worldBinormal = normalize(rot * -binormal);

    var tbnW = mat3x3<f32>(output.worldTangent, output.worldBinormal, output.worldNormal);
    var toCam = normalize(-global.viewPos - output.worldPos);
    output.toCam =  toCam * tbnW;

    output.eyeNormal = normalize(global.viewMat * output.worldNormal);
    output.eyeTangent = normalize(global.viewMat * output.worldTangent);
    output.eyeBinormal = normalize(global.viewMat * output.worldBinormal);

    output.eyePos = global.viewMat *  (output.worldPos + global.viewPos);
    output.Position = global.projMat * vec4(output.eyePos, 1.0);
    output.uv = uv;
    output.uv2 = uv2;

    
    
    {{vertex_color}}
    {{vertex_uv3}}
    {{shadow}}

    return output;
}`;

const PIXEL_LIGHTNING_MESH_FRAGMENT_SHADER = `

    struct Model {
        params : vec4<f32>,
        diffuse : vec4<f32>,
        specular : vec4<f32>,
        coat : vec4<f32>,
        texBox :  vec4<f32>,
        texMat :  mat2x2<f32>,
        texPos :  vec2<f32>,
    };

    @group(1) @binding(0) var<uniform> model : Model;
    @group(1) @binding(1) var texture : texture_2d<f32>;
    @group(1) @binding(2) var normalMap : texture_2d<f32>;
    @group(1) @binding(3) var roughnessMap : texture_2d<f32>;
    @group(1) @binding(4) var emissiveMap : texture_2d<f32>;
    {{secmap}}

    @fragment
    fn main(
        @builtin(position) Position: vec4<f32>,
        @location(0) worldPos: vec3<f32>,
        @location(1) eyePos: vec3<f32>,
        @location(2) toCam: vec3<f32>,

        @location(3) worldNormal: vec3<f32>,
        @location(4) worldTangent: vec3<f32>,
        @location(5) worldBinormal: vec3<f32>,
        
        @location(6) eyeNormal: vec3<f32>,
        @location(7) eyeTangent: vec3<f32>,
        @location(8) eyeBinormal: vec3<f32>,

        @location(9) shadowPos: vec3<f32>,
        @location(10) uv: vec2<f32>,
        @location(11) uv2: vec2<f32>,
        @location(12) uv3: vec2<f32>,
        @location(13) vcolor: vec4<f32>
    
    ) -> @location(0) vec4<f32> {


    /* move the uv into 'real' 0.0 - 1.0 coordinates in the texture */
    var fracuv = fract(uv);
    if(fracuv.x < 0.0) { fracuv.x += 1.0; }
    if(fracuv.y < 0.0) { fracuv.y += 1.0; }

    /* apply texture tranformation */
    var tuv = (fracuv - vec2(0.5)) * model.texMat + vec2(0.5) + model.texPos;
    var buv = model.texBox.xy + tuv * model.texBox.zw;

    var texelFx = vec3(1.0 * (1.0 - vcolor.r), 1.0 * (1.0 - vcolor.g), 1.0 * (1.0 - vcolor.b));
    
    {{driftmap_mix}}
    
    /* apply texture ox */
    

    //var tbnW = mat3x3<f32>(normalize(worldTangent), normalize(worldBinormal), normalize(worldNormal));
    //var tbnE = mat3x3<f32>(normalize(eyeTangent), normalize(eyeBinormal), normalize(eyeNormal));
    
    var bh = 0.0;
    var shadow = 1.0;

    {{parallax_1}}

    /* fetch texels */
    var texel = mix(textureSample(texture, Sampler, buv), textureSample(texture, Sampler, buv* 2.0), 0.5);

    //var emmission = textureSample(emissiveMap, Sampler, uv2).rgb;
    var emmission = textureSample(emissiveMap, Sampler, buv).rgb;

    var mapNormal = normalize((textureSample(normalMap, Sampler, buv).xyz - 0.5) * 2.0);
    var roughness = max(0.05, textureSample(roughnessMap, Sampler, buv).r * model.specular.a* texelFx.r);
    
    {{secmap_mix}}

    mapNormal = normalize(vec3(mapNormal.x * texelFx.r, mapNormal.y * texelFx.r, mapNormal.z));

    var pixNormalE = normalize(normalize(eyeTangent) * mapNormal.x + normalize(eyeBinormal) * mapNormal.y + eyeNormal * mapNormal.z) ;
    var pixNormalW = normalize(normalize(worldTangent) * mapNormal.x + normalize(worldBinormal) * mapNormal.y +  normalize(worldNormal) * mapNormal.z) ;

    //var pixNormalW = mapNormal * tbnW;
    //var pixNormalE = mapNormal * tbnE;

    var viewDir = normalize((-global.viewPos) - worldPos);
    //var viewDir = normalize(eyePos);
    
    {{shadow}}

    var wReflectDir = reflect(viewDir, pixNormalW);

    /* env map */
    var euv = vec2<f32>(atan2( wReflectDir.z, wReflectDir.x ) * 0.15915494309189535 + 0.5, 1.0-asin( clamp( wReflectDir.y, - 1.0, 1.0 ) ) * 0.3183098861837907 + 0.5);
    var env = textureSample(envmap, Sampler, euv).rgb;

    var lightCol = vec3(0.0, 0.0, 0.0);
    var lightSpecular = vec3(0.0, 0.0, 0.0);
    var lightDiffuse = vec3(0.0, 0.0, 0.0);

    //var modelDiffuse = mix(vec3(0.1,0.1,0.1), texel.rgb * model.diffuse.rgb, max(0.2, 1.0 - bh));

    var modelDiffuse = mix(vec3(0.1,0.4,0.7), texel.rgb * model.diffuse.rgb, texelFx.b);

    pixNormalW = normalize(mix(vec3(0.0, 0.0, 1.0), pixNormalW, max(0.2, 1.0-bh)));
    

    var metallic =min(1.0, model.params.z + (1.0 - texelFx.r));


    //roughness= 0.9;

    var F0 = vec3(0.04);
    var vis: f32;
    F0 = mix(F0 , modelDiffuse,  metallic);
    
    for(var n:i32 =0;n<16;n++) {
        var ret : lightRet;
        var attenuation = 1.0;
        var NdL : f32;
        var NdV : f32;
        var NdH : f32;
        var HdV : f32;
        var halfDir : vec3<f32>;
        var radiance : LightRadiance;
       
        if (global.lights[n].color.a < 0.5) {
            continue;
        }

        var norm :vec3<f32>;

        

        if((global.lights[n].color.a < 1.5)&&(n==0)){
            vis = visibility * 0.8 + 0.2;
           
        }else{
            vis = visibility * 0.2 + 0.8;
           
        }
        
        /*
        if(global.lights[n].color.a < 1.5){
            norm = pixNormalW;
        }else{
            norm = -pixNormalW;
        }
        */
         
        radiance = getLightRadiance(global.lights[n], worldPos, vis);

        /*shadow = parallaxSoftShadowMultiplier(radiance.dir * tbnW, buv, bh);*/

        /*{{parallax_2}}*/

        ret = specularContribution(radiance.dir, viewDir, pixNormalW, modelDiffuse, radiance.radiance ,  F0, metallic, roughness, 1.0);

        lightSpecular += vis * ret.spec;
        lightDiffuse += vis * ret.diff;
        
        if(model.coat.x > 0.0)
        {
            var nn = normalize( vec3(model.coat.zw, 1.0) * mapNormal);
            var coatNormal = normalize(normalize(worldTangent) * nn.x + normalize(worldBinormal) * nn.y +  normalize(worldNormal) * nn.z) ;
            var coatRoughness = model.coat.y;
            ret = specularContribution(radiance.dir, viewDir, coatNormal, modelDiffuse, vis * radiance.radiance ,  F0, metallic, coatRoughness, shadow);

            lightSpecular += vis * ret.spec;
            lightDiffuse += vis * ret.diff;
       }
    
    }

    //var envret = specularContribution(vec3(0,-1, 0), viewDir, normalize(pixNormalW * vec3(1.0,1.0,1.0)), env.rgb, vec3<f32>(1.0),   F0, metallic, roughness, 1.0);
     /*
    F0 = mix(vec3(0.04), lightDiffuse,  metallic);
    let F : vec3<f32> = F_SchlickR(max(dot(pixNormalW, viewDir), 0.0), F0, roughness * roughness);
    //var D = V_GGX(0.5, max(dot(pixNormalW, viewDir), 0.0), roughness * roughness);
   
    // Specular reflectance
    vis = visibility * 0.8 + 0.2;
    var ivis = 1.0 + (1.0 - vis) * 0.8;
    let specular : vec3<f32> = env.rgb * F * vis;
    // Ambient part
   
    var kD : vec3<f32> = (1.0 - F) * (1.0 - metallic);
     */
    //lightSpecular = mix(lightSpecular, envret.diff, 0.8);
    

    var color : vec3<f32> = lightSpecular + mix(lightDiffuse , env.rgb * 0.8, metallic);
    color = getFog(worldPos, eyePos.z, color);

    //color =  color / (1.0f + color);

        
    let gamma = 0.8;
    let exposure = 3.4;
    color = Uncharted2Tonemap(color * exposure);
    color = color * (1.0f / Uncharted2Tonemap(vec3<f32>(11.2f)));
    color = pow(color, vec3<f32>(1.0f / gamma));
    color += emmission * model.params.w;
    

    //color = pow(color, vec3<f32>(0.4545));
    //color = color / (color + vec3(1.0));
    //color = pow(color, vec3(1.0/2.2));  
    
    return vec4(color , model.diffuse.a * texel.a);
    }
`;


const TOON_MESH_VERTEX_SHADER = `
    
    {{skinning_uniform}}

    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) worldPos: vec3<f32>,
        @location(1) eyePos: vec3<f32>,
        @location(3) wReflectDir: vec3<f32>,
        @location(4) worldNormal: vec3<f32>,
        @location(5) shadowPos: vec3<f32>
    };

    @vertex
    fn main(
    @builtin(instance_index) instance_idx: u32,
    @location(0) position: vec3<f32>,
    {{skinning_attr}}
    @location(3) normal: vec3<f32>,

    @location(4) m0: vec3<f32>,
    @location(5) m1: vec3<f32>,
    @location(6) m2: vec3<f32>,
    @location(7) pos: vec3<f32>,
    @location(8) size: vec3<f32>

    ) -> VertexOutput {
    
    
    var output: VertexOutput;

    var rot = mat3x3<f32>(m0, m1, m2);

    {{skinning_vtx}}
    {{skinning_norm}}
    

    output.worldPos = (rot * (skinnedPosition * size.x)) + pos;
    output.eyePos = global.viewMat *  (output.worldPos + global.viewPos);
    output.Position = global.projMat * vec4(output.eyePos, 1.0);

    output.worldNormal = rot * skinnedNormal;

    var viewDir = normalize(-global.viewPos - output.worldPos);
    output.wReflectDir = reflect(viewDir, output.worldNormal);
        
    {{shadow}}
    
    return output;
}`;

const TOON_MESH_FRAGMENT_SHADER = `

    struct Model {
        diffuse : vec4<f32>
    };

    @group(1) @binding(0) var<uniform> model : Model;


    @group(1) @binding(1) var shading : texture_2d<f32>;

    @fragment
    fn main(
    @builtin(position) Position: vec4<f32>,
    @location(0) worldPos: vec3<f32>,
    @location(1) eyePos: vec3<f32>,
    @location(3) wReflectDir: vec3<f32>,
    @location(4) worldNormal: vec3<f32>,
    @location(5) shadowPos: vec3<f32>
    
    ) -> @location(0) vec4<f32> {

        var euv = vec2<f32>(atan2( wReflectDir.z, wReflectDir.x ) * 0.15915494309189535 + 0.5, asin( clamp( wReflectDir.y, - 1.0, 1.0 ) ) * 0.3183098861837907 + 0.5);
        var env = textureSample(envmap, Sampler, euv).rgb;

        {{shadow}}

        var shade = vec3(0.0,0.0,0.0);

        for(var n:i32 =0;n<8;n++)
        {
            var attenuation = 0.0;
            var lightDir : vec3<f32>;
            var lightDot : f32;
           
            if (global.lights[n].color.a < 0.5) {
                continue;
            }
    
            //directional 
            if(global.lights[n].color.a < 1.5) {
                lightDir = -normalize(global.lights[n].vec.xyz);
                attenuation = 1.0;
            } else if(global.lights[n].color.a < 2.5) {
                var vecToLight = global.lights[n].vec.xyz - worldPos;
                var distToLight = length(vecToLight);
                lightDir = normalize(vecToLight);
                attenuation = visibility / ( distToLight * distToLight * global.lights[n].vec.w);
            } else if(global.lights[n].color.a < 3.5) {
    
                var vecToLight  = global.lights[n].vec.xyz - worldPos;
                var distToLight = length(vecToLight);
                lightDir = normalize(vecToLight);
    
                var angleDot = max(0.0, dot(-lightDir, global.lights[n].vec2.xyz));
                attenuation = pow(angleDot, global.lights[n].vec2.w) + ( distToLight * global.lights[n].vec.w);
            } 
            lightDot = max(0.0, dot(lightDir, normalize(worldNormal)));
            shade  += textureSample(shading, SamplerN, vec2(saturate(lightDot), 0.0)).rgb * lightDot * global.lights[n].color.rgb * model.diffuse.rgb * attenuation;
        }
    

        return vec4( getFog(worldPos, eyePos.z,  shade.rgb)  ,  1.0);
    }
`;


const SKYBOX_MESH_VERTEX_SHADER = `

    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) texCoord: vec2<f32>
    };

    @vertex
    fn main(
    @location(0) position: vec3<f32>,
    @location(1) texcoord: vec2<f32>,
      ) -> VertexOutput {

        var output: VertexOutput;

        output.Position = global.projMat * vec4(global.viewMat * position, 1.0);
        output.texCoord = texcoord;
    
    return output;
}`;

const SKYBOX_MESH_FRAGMENT_SHADER = `

    @fragment
    fn main(
    @builtin(position) Position: vec4<f32>, 
    @location(0) texCoord: vec2<f32>
    ) -> @location(0) vec4<f32> {
        return vec4(textureSample(envmap, Sampler, vec2(texCoord.x, texCoord.y)).rgb, 1.0);
    }
`;


const SHADOW_VERTEX_SHADER = `

    {{skinning_uniform}}

    @vertex
    fn main(
    @builtin(instance_index) instance_idx: u32,
    @location(0) position: vec3<f32>,
    {{skinning_attr}}

    @location(3) m0: vec3<f32>,
    @location(4) m1: vec3<f32>,
    @location(5) m2: vec3<f32>,
    @location(6) pos: vec3<f32>,
    @location(7) size: vec3<f32>

    ) -> @builtin(position) vec4<f32> {

        var rot = mat3x3<f32>(m0, m1, m2);

        {{skinning_vtx}}

        return global.lightMat * vec4( (rot * (skinnedPosition * size.x)) + pos, 1.0);
    }
`;

const ALPHA_SHADOW_VERTEX_SHADER = `

    {{skinning_uniform}}

    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) uv: vec2<f32>
    };

    @vertex
    fn main(
    @builtin(instance_index) instance_idx: u32,
    @location(0) position: vec3<f32>,
    {{skinning_attr}}
    @location(3) uv: vec2<f32>,

    @location(4) m0: vec3<f32>,
    @location(5) m1: vec3<f32>,
    @location(6) m2: vec3<f32>,
    @location(7) pos: vec3<f32>,
    @location(8) size: vec3<f32>

    ) -> VertexOutput {

        var output : VertexOutput;

        var rot = mat3x3<f32>(m0, m1, m2);

        {{skinning_vtx}}

        output.Position = global.lightMat * vec4( (rot * (skinnedPosition * size.x)) + pos, 1.0);
        output.uv = uv;

        return output;
    }
`;


const ALPHA_SHADOW_BILLBOARD_VERTEX_SHADER = `

    {{skinning_uniform}}

    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) uv: vec2<f32>
    };

    @vertex
    fn main(
    @builtin(instance_index) instance_idx: u32,
    @location(0) position: vec3<f32>,
    {{skinning_attr}}
    @location(3) uv: vec2<f32>,

    @location(4) m0: vec3<f32>,
    @location(5) m1: vec3<f32>,
    @location(6) m2: vec3<f32>,
    @location(7) pos: vec3<f32>,
    @location(8) size: vec3<f32>

    ) -> VertexOutput {

        var output : VertexOutput;
      
        output.Position = global.lightMat * vec4( (position * m2.x) + pos, 1.0);
        output.uv = uv;

        return output;
    }
`;

const ALPHA_SHADOW_FRAGMENT_SHADER= `

@group(1) @binding(1) var Texture: texture_2d<f32>;

@fragment
fn main(
@builtin(position) Position: vec4<f32>,
@location(0) uv : vec2<f32>
)  {

    /*
    var fracuv = fract(uv);

    if(fracuv.x < 0.0) { fracuv.x += 1.0; }
    if(fracuv.y < 0.0) { fracuv.y += 1.0; }

    // apply texture tranformation 
    var tuv = (fracuv - vec2(0.5)) * model.texMat + vec2(0.5) + model.texPos;

    // apply texture box 
    var buv = model.texBox.xy + tuv * model.texBox.zw;
    */

    var texel = textureSample(Texture, Sampler, uv);

    if(texel.a < 0.5) {
        discard;
    }

    return;
}`;


  export const PARTICLE_VERTEX_SHADER = `
  
  
    struct VertexOutput {
        @builtin(position) Position: vec4<f32>,
        @location(0) vColor : vec4<f32>,
        @location(1) vAngle: f32,
        @location(2) uv : vec2<f32>,
        @location(3) eyePos : vec3<f32>,
        @location(4) shadowPos: vec3<f32>
    };
  
  @vertex
  fn main(
    @location(0) position : vec3<f32>,
    @location(1) uv : vec2<f32>,


    @location(2) m0: vec3<f32>,
    @location(3) m1: vec3<f32>,
    @location(4) m2: vec3<f32>,
    
    @location(5) pos: vec3<f32>,
    @location(6) size: vec3<f32>


 ) -> VertexOutput {
  
    var output : VertexOutput;

    if(m1.z > 0.5){  // true
      output.vColor = vec4( m0, m1.x ); //     set color associated to vertex; use later in fragment shader.
    }else{ // false
      output.vColor = vec4(0.0, 0.0, 0.0, 0.0); 		//     make particle invisible.
    }
  
    output.vAngle = m1.y;

    output.eyePos = position * m2.x  + ((pos + global.viewPos) * transpose(global.viewMat));
    output.Position = global.projMat * vec4(output.eyePos, 1.0);
    output.uv = uv;

    /*
    var lightCol = vec3(0.0, 0.0, 0.0);
    
    for(var n:i32 =0;n<8;n++)
    {
        var attenuation = 1.0;
        var lightDir : vec3<f32>;
        var lightDot : f32;
       
        if (global.lights[n].color.a < 0.5) {
            continue;
        }

        //directional 
        if(global.lights[n].color.a < 1.5) {
            lightDir = normalize(global.lights[n].vec.xyz);
        } else {
            var vecToLight = global.lights[n].vec.xyz - output.worldPos;
            var distToLight = length(vecToLight);
            lightDir = normalize(vecToLight);
            attenuation = 1.0 / ( distToLight * distToLight * global.lights[n].vec.w);
        }
        lightDot = max(0.0, dot(lightDir, vec3(0,0,-1)));
        lightCol += saturate(global.lights[n].color.rgb * lightDot * attenuation);
    }
    
    */

    return output;
  }`;
  
  export const PARTICLE_FRAGMENT_SHADER = `

    @group(1) @binding(1) var Texture: texture_2d<f32>;
  
  @fragment
  fn main(
    @builtin(position) Position: vec4<f32>,
    @location(0) vColor : vec4<f32>,
    @location(1) vAngle: f32,
    @location(2) uv : vec2<f32>,
    @location(3) eyePos : vec3<f32>,
    @location(4) shadowPos : vec3<f32>,
  ) -> @location(0) vec4<f32> {
  
    var pixel = vColor.xyz;
    var alpha = vColor.a;

    if(alpha < 0.1 ){
        discard;
    }
  
    var c = cos(vAngle);
    var s = sin(vAngle);
  
    var rotatedUV = vec2(  clamp(c * (uv.x - 0.5) + s * (uv.y - 0.5) + 0.5, 0.0, 1.0) , 
                           clamp(c * (uv.y - 0.5) - s * (uv.x - 0.5) + 0.5, 0.0, 1.0) );
  
    var texel = textureSample(Texture, Sampler, rotatedUV);
    pixel *= texel.xyz;
    alpha *= texel.a;

    

    return vec4(pixel, alpha );

  }
  `;


  const UI_SPRITE_VERTEX_SHADER = `


  struct Model {
      coords : vec4<f32>,
      tcoords : vec4<f32>,
      params : vec4<f32>
  };

  @group(0) @binding(0) var<uniform> projMat : mat4x4<f32>;
  @group(0) @binding(1) var Sampler : sampler;


  @group(1) @binding(0) var<uniform> model : Model;
  
  struct VertexOutput {
      @builtin(position) Position: vec4<f32>,
      @location(0) uv: vec2<f32>
  };

  @vertex
  fn main(
      @location(0) position: vec2<f32>,
      @location(1) uv: vec2<f32>,
  ) -> VertexOutput {
  
  
  var output: VertexOutput;
 
  var worldPos = (position * model.coords.zw) + model.coords.xy;

  output.Position = projMat * vec4(worldPos, 1.0, 1.0);
  output.uv = (uv * model.tcoords.zw) + model.tcoords.xy;

  return output;
}`;

const UI_SPRITE_FRAGMENT_SHADER = `

  struct Model {
      coords : vec4<f32>,
      tcoords : vec4<f32>,
      params : vec4<f32>
  };

  @group(0) @binding(1) var Sampler : sampler;

  @group(1) @binding(0) var<uniform> model : Model;
  @group(1) @binding(1) var texture : texture_2d<f32>;

  @fragment
  fn main(
  @builtin(position) Position: vec4<f32>,
  @location(0) uv: vec2<f32>
  
  ) -> @location(0) vec4<f32> {

    var c = cos(model.params.y);
    var s = sin(model.params.y);

    var cuv = uv - vec2(0.5);
    var tuv = min(vec2(1.0), max(vec2(0.0), vec2(cuv.x * c + cuv.y * s,  cuv.y * c - cuv.x * s) + vec2(0.5)));

    var texel =  textureSample(texture, Sampler, tuv);
    return vec4( texel.rgb,  texel.a * model.params.x);
  }
`;



const UI_MESH_VERTEX_SHADER = `


  struct Model {
      color : vec4<f32>,
      coords : vec4<f32>,
      params : vec4<f32>
  };

  @group(0) @binding(0) var<uniform> projMat : mat4x4<f32>;
  @group(1) @binding(0) var<uniform> model : Model;
  
  struct VertexOutput {
      @builtin(position) Position: vec4<f32>
  };

  @vertex
  fn main(
      @location(0) position: vec3<f32>
  ) -> VertexOutput {
  
  var output: VertexOutput;
 
  var worldPos = (position * vec3(model.coords.zw, 1.0)) + vec3(model.coords.xy, 1.0);

  output.Position = projMat * vec4(worldPos, 1.0);
  
  return output;
}`;

const UI_MESH_FRAGMENT_SHADER = `

  struct Model {
      color : vec4<f32>,
      coords : vec4<f32>,
      params : vec4<f32>
  };

  @group(1) @binding(0) var<uniform> model : Model;

  @fragment
  fn main(
  @builtin(position) Position: vec4<f32>
  ) -> @location(0) vec4<f32> {
      
      return model.color;
  }
`;

const UNIFORM_ALIGN = 256;

const WGPUTrianglePrimitive = { topology: 'triangle-list', cullMode: 'none',  frontFace : "ccw"};

const WGPUdepthStenctil = { depthWriteEnabled: true, depthCompare: 'less', format: 'depth24plus-stencil8' };
const WGPUShadowStenctil = { depthWriteEnabled: true, depthCompare: 'less', format: 'depth32float' };

const WGPUParticlesStenctil = { depthWriteEnabled: false, depthCompare: 'less', format: 'depth24plus-stencil8' };

const WGPUNoDepthStenctil = { depthWriteEnabled: false, depthCompare: 'always', format: 'depth24plus-stencil8' };

const WGPUCameraDepthStenctil = { depthWriteEnabled: false, depthCompare: 'always', format: 'depth24plus-stencil8' };

const WGPUTwoSideddPrimitive = { topology: 'triangle-list', cullMode: 'none'};

const colorBlend =  { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' };
const alphaBlend =  { srcFactor: 'one'      , dstFactor: 'one-minus-src-alpha', operation: 'add' };

const colorAdditiveBlend = { srcFactor: "one", dstFactor: "one", operation: "add" };
const alphaAdditiveBlend = { srcFactor: "one", dstFactor: "one", operation: "add" };


const pipeline1 =   {
                            label: 'VertexLightning',  
                            isParallax : false,
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive, 
                            depthStencil : WGPUdepthStenctil,
                            inc : SHADER_LIGHTNINH_GLOBAL
                    };



const pipeline2 =   {
                            label: 'Skybox',  
                            isParallax : false,
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTwoSideddPrimitive, 
                            depthStencil : WGPUNoDepthStenctil,
                            inc : ''
                    };
                    

const pipeline3 =   {
                            label: 'VertexLightningTextured',  
                            isParallax : false,
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive, 
                            depthStencil : WGPUdepthStenctil,
                            inc : SHADER_LIGHTNINH_GLOBAL
                    };     
                    
const pipeline3_1 =   {
                            label: 'Textured',  
                            isParallax : false,
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive, 
                            depthStencil : WGPUdepthStenctil,
                            inc : ''
                    };  
                    
/*                    
const pipeline3_sc =   {
                            label: 'ScreenTextured',
                            isParallax : false,
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive, 
                            depthStencil : WGPUCameraDepthStenctil,
                            inc : ''
                    };      
*/                                    
                                        
const pipeline4 =   {
                        label: 'PixelLightning',  
                        isParallax : false,
                        layout: 'auto',  
                        vertex : { entryPoint: 'main', buffers : []},  
                        fragment : { 
                            entryPoint: 'main',  
                            targets : [    
                            { 
                                blend : {color: colorBlend, alpha: alphaBlend}
                            }] 
                        },  
                        primitive : WGPUTrianglePrimitive, 
                        depthStencil : WGPUdepthStenctil,
                        inc : SHADER_LIGHTNINH_GLOBAL
                    };
                    
                    
const pipeline4_1 =   {
                        label: 'PixelLightning-Parallax', 
                        isParallax : true,
                        layout: 'auto',  
                        vertex : { entryPoint: 'main', buffers : []},  
                        fragment : { 
                            entryPoint: 'main',  
                            targets : [    
                            { 
                                blend : {color: colorBlend, alpha: alphaBlend}
                            }] 
                        },  
                        primitive : WGPUTrianglePrimitive, 
                        depthStencil : WGPUdepthStenctil,
                        inc : SHADER_LIGHTNINH_GLOBAL + PARALLAX_GLOBAL
                    };

const pipeline4_2 =   {
                        label: 'PixelLightning-Road', 
                        isParallax : false,
                        layout: 'auto',  
                        vertex : { entryPoint: 'main', buffers : []},  
                        fragment : { 
                            entryPoint: 'main',  
                            targets : [    
                            { 
                                blend : {color: colorBlend, alpha: alphaBlend}
                            }] 
                        },  
                        primitive : WGPUTrianglePrimitive, 
                        depthStencil : WGPUdepthStenctil,
                        inc : SHADER_LIGHTNINH_GLOBAL
                    };                    

const pipeline5 =   { 
                        label: 'Particles',
                        layout: 'auto',
                        vertex: { entryPoint: 'main', buffers: []},
                        fragment: {
                            entryPoint: 'main',
                            targets: [    
                            { 
                               // blend : {color: colorAdditiveBlend, alpha: alphaAdditiveBlend}
                               blend : {color: colorBlend, alpha: alphaBlend}
                            }] 
                        },
                        primitive: WGPUTwoSideddPrimitive,
                        depthStencil: WGPUParticlesStenctil,
                        inc : ''
                    };

const pipeline6 =   {
                        label: 'ToonShading',  
                        layout: 'auto',  
                        vertex : { entryPoint: 'main', buffers : []},  
                        fragment : { 
                            entryPoint: 'main',  
                            targets : [    
                            { 
                                blend : {color: colorBlend, alpha: alphaBlend}
                            }] 
                        },  
                        primitive : WGPUTrianglePrimitive, 
                        depthStencil : WGPUdepthStenctil,
                        inc : ''
                    };                      
                                          
                     
const pipeline7 =   {
                            label: 'ui',  
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTwoSideddPrimitive,
                            inc : ''
                    };

         
const pipeline8 =   {
                            label: 'uimesh',  
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive,
                            inc : ''
                    };              
                    
const pipeline9 =  {
                            label: 'Rain',  
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive,
                            depthStencil : WGPUParticlesStenctil,
                            inc : ''
                    };     
const pipeline10 =  {
                            label: 'WorldUIMesh',  
                            layout: 'auto',  
                            vertex : { entryPoint: 'main', buffers : []},  
                            fragment : { 
                                entryPoint: 'main',  
                                targets : [    
                                { 
                                    blend : {color: colorBlend, alpha: alphaBlend}
                                }] 
                            },  
                            primitive : WGPUTrianglePrimitive,
                            depthStencil : WGPUdepthStenctil,
                            inc : SHADER_LIGHTNINH_GLOBAL
                    };
//const pipelineUI =  {d:pipelineUIDesc, p : null, isUI : true, v: UI_MESH_VERTEX_SHADER, f: UI_MESH_FRAGMENT_SHADER, model: { 'pos': 'vec2', "scale" : 'vec2', 'texPos' : 'vec2', 'texScale' : 'vec2'}};
const shadowPipelineDesc =  {
    label: 'Shadow',
    layout: 'auto',
    vertex: { entryPoint: 'main', buffers: [] },
    primitive: WGPUTwoSideddPrimitive ,
    depthStencil: WGPUShadowStenctil,
    inc : ''
};  


const shadowBillboardPipelineDesc =  {
    label: 'BillBoardShadow',
    layout: 'auto',
    vertex: { entryPoint: 'main', buffers: [] },
    primitive: WGPUTwoSideddPrimitive ,
    depthStencil: WGPUShadowStenctil,
    inc : ''
};  


const alphaShadowPipelineDesc =  {
    label: 'AlphaShadow',
    layout: 'auto',
    vertex: { entryPoint: 'main', buffers: [] },
    fragment : { 
        entryPoint: 'main',
        targets : []
    },  
    primitive: WGPUTwoSideddPrimitive ,
    depthStencil: WGPUShadowStenctil,
    inc : ''
};     

const alphaShadowillboardPipelineDesc =  {
    label: 'BillboardAlphaShadow',
    layout: 'auto',
    vertex: { entryPoint: 'main', buffers: [] },
    fragment : { 
        entryPoint: 'main',
        targets : []
    },  
    primitive: WGPUTwoSideddPrimitive ,
    depthStencil: WGPUShadowStenctil,
    inc : ''
};     
const shadowSkinnedPipelineDesc =  {
    label: 'SkinnedShadow',
    layout: 'auto',
    vertex: { entryPoint: 'main', buffers: [] },
    primitive: WGPUTwoSideddPrimitive ,
    depthStencil: WGPUShadowStenctil,
    inc : ''
};     

const alphaShadowSkinnedPipelineDesc =  {
    label: 'AlphaSkinnedShadow',
    layout: 'auto',
    vertex: { entryPoint: 'main', buffers: [] },
    fragment : { 
        entryPoint: 'main',
        targets : []
    },  
    primitive: WGPUTwoSideddPrimitive ,
    depthStencil: WGPUShadowStenctil,
    inc : ''
};           
const shadowBillboardPipeline = {d:shadowBillboardPipelineDesc, p : null, hasUniforms : false, isSkinned: false , useTex:{  modelTex: false, useSampler: false, useSamplerN: false, envMap : false, shadowMap : false }, v: SHADOW_VERTEX_SHADER, f: null, model: {  "params" : 'vec4'}}
const shadowPipeline = {d:shadowPipelineDesc, p : null, hasUniforms : false, isSkinned: false , useTex:{  modelTex: false, useSampler: false, useSamplerN: false, envMap : false, shadowMap : false }, v: SHADOW_VERTEX_SHADER, f: null, model: {  "params" : 'vec4'}}
const alphaShadowPipeline = {d:alphaShadowPipelineDesc, p : null, hasUniforms : true, isSkinned: false ,  useTex:{ modelTex: true, useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: ALPHA_SHADOW_VERTEX_SHADER, f: ALPHA_SHADOW_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}}   
const alphaShadowBillboardPipeline = {d:alphaShadowillboardPipelineDesc, p : null, hasUniforms : true, isSkinned: false ,  useTex:{ modelTex: true, useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: ALPHA_SHADOW_BILLBOARD_VERTEX_SHADER, f: ALPHA_SHADOW_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}}   

/*
const shadowSkinnedPipeline = {d:shadowSkinnedPipelineDesc, p : null, hasUniforms : false, isSkinned: true ,  useTex:{  modelTex: false, useSampler: false, useSamplerN: false, envMap : false, shadowMap : false }, v: SHADOW_SKINNED_VERTEX_SHADER, f: null, model: { "params" : 'vec4'}}
const alphaSkinnedPipeline = {d:alphaShadowSkinnedPipelineDesc, p : null, hasUniforms : true, isSkinned: true ,  useTex:{  modelTex: true, useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: ALPHA_SHADOW_SKINNED_VERTEX_SHADER, f: ALPHA_SHADOW_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}}
*/
                  
const pipelines = [ {d:pipeline1  , p : null, hasUniforms : true, hasTransform:true, isSkinned: true , isUI : false, useTex:{ modelTex: true, useSampler: true, useSamplerN: false, envMap : true, shadowMap : true }, v: SIMPLE_MESH_VERTEX_SHADER, f: SIMPLE_MESH_FRAGMENT_SHADER, model: { "params" : 'vec4', 'diffuse' : 'vec4','emissive' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}}, 
                    {d:pipeline2  , p : null, hasUniforms : true, hasTransform:false, isSkinned: false, isUI : false, useTex:{ modelTex: false, useSampler: true, useSamplerN: false, envMap : true, shadowMap : false }, v: SKYBOX_MESH_VERTEX_SHADER, f: SKYBOX_MESH_FRAGMENT_SHADER, model: { } }, 
                    {d:pipeline3  , p : null, hasUniforms : true, hasTransform:true, isSkinned: true , isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : true, shadowMap : true }, v: TEXTURED_MESH_VERTEX_SHADER, f: TEXTURED_MESH_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}},
                    {d:pipeline3_1, p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: SIMPLE_TEXTURED_MESH_VERTEX_SHADER, f: SIMPLE_TEXTURED_MESH_FRAGMENT_SHADER, model: { 'diffuse' : 'vec4', "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}},
//                  {d:pipeline3_sc, p : null, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: SIMPLE_TEXTURED_MESH_VERTEX_SHADER, f: SIMPLE_TEXTURED_MESH_FRAGMENT_SHADER, model: {  'diffuse' : 'vec4', "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}},
                    {d:pipeline4  , p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : true, shadowMap : true }, v: PIXEL_LIGHTNING_MESH_VERTEX_SHADER, f: PIXEL_LIGHTNING_MESH_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'diffuse' : 'vec4', 'specular' : 'vec4', 'clearcoat' : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2', 'texPos' : 'vec2'}},
                    {d:pipeline4_1, p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : true, shadowMap : true }, v: PIXEL_LIGHTNING_MESH_VERTEX_SHADER, f: PIXEL_LIGHTNING_MESH_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'diffuse' : 'vec4', 'specular' : 'vec4', 'clearcoat' : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2', 'texPos' : 'vec2'}},
                    {d:pipeline4_2, p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : true, shadowMap : true }, v: PIXEL_LIGHTNING_MESH_VERTEX_SHADER, f: PIXEL_LIGHTNING_MESH_FRAGMENT_SHADER, model: {  "params" : 'vec4', 'diffuse' : 'vec4', 'specular' : 'vec4', 'clearcoat' : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2', 'texPos' : 'vec2'}},
                    {d:pipeline5  , p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: PARTICLE_VERTEX_SHADER, f: PARTICLE_FRAGMENT_SHADER, model: { "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}},
                    {d:pipeline6  , p : null, hasUniforms : true, hasTransform:true, isSkinned: true , isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: true , envMap : true, shadowMap : true }, v: TOON_MESH_VERTEX_SHADER, f: TOON_MESH_FRAGMENT_SHADER, model: { 'diffuse' : 'vec4'}},
                    {d:pipeline9  , p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: true , useSampler: true, useSamplerN: false, envMap : false, shadowMap : false }, v: SIMPLE_TEXTURED_MESH_VERTEX_SHADER, f: SIMPLE_TEXTURED_MESH_FRAGMENT_SHADER, model: { 'diffuse' : 'vec4', "params" : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'}},
                    {d:pipeline10  , p : null, hasUniforms : true, hasTransform:true, isSkinned: false, isUI : false, useTex:{ modelTex: false , useSampler: false, useSamplerN: false, envMap : false, shadowMap : true }, v: UI3DMESH_VERTEX_SHADER, f: UI3DMESH_FRAGMENT_SHADER, model: { 'diffuse' : 'vec4', "params" : 'vec4'}},

                    {d:pipeline7  , p : null, hasUniforms : true, hasTransform:false, isSkinned: false, isUI : true , useTex:{ modelTex: true }, v: UI_SPRITE_VERTEX_SHADER, f: UI_SPRITE_FRAGMENT_SHADER, model: { 'coords': 'vec4', 'tcoords' : 'vec4', 'params' : 'vec4'}},
                    {d:pipeline8  , p : null, hasUniforms : true, hasTransform:false, isSkinned: false, isUI : true , useTex:{ modelTex: false }, v: UI_MESH_VERTEX_SHADER, f: UI_MESH_FRAGMENT_SHADER, model: { 'color' : 'vec4', 'coords': 'vec4', 'params' : 'vec4'}}
                    

                    
                  ]

function getTypeSize(type, a = 1) {

    if(type == 'f32')
        return 1;

    if(type == 'u32')
        return 1;

    if(type == 'vec2')
        return Math.max(2 , a);

    if(type == 'vec3')
        return Math.max(3 , a);

    if(type == 'vec4')
        return 4;

    if(type == 'vec4i')
        return 4;

    if(type == 'mat2')
        return 4;

    if(type == 'transform2')
        return 8; /* mat2 + vec2 */

    if(type == 'mat3')
        return 12;

    if(type == 'mat4')
        return 16;

    if(type == 'transform3')
        return 16; /* mat3 + vec3 */

    return 0;
}                  

class bufferLayoutEntry{
    name;
    type;
    location;
    offset;

    constructor(n,t,l, o) {
        this.name = n;
        this.location = l;
        this.type = t;
        this.offset = o;
    }

    getSize(a = 1) { return getTypeSize(this.type, a); }
}

class bufferLayout{
    entries;
    numFloats;
    align;

    constructor(entries, a = 1)
    {
        this.entries = new Array(entries.length);
        this.numFloats = 0;
        this.align = a;

        for(let n=0;n< entries.length;n++)
        {
            this.entries[n] = new bufferLayoutEntry (entries[n].name, entries[n].type, entries[n].location, this.numFloats);
            this.numFloats += this.entries[n].getSize(this.align);
        }
    }

    hasAttribute(name){
        for(let n=0;n< this.entries.length;n++){
            if(this.entries[n].name === name)
                return true;
        }

        return false;

    }
}

class webGPUPrimitive {

    attributes;
    vertexBufferRange;
    vertexLayout;
    
    uniforms;
    uniformBufferRange;
    uniformLayout;
    
    material;
    pipeline;

    initialized;
    hasColor;
    needsUpdate;

    constructor(def, nTriangles, material = null, hasSkinning = false , hasColor = false, transformPos = null) {

        this.attributes = def.attributes;
        this.uniforms = def.uniforms; 
        this.pipeline = def.pipeline;
        this.material = material;
        this.initialized = 0;
        this.hasColor = hasColor;
        this.hasSkinning = hasSkinning;
        this.needsUpdate = true;
        this.isBillBloard = false;

        this.vertexLayout = new bufferLayout(this.attributes, 1);
        this.vertexBufferRange = null;
        this.nTriangles = nTriangles;
        this.uniformBufferRange = null;

        const transformStart = transformPos ? transformPos : this.vertexLayout.entries.length;

        this.transformAttributes = [  
            {name : 'm0' , type: 'vec3', location : transformStart + 0},
            {name : 'm1' , type: 'vec3', location : transformStart + 1},
            {name : 'm2' , type: 'vec3', location : transformStart + 2}, 
            {name : 'pos', type: 'vec3', location : transformStart + 3},
            {name : 'size', type: 'vec3', location : transformStart + 4}
        ];


        this.transformLayout = new bufferLayout(this.transformAttributes, 1);
        
        if (this.uniforms.length) {
            this.uniformLayout = new bufferLayout(this.uniforms, 4);
            this.uniformEntries = [];

            this.uniformGroups = {};
            this.uniformGroups[this.pipeline] = null;

        } else {
            this.uniformLayout = null;
            this.uniformEntries = null;
            this.uniformGroups = null;
        }
    }

    clone(cloneMat = false)
    {
        const mat = cloneMat? cloneMat(this.material) : this.material;
        return new webGPUPrimitive({attributes: this.attributes, uniforms: this.uniforms, pipeline : this.pipeline }, this.nTriangles, mat, this.hasSkinning , this.hasColor);

    }
    
    getBufferDescription() {

        const buffers = []

        for(let n=0;n<2;n++){
         
            const layout = n==0? this.vertexLayout:this.transformLayout;
            const stepMode = n==0? 'vertex':'instance';

            const desc = { arrayStride: layout.numFloats * 4, stepMode: stepMode, attributes : []};
            let offset=0;

            for (let e of layout.entries) {
                let format='';

                if(e.type == 'vec4')
                    format = 'float32x4';
                if(e.type == 'vec4i')
                    format = 'uint32x4';           
                else if(e.type == 'vec3')
                    format = 'float32x3';
                else if(e.type == 'vec2')
                    format = 'float32x2';
                else if(e.type == 'f32')
                    format = 'float32';
                else if(e.type == 'u32')
                    format = 'uint32';

                desc.attributes.push({shaderLocation: e.location, offset : offset * 4, format : format});

                offset += e.getSize(1);
            }

            buffers.push(desc);
        }

        return buffers;
    }

    getUniformOffset(name, relative = true) {
        for (let e of this.uniformLayout.entries) {

            if (e.name === name) {

                if(relative)
                    return e.offset * 4;
                else
                    return this.uniformBufferRange.start + e.offset * 4;
            }
        }

        return -1;
    }


    
    
    
    writeSkinnedAttributes(vertexBuffer, geo, flip, grp = null) {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        if(grp !== null ) {
            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0; i<3; i++) {

                    const oo = n + i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                        const o = oo + e.offset;
                
                        if(e.name == 'boneIndex') {
                         
                            if (geo.attributes.skinIndex) {
                                vertexBuffer[o * 4 + 0] = geo.attributes.skinIndex.array[(grp.start + v + ids[i]) * 4 + 0];
                                vertexBuffer[o * 4 + 1] = geo.attributes.skinIndex.array[(grp.start + v + ids[i]) * 4 + 1];
                                vertexBuffer[o * 4 + 2] = geo.attributes.skinIndex.array[(grp.start + v + ids[i]) * 4 + 2];
                                vertexBuffer[o * 4 + 3] = geo.attributes.skinIndex.array[(grp.start + v + ids[i]) * 4 + 3];
                            }else{
                                vertexBuffer[o * 4 + 0] = 0;
                                vertexBuffer[o * 4 + 1] = 0;
                                vertexBuffer[o * 4 + 2] = 0;
                                vertexBuffer[o * 4 + 3] = 0;
                            }
                        }
                        
                        if(e.name == 'weight') {
                            if(geo.attributes.skinWeight) {
                                vertexBuffer[o * 4 + 0] = Math.floor(geo.attributes.skinWeight.array[(grp.start + v + ids[i]) * 4 + 0] * 255.0);
                                vertexBuffer[o * 4 + 1] = Math.floor(geo.attributes.skinWeight.array[(grp.start + v + ids[i]) * 4 + 1] * 255.0);
                                vertexBuffer[o * 4 + 2] = Math.floor(geo.attributes.skinWeight.array[(grp.start + v + ids[i]) * 4 + 2] * 255.0);
                                vertexBuffer[o * 4 + 3] = Math.floor(geo.attributes.skinWeight.array[(grp.start + v + ids[i]) * 4 + 3] * 255.0);
                            }else{
                                vertexBuffer[o * 4 + 0] = 255;
                                vertexBuffer[o * 4 + 1] = 0;
                                vertexBuffer[o * 4 + 2] = 0;
                                vertexBuffer[o * 4 + 3] = 0;
                            }
                        }
                    }
                }
            }
        } else {

            for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i =0; i<3; i++) {
                    const oo = i * this.vertexLayout.numFloats + n;

                    for(let e of this.vertexLayout.entries) {

                        const o = oo + e.offset;
                        if(e.name == 'boneIndex') {

                            if(geo.attributes.skinIndex) {
                                vertexBuffer[o * 4 + 0] = geo.attributes.skinIndex.array[(t + ids[i]) * 4 + 0];
                                vertexBuffer[o * 4 + 1] = geo.attributes.skinIndex.array[(t + ids[i]) * 4 + 1];
                                vertexBuffer[o * 4 + 2] = geo.attributes.skinIndex.array[(t + ids[i]) * 4 + 2];
                                vertexBuffer[o * 4 + 3] = geo.attributes.skinIndex.array[(t + ids[i]) * 4 + 3];
                            }else{
                                vertexBuffer[o * 4 + 0] = 0;
                                vertexBuffer[o * 4 + 1] = 0;
                                vertexBuffer[o * 4 + 2] = 0;
                                vertexBuffer[o * 4 + 3] = 0;
                            }
                        }

                        if(e.name == 'weight') {

                            if(geo.attributes.skinWeight) {
                                vertexBuffer[o * 4 + 0] = Math.floor(geo.attributes.skinWeight.array[(t + ids[i]) * 4 + 0] * 255.0);
                                vertexBuffer[o * 4 + 1] = Math.floor(geo.attributes.skinWeight.array[(t + ids[i]) * 4 + 1] * 255.0);
                                vertexBuffer[o * 4 + 2] = Math.floor(geo.attributes.skinWeight.array[(t + ids[i]) * 4 + 2] * 255.0);
                                vertexBuffer[o * 4 + 3] = Math.floor(geo.attributes.skinWeight.array[(t + ids[i]) * 4 + 3] * 255.0);
                            } else {
                                vertexBuffer[o * 4 + 0] = 255;
                                vertexBuffer[o * 4 + 1] = 0;
                                vertexBuffer[o * 4 + 2] = 0;
                                vertexBuffer[o * 4 + 3] = 0;
                            }
                        }
                    }
                }
            }
        }
    }
}



class webGPUWorldUIPrimitive extends webGPUPrimitive{

    constructor(nTriangles, material) {

        super({
            attributes :[  
                {name : 'position', type: 'vec3', location : 0},
                {name : 'normal', type: 'vec3', location : 1} 
            ],
            uniforms  : [   
                {name: 'diffuse', type:'vec4', location : 0},
                {name :'params', type: 'vec4', location : 0},
            ] ,
            pipeline : 'WorldUIMesh'}, 
            nTriangles, 
            material
        );

        this.size = new THREE.Vector2();
    }
    cloneMat() {

        return { diffuse : {r:this.material.diffuse.r, g:this.material.diffuse.g, b:this.material.diffuse.b}}
    }

    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat(this.material) : this.material;
        return new webGPUWorldUIPrimitive(this.nTriangles, mat);
    }

    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        // { 'model' : 'transform'} 
        // { 'modelView': 'mat3'} 
        // { 'modelView': 'transform', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}
        for(let k in model) {

            if (k==='diffuse') {

                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            } else if (k ==='params') {
                
                uniformBuffer[ofs + 0] = 0.0;
                uniformBuffer[ofs + 1] = 0.0;
                uniformBuffer[ofs + 2] = 0.0;
                uniformBuffer[ofs + 3] = 0.0;
                
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1,y:1}, grp = null, transform = null) {

        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        if ( grp !== null ) {

            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                        }
                        if((e.name == 'normal')&&(geo.attributes.normal)) {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o +2] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                        }
                    }
                }
            }
        } else {

            for (let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for (let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;
                    for(let e of this.vertexLayout.entries) {
                        
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                        }
                        if((e.name == 'normal')&&(geo.attributes.normal))  {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.normal.array[(t + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.normal.array[(t + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o + 2] = geo.attributes.normal.array[(t + ids[i]) * 3 + 2] * sz;
                        }
                    }
                }
            }
        }
    }
}

class webGPUSimplePrimitive extends webGPUPrimitive{

    constructor(nTriangles, material, hasSkinning = false) {

        const attrs = hasSkinning ? 
        [  
            {name : 'position', type: 'vec3', location : 0},
            {name : 'boneIndex', type: 'u32', location : 1},
            {name : 'weight', type: 'u32', location : 2}, 
            {name : 'normal', type: 'vec3', location : 3},
            {name : 'uv', type: 'vec2', location : 4},
            
        ] 
        :
        [  
            {name : 'position', type: 'vec3', location : 0},
            {name : 'normal', type: 'vec3', location : 3} ,
            {name : 'uv', type: 'vec2', location : 4},
            
        ];


        super({
            attributes :attrs,
            uniforms  : [   
                {name :'params', type: 'vec4', location : 0},
                {name: 'modelColor', type:'vec4', location : 0},
                {name: 'emissive', type:'vec4', location : 0},
                {name: 'modelAmbiant+Metalness', type:'vec4', location : 0},
                {name: 'modelSpecular+Rough', type:'vec4', location : 0},
                {name: 'roughnessMap', type:'roughnessmap', location : 1},
            ] ,
            pipeline : 'VertexLightning'}, 
            nTriangles, 
            material,
            hasSkinning,
            false,
            5
        );
    }
    cloneMat() {

        return { diffuse : {r:this.material.diffuse.r, g:this.material.diffuse.g, b:this.material.diffuse.b},
                 emissive : {r:this.material.emissive.r, g:this.material.emissive.g, b:this.material.emissive.b},
                 ambiant : {r:this.material.ambiant.r, g:this.material.ambiant.g, b:this.material.ambiant.b},
                 specular : {r:this.material.specular.r, g:this.material.specular.g, b:this.material.specular.b},
                 metalness : this.material.metalness,
                 opacity : this.material.opacity,
                 transparent : this.material.transparent,
                 reflectivity : this.material.reflectivity,
                 alphaTest: this.material.alphaTest,
                 rmap: this.material.rmap,
                 roughness : this.material.roughness,
                 emissiveIntensity: this.material.emissiveIntensity
                }
    }

    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat(this.material) : this.material;
        return new webGPUSimplePrimitive(this.nTriangles, mat);
    }

    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        // { 'model' : 'transform'} 
        // { 'modelView': 'mat3'} 
        // { 'modelView': 'transform', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}
        for(let k in model) {

            if (k==='diffuse') {

                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            } else if (k==='emissive') {

                uniformBuffer[ofs + 0] = this.material.emissive.r;
                uniformBuffer[ofs + 1] = this.material.emissive.g;
                uniformBuffer[ofs + 2] = this.material.emissive.b;
                uniformBuffer[ofs + 3] = this.material.emissiveIntensity;
                
            } else if (k==='ambiant') {

                uniformBuffer[ofs + 0] = this.material.ambiant.r;
                uniformBuffer[ofs + 1] = this.material.ambiant.g;
                uniformBuffer[ofs + 2] = this.material.ambiant.b;
                uniformBuffer[ofs + 3] = this.material.metalness;
            } else if (k==='specular') {

                uniformBuffer[ofs + 0] = this.material.specular.r;
                uniformBuffer[ofs + 1] = this.material.specular.g;
                uniformBuffer[ofs + 2] = this.material.specular.b;
                uniformBuffer[ofs + 3] = this.material.roughness;
            }else if (k ==='params') {
                
                uniformBuffer[ofs + 1] = this.material.alphaTest;
                uniformBuffer[ofs + 3] = this.material.reflectivity;
                
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1,y:1}, grp = null, transform = null) {

        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;
        

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        const attr_offet = {};

        for(let e of this.vertexLayout.entries) {

            if(e.name == 'position') {
                attr_offet['position'] = e.offset;
            }else if(e.name == 'uv') {
                attr_offet['uv'] = e.offset;
            }else if(e.name == 'normal') {
                attr_offet['normal'] = e.offset;
            }
        }

        if ( grp !== null ) {

            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;
                    vertexBuffer[n + attr_offet['position'] + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;

                    vertexBuffer[n + attr_offet['normal'] + o +0] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o +1] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['normal'] + o +2] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 2] * sz;

                    if(geo.attributes.uv){
                        vertexBuffer[n + attr_offet['uv'] + o +0] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 0] * uvScale.x;
                        vertexBuffer[n + attr_offet['uv'] + o +1] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 1] * uvScale.y;
                    }
                }
            }
        } else {

            for (let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for (let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;
                    vertexBuffer[n + attr_offet['position'] + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                    
                    vertexBuffer[n + attr_offet['normal'] + o + 0] = geo.attributes.normal.array[(t + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o + 1] = geo.attributes.normal.array[(t + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['normal'] + o + 2] = geo.attributes.normal.array[(t + ids[i]) * 3 + 2] * sz;
                    if(geo.attributes.uv){
                        vertexBuffer[n + attr_offet['uv'] + o + 0] = geo.attributes.uv.array[(t + ids[i]) * 2 + 0] * uvScale.x;
                        vertexBuffer[n + attr_offet['uv'] + o + 1] = geo.attributes.uv.array[(t + ids[i]) * 2 + 1] * uvScale.y;
                    }
                }
            }
        }
    }
}

/*
class webGPUSimpleScreenTexturedPrimitive extends webGPUPrimitive{

    constructor(nTriangles, material) {
        super({
            attributes: [ 
                {name : 'position', type: 'vec3', location : 0},
                {name : 'uv', type: 'vec2', location : 1}
            ], 
            uniforms : [
                {name: 'modelColor', type:'vec4', location : 0},
                {name: 'Texture Box', type:'vec4', location : 0},
                {name: 'Texture Transform', type:'transform2', location : 0},
                {name: 'Texture', type:'tex', location : 1}
            ], 
            pipeline : 'ScreenTextured'
        }, 
        nTriangles, 
        material);
        this.material.texBox = {x: 0.0 , y: 0.0, z: 1.0 , w: 1.0};
        this.material.texTransform = new Transformation2 ();
        this.isScreenSpace = true;
        this.size = {x : 1, y : 1};
    }

    cloneMat() {

        return { diffuse : {r:this.material.diffuse.r, g:this.material.diffuse.g, b:this.material.diffuse.b},
                 texBox : {x:this.material.texBox.x, y:this.material.texBox.y, z:this.material.texBox.z, w:this.material.texBox.w},
                 texTransform : new Transformation2(this.material.texTransform.position, this.material.texTransform.angle),
                 opacity : this.material.opacity,
                 reflectivity: this.material.reflectivity,
                 transparent : this.material.transparent,
                 alphaTest: this.material.alphaTest,
                 tex : this.material.tex
                }
    }
    

    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat(this.material) : this.material;
        return new webGPUSimpleScreenTexturedPrimitive(this.nTriangles, mat);
    }
    
    writeUniforms(uniformBuffer, size, model) {

        let ofs = 0;

        // { 'model' : 'transform'} 
        // { 'modelView': 'mat3'} 
        // { 'modelView': 'transform', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}
        for(let k in model) {

            if (k === 'texBox') {
                if(model[k] === 'vec4') {
                    uniformBuffer[ofs + 0] = this.material.texBox.x;
                    uniformBuffer[ofs + 1] = this.material.texBox.y;
                    uniformBuffer[ofs + 2] = this.material.texBox.z;
                    uniformBuffer[ofs + 3] = this.material.texBox.w;
                }

            }
            else if (k === 'texTransform') {
                if(model[k] === 'transform2') {
                    this.material.texTransform.toBuffer(uniformBuffer, ofs);
                }

            }
            else if (k==='diffuse') {

                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            } else if (k ==='params') {

                uniformBuffer[ofs + 0] = 0;
                uniformBuffer[ofs + 1] = this.material.alphaTest;
                uniformBuffer[ofs + 2] = 0;
                uniformBuffer[ofs + 3] = this.material.reflectivity;

                
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        if(grp !== null ) {
            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {

                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                        }
                        if(e.name == 'uv') {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 0] * uvScale.x;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 1] * uvScale.y;
                        }

                    }
                }
            }
        } else {

            for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                        
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                        }
                        if(e.name == 'uv') {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.uv.array[(t + ids[i]) * 2 + 0] * uvScale.x;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.uv.array[(t + ids[i]) * 2 + 1] * uvScale.y;
                        }
                    }
                }
            }
        }
    }
}
*/


class webGPUSimpleTexturedPrimitive extends webGPUPrimitive{

    constructor(nTriangles, material) {
        super({
            attributes: [ 
                {name : 'position', type: 'vec3', location : 0},
                {name : 'uv', type: 'vec2', location : 1}
            ], 
            uniforms : [
                {name: 'modelColor', type:'vec4', location : 0},
                {name: 'Texture Box', type:'vec4', location : 0},
                {name: 'Texture Transform', type:'transform2', location : 0},
                {name: 'Texture', type:'tex', location : 1}
            ], 
            pipeline : 'Textured'
        }, 
        nTriangles, 
        material);
        this.material.texBox = {x: 0.0 , y: 0.0, z: 1.0 , w: 1.0};
        this.material.texTransform = new Transformation2 ();
    }

    cloneMat() {

        return { diffuse : {r:this.material.diffuse.r, g:this.material.diffuse.g, b:this.material.diffuse.b},
                 texBox : {x:this.material.texBox.x, y:this.material.texBox.y, z:this.material.texBox.z, w:this.material.texBox.w},
                 texTransform : new Transformation2(this.material.texTransform.position, this.material.texTransform.angle),
                 opacity : this.material.opacity,
                 reflectivity: this.material.reflectivity,
                 transparent : this.material.transparent,
                 alphaTest: this.material.alphaTest,
                 tex : this.material.tex
                }
    }
    
    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat(this.material) : this.material;
        return new webGPUSimpleTexturedPrimitive(this.nTriangles, mat);
    }


    
    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        // { 'model' : 'transform'} 
        // { 'modelView': 'mat3'} 
        // { 'modelView': 'transform', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}
        for(let k in model) {

            if (k === 'texBox') {
                if(model[k] === 'vec4') {
                    uniformBuffer[ofs + 0] = this.material.texBox.x;
                    uniformBuffer[ofs + 1] = this.material.texBox.y;
                    uniformBuffer[ofs + 2] = this.material.texBox.z;
                    uniformBuffer[ofs + 3] = this.material.texBox.w;
                }

            }
            else if (k === 'texTransform') {
                if(model[k] === 'transform2') {
                    this.material.texTransform.toBuffer(uniformBuffer, ofs);
                }

            }
            else if (k==='diffuse') {

                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            } else if (k ==='params') {
                uniformBuffer[ofs + 1] = this.material.alphaTest;
                uniformBuffer[ofs + 3] = this.material.reflectivity;

                
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        if(grp !== null ) {
            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {

                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                        }
                        if(e.name == 'uv') {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 0] * uvScale.x;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 1] * uvScale.y;
                        }

                    }
                }
            }
        } else {

            for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                        
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                        }
                        if(e.name == 'uv') {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.uv.array[(t + ids[i]) * 2 + 0] * uvScale.x;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.uv.array[(t + ids[i]) * 2 + 1] * uvScale.y;
                        }
                    }
                }
            }
        }
    }
}


class webGPUTexturedPrimitive extends webGPUPrimitive{

    constructor(nTriangles, material, hasSkinning = false) {

        const attrs = hasSkinning ? 
        [ 
            {name : 'position', type: 'vec3', location : 0},
            {name : 'boneIndex', type: 'u32', location : 1},
            {name : 'weight', type: 'u32', location : 2},
            {name : 'uv', type: 'vec2', location : 3},
            {name : 'normal', type: 'vec3', location : 4}
        ]
        :
        [ 
            {name : 'position', type: 'vec3', location : 0},
            {name : 'uv', type: 'vec2', location : 3},
            {name : 'normal', type: 'vec3', location : 4}
        ];


        //"params" : 'vec4', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4', 'texBox' : 'vec4', 'texTransform' : 'transform2'
              
        super({
            attributes:attrs , 
            uniforms : [
                {name: 'params', type:'vec4', location : 0},
                {name: 'modelColor', type:'vec4', location : 0},
                {name: 'modelAmbiant+Metalness', type:'vec4', location : 0},
                {name: 'modelSpecular+Rough', type:'vec4', location : 0},
                {name: 'Texture Box', type:'vec4', location : 0},
                {name: 'Texture Transform', type:'transform2', location : 0},
                {name: 'size', type:'vec2', location : 0},
                {name: 'Texture', type:'tex', location : 1},
                {name: 'emissiveMap', type:'emissivemap', location : 2}
            ], 
            pipeline : 'VertexLightningTextured'
        }, 
        nTriangles, 
        material,
        hasSkinning,
        false,
        5
        );


        this.material.texBox = {x: 0.0 , y: 0.0, z: 1.0 , w: 1.0};
        this.material.texTransform = new Transformation2 ();
        this.material.instanceTextureOffset = new THREE.Vector2();
        this.material.instanceTextureAngle = 0;
    }

    cloneMat() {

        return { diffuse : {r:this.material.diffuse.r, g:this.material.diffuse.g, b:this.material.diffuse.b},
                 ambiant : {r:this.material.ambiant.r, g:this.material.ambiant.g, b:this.material.ambiant.b},
                 specular : {r:this.material.specular.r, g:this.material.specular.g, b:this.material.specular.b},
                 texBox : {x:this.material.texBox.x, y:this.material.texBox.y, z:this.material.texBox.z, w:this.material.texBox.w},
                 texTransform : new Transformation2(this.material.texTransform.position, this.material.texTransform.angle),
                 metalness : this.material.metalness,
                 opacity : this.material.opacity,
                 reflectivity: this.material.reflectivity,
                 transparent : this.material.transparent,
                 alphaTest: this.material.alphaTest,
                 roughness : this.material.roughness,
                 tex : this.material.tex
                }
    }
    
    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat(this.material) : this.material;
        return new webGPUTexturedPrimitive(this.nTriangles, mat, this.hasSkinning);
    }
    
    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

   
        for(let k in model) {

            if (k === 'texBox') {
                if(model[k] === 'vec4') {
                    uniformBuffer[ofs + 0] = this.material.texBox.x;
                    uniformBuffer[ofs + 1] = this.material.texBox.y;
                    uniformBuffer[ofs + 2] = this.material.texBox.z;
                    uniformBuffer[ofs + 3] = this.material.texBox.w;
                }

            }
            else if (k === 'texTransform') {
                if(model[k] === 'transform2') {
                    this.material.texTransform.toBuffer(uniformBuffer, ofs);
                }

            }
            else if (k==='diffuse') {

                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            } else if (k==='ambiant') {

                uniformBuffer[ofs + 0] = this.material.ambiant.r;
                uniformBuffer[ofs + 1] = this.material.ambiant.g;
                uniformBuffer[ofs + 2] = this.material.ambiant.b;
                uniformBuffer[ofs + 3] = this.material.metalness;
            } else if (k==='specular') {

                uniformBuffer[ofs + 0] = this.material.specular.r;
                uniformBuffer[ofs + 1] = this.material.specular.g;
                uniformBuffer[ofs + 2] = this.material.specular.b;
                uniformBuffer[ofs + 3] = this.material.roughness;
            } else if (k ==='params') {

                uniformBuffer[ofs + 0] = this.material.instanceTextureOffset.x;
                uniformBuffer[ofs + 1] = this.material.instanceTextureOffset.y;
                uniformBuffer[ofs + 2] = this.material.instanceTextureAngle;
                uniformBuffer[ofs + 3] = this.material.emissiveIntensity;
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        const attr_offet = {};

        for(let e of this.vertexLayout.entries) {

            if(e.name == 'position') {
                attr_offet['position'] =e.offset;
            }else if(e.name == 'uv') {
                attr_offet['uv'] = e.offset;
            }else if(e.name == 'normal') {
                attr_offet['normal'] = e.offset;
            }
        }

        if(grp !== null ) {
            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {

                    const o = i * this.vertexLayout.numFloats;

                    vertexBuffer[n + attr_offet['position'] + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;

                    vertexBuffer[n + attr_offet['normal'] + o +0] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o +1] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['normal'] + o +2] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 2] * sz;

                    if(geo.attributes.uv){
                        vertexBuffer[n + attr_offet['uv'] + o +0] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 0] * uvScale.x;
                        vertexBuffer[n + attr_offet['uv'] + o +1] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 1] * uvScale.y;

                    }
                }
            }
        } else {

            for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;

                    vertexBuffer[n + attr_offet['position'] + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                    vertexBuffer[n + attr_offet['normal'] + o + 0] = geo.attributes.normal.array[(t + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o + 1] = geo.attributes.normal.array[(t + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['normal'] + o + 2] = geo.attributes.normal.array[(t + ids[i]) * 3 + 2] * sz;
                    if(geo.attributes.uv){
                        vertexBuffer[n + attr_offet['uv'] + o + 0] = geo.attributes.uv.array[(t + ids[i]) * 2 + 0] * uvScale.x;
                        vertexBuffer[n + attr_offet['uv'] + o + 1] = geo.attributes.uv.array[(t + ids[i]) * 2 + 1] * uvScale.y;
                    }
                }
            }
        }
    }
}

class webGPUSpritePrimitive extends webGPUPrimitive{

    constructor(tex) {
        super({
            attributes: [ 
                {name : 'position', type: 'vec2', location : 0},
                {name : 'uv', type: 'vec2', location : 1},
            ], 
            uniforms : [
                {name: 'coords', type:'vec4', location : 0},
                {name: 'tcoords', type:'vec4', location : 0},
                {name: 'opacity', type:'vec4', location : 0},
                {name: 'Texture', type:'tex', location : 1}
            ], 
            pipeline : 'ui'
        }, 
        2);

        this.scale = {x: 1.0, y: 1.0};
        this.pos = {x: 0.0, y: 0.0};

        this.texScale = {x: 1.0, y: 1.0};
        this.texPos = {x: 0.0, y: 0.0};

        this.tex = tex;

        this.opacity = 1.0;
        this.rotation = 0.0;
    }

    clone(cloneMat = false) {
        return new webGPUSpritePrimitive(this.tex);
    }
    
    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        for(const k in model) {

            if(k === 'coords'){
                uniformBuffer[ofs + 0] = this.pos.x;
                uniformBuffer[ofs + 1] = this.pos.y;
                uniformBuffer[ofs + 2] = this.scale.x;
                uniformBuffer[ofs + 3] = this.scale.y;

            }
            if(k === 'tcoords'){
                uniformBuffer[ofs + 0] = this.texPos.x;
                uniformBuffer[ofs + 1] = this.texPos.y;
                uniformBuffer[ofs + 2] = this.texScale.x;
                uniformBuffer[ofs + 3] = this.texScale.y;

            }
            if(k === 'params'){
                uniformBuffer[ofs + 0] = this.opacity;
                uniformBuffer[ofs + 1] = this.rotation;
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
}

class webGPUUIMeshPrimitive extends webGPUPrimitive{
    constructor(ntris, material) {
        super({
            attributes: [ 
                {name : 'position', type: 'vec3', location : 0}
            ], 
            uniforms : [
                {name: 'color', type:'vec4', location : 0},
                {name: 'coords', type:'vec4', location : 0},
                {name: 'params', type:'vec4', location : 0}
            ], 
            pipeline : 'uimesh'
        }, 
        ntris,
        material);

        this.scale = {x: 1.0, y: 1.0};
        this.pos = {x: 0.0, y: 0.0};
    }

    clone(cloneMat = false) {
        return new webGPUUIMeshPrimitive();
    }
    
    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        for(const k in model) {

            if(k === 'coords'){
                uniformBuffer[ofs + 0] = this.pos.x;
                uniformBuffer[ofs + 1] = this.pos.y;
                uniformBuffer[ofs + 2] = this.scale.x;
                uniformBuffer[ofs + 3] = this.scale.y;

            }
            if(k === 'color'){
                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            }

            if(k === 'params'){


            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }

    
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

        if(grp !== null ) {
            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {

                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                        }
                    }
                }
            }
        } else {

            for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;

                    for(let e of this.vertexLayout.entries) {
                        
                        if(e.name == 'position') {
                            vertexBuffer[n + e.offset + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                            vertexBuffer[n + e.offset + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                            vertexBuffer[n + e.offset + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                        }
                    }
                }
            }
        }
    }
}


class webGPUToonPrimitive extends webGPUPrimitive{

    constructor(nTriangles, material, hasSkinning = false) {

        const attrs = hasSkinning ? 
        [ 
            {name : 'position', type: 'vec3', location : 0},
            {name : 'boneIndex', type: 'u32', location : 1},
            {name : 'weight', type: 'u32', location : 2},
            {name : 'normal', type: 'vec3', location : 3}
        ]
        :
        [ 
            {name : 'position', type: 'vec3', location : 0},
            {name : 'normal', type: 'vec3', location : 3}
        ];
        super({
            attributes : attrs, 
            uniforms : [    {name: 'diffuse', type:'vec4', location : 0},
                            {name: 'Texture', type:'tex', location : 1}], 
            pipeline : 'ToonShading'
        }, 
        nTriangles, 
        {name: material.name, tex: material.tex, diffuse : {r:material.diffuse.r, g:material.diffuse.g, b:material.diffuse.b}},
        hasSkinning, false, 4);
    }

    cloneMat() {

        return { opacity : this.material.opacity,
                 diffuse :this.material.diffuse,
                 roughness : this.material.roughness,
                 reflectivity: this.material.reflectivity,
                 tex : this.material.tex
                }
    }
    
    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat(this.material) : this.material;
        return new webGPUToonPrimitive(this.nTriangles, mat);
    }
    
    writeUniforms(uniformBuffer, model, offset = 0) {

        let ofs = 0;

        for(let k in model) {
            if (k ==='diffuse') {

                uniformBuffer[offset + 0] = this.material.diffuse.r;
                uniformBuffer[offset + 1] = this.material.diffuse.g;
                uniformBuffer[offset + 2] = this.material.diffuse.b;
                uniformBuffer[offset + 3] = 1.0;
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }


    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

         const attr_offet = {};

        for(let e of this.vertexLayout.entries) {

            if(e.name == 'position') {
                attr_offet['position'] =e.offset;
            }else if(e.name == 'normal') {
                attr_offet['normal'] = e.offset;
            }
        }

        if(grp !== null ) {
            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0;i<3;i++) {

                    const o = i * this.vertexLayout.numFloats;

                    vertexBuffer[n + attr_offet['position'] + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;

                    vertexBuffer[n + attr_offet['normal'] + o +0] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o +1] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['normal'] + o +2] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                }
            }
        } else {

            for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i =0;i<3;i++) {
                    const o = i * this.vertexLayout.numFloats;

                
                    vertexBuffer[n + attr_offet['position'] + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2] * sz;
                    vertexBuffer[n + attr_offet['normal'] + o + 0] = geo.attributes.normal.array[(t + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o + 1] = geo.attributes.normal.array[(t + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['normal'] + o + 2] = geo.attributes.normal.array[(t + ids[i]) * 3 + 2] * sz;

                }
            }
        }
    }
}

class webGPUPixelLightingPrimitive extends webGPUPrimitive{

    constructor(nTriangles, material, hasColor = false) {
        let pipe =  'PixelLightning';
        let uniforms = [
            {name: 'params', type:'vec4', location : 0},
            {name: 'modelColor', type:'vec4', location : 0},
            {name: 'modelSpecular+Rough', type:'vec4', location : 0},
            {name: 'clearcoat', type:'vec4', location : 0},
            {name: 'Texture Box', type:'vec4', location : 0},
            {name: 'Texture Transform', type:'transform2', location : 0},
            {name: 'Texture Pos', type:'vec2', location : 0},
            {name: 'texture', type:'tex', location : 1},
            {name: 'normalMap', type:'normalmap', location : 2},
            {name: 'roughnessMap', type:'roughnessmap', location : 3},
            {name: 'emissiveMap', type:'emissivemap', location : 4},
        ];

        let startLoc = 5;
  
        if(material.driftmap !== null){
            pipe += '-Road';
            uniforms.push({name: 'driftMap', type:'driftmap', location : startLoc});
            startLoc++;
        }
       
        if(material.secmap !== null){
            uniforms.push({name: 'secMap', type:'secmap', location : startLoc});
            startLoc++;
        }

        if(material.secnmap !== null){
            uniforms.push({name: 'secNMap', type:'secnmap', location : startLoc});
            startLoc++;
        }

        const attrs =[ 
                {name : 'position', type: 'vec3', location : 0},
                {name : 'uv', type: 'vec2', location : 1},
                {name : 'normal', type: 'vec3', location : 2},
                {name : 'tangent', type: 'vec3', location : 3},
                {name : 'binormal', type: 'vec3', location : 4},
                {name : 'uv2', type: 'vec2', location : 5},
            ];

        if(material.driftmap !== null){
            attrs.push({name : 'uv3', type: 'vec2', location : 11});
        }
            
        if((hasColor)&&(material.driftmap !== null)){
            attrs.push({name : 'color', type: 'vec4', location : 12});
        }

        super({
            attributes: attrs, 
            uniforms : uniforms, 
            pipeline : pipe
        }, nTriangles, material, false, hasColor, 6);

        this.material.texBox = {x: 0.0 , y: 0.0, z: 1.0 , w: 1.0};
        this.material.texTransform = new Transformation2 ();
        this.material.texPos = new THREE.Vector2(0.0, 0.0);
        
    }

    cloneMat() {

        return { diffuse : {r:this.material.diffuse.r, g:this.material.diffuse.g, b:this.material.diffuse.b},
                 ambiant : {r:this.material.ambiant.r, g:this.material.ambiant.g, b:this.material.ambiant.b},
                 specular : {r:this.material.specular.r, g:this.material.specular.g, b:this.material.specular.b},
                 metalness : this.material.metalness,
                 opacity : this.material.opacity,
                 transparent : this.material.transparent,
                 alphaTest: this.material.alphaTest,
                 clearcoat : this.material.clearcoat,
                 clearcoatRougness : this.material.clearcoatRougness,
                 clearcoatNormalScale : this.material.clearcoatNormalScale,
                 reflectivity : this.material.reflectivity,
                 roughness : this.material.roughness,
                 texBox : {x:this.material.texBox.x, y:this.material.texBox.y, z:this.material.texBox.z, w:this.material.texBox.w},
                 texTransform : new Transformation2(this.material.texTransform.position, this.material.texTransform.angle),
                 tex : this.material.tex,
                 nmap : this.material.nmap,
                 rmap : this.material.rmap,
                 emap : this.material.emap,
                 secmap : this.material.secmap,
                 secnmap : this.material.secnmap,
                 driftmap : this.material.driftmap

                 //bmap : this.material.bmap
                }
    }
    
    clone(cloneMat = false)
    {
        const mat = cloneMat? this.cloneMat() : this.material;
        return new webGPUPixelLightingPrimitive(this.nTriangles, mat, false, this.hasColor);
    }

    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        // { 'model' : 'transform'} 
        // { 'modelView': 'mat3'} 
        // { 'modelView': 'transform', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}
        for (let k in model) {

            if (k === 'texBox') {
                if(model[k] === 'vec4') {
                    uniformBuffer[ofs + 0] = this.material.texBox.x;
                    uniformBuffer[ofs + 1] = this.material.texBox.y;
                    uniformBuffer[ofs + 2] = this.material.texBox.z;
                    uniformBuffer[ofs + 3] = this.material.texBox.w;
                }

            }
            else if (k === 'texTransform') {
                if(model[k] === 'transform2') {
                    this.material.texTransform.toBuffer(uniformBuffer, ofs);
                }

            } else if (k === 'texPos') {
                if(model[k] === 'texPos') {
                    uniformBuffer[ofs + 0] = this.material.texPos.x;
                    uniformBuffer[ofs + 1] = this.material.texPos.y;
                }

            }
            else if (k==='diffuse') {

                uniformBuffer[ofs + 0] = this.material.diffuse.r;
                uniformBuffer[ofs + 1] = this.material.diffuse.g;
                uniformBuffer[ofs + 2] = this.material.diffuse.b;
                uniformBuffer[ofs + 3] = this.material.opacity;
            }  else if (k==='specular') {

                uniformBuffer[ofs + 0] = this.material.specular.r;
                uniformBuffer[ofs + 1] = this.material.specular.g;
                uniformBuffer[ofs + 2] = this.material.specular.b;
                uniformBuffer[ofs + 3] = this.material.roughness;
            }
            else if (k==='clearcoat') {

                uniformBuffer[ofs + 0] = this.material.clearcoat;
                uniformBuffer[ofs + 1] = this.material.clearcoatRougness;
                uniformBuffer[ofs + 2] = this.material.clearcoatNormalScale.x;
                uniformBuffer[ofs + 3] = this.material.clearcoatNormalScale.y;
            }
            else if (k ==='params') {
                uniformBuffer[ofs + 1] = this.material.bumpScale;
                uniformBuffer[ofs + 2] = this.material.metalness;
                uniformBuffer[ofs + 3] = this.material.emissiveIntensity;
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {


        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids = (flip.t) ? [2,1,0] : (det > 0) ? [0,1,2]:[2,1,0];

        if(!geo.attributes.tangent) {
            const tangents = generateTangents(geo.attributes.position.array, geo.attributes.normal.array, geo.attributes.uv.array);
            geo.setAttribute('tangent', new THREE.Float32BufferAttribute(tangents, 4));
        }

        const attr_offet={}

        for(let e of this.vertexLayout.entries) {

            if(e.name == 'position') {
                attr_offet['position'] =e.offset;
            }else if(e.name == 'normal') {
                attr_offet['normal'] = e.offset;
            }else if(e.name == 'uv') {
                attr_offet['uv'] = e.offset;
            }else if(e.name == 'uv2') {
                attr_offet['uv2'] = e.offset;
            }else if(e.name == 'uv3') {
                attr_offet['uv3'] = e.offset;
            }else if(e.name == 'tangent') {
                attr_offet['tangent'] = e.offset;
            }else if(e.name == 'binormal') {
                attr_offet['binormal'] = e.offset;
            }else if(e.name == 'color') {
                attr_offet['color'] = e.offset;
            }
        }
        if(grp !== null ) {

            for (let v = 0, n = 0; v < grp.count; v+=3, n+= this.vertexLayout.numFloats * 3) {

                for(let i =0; i<3; i++) {

                    const o = i * this.vertexLayout.numFloats;

                
                    vertexBuffer[n + attr_offet['position'] + o +0] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['position'] + o +1] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n + attr_offet['position'] + o +2] = geo.attributes.position.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                
                    vertexBuffer[n + attr_offet['normal'] + o +0] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 0] * sx;
                    vertexBuffer[n + attr_offet['normal'] + o +1] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 1] * sy;
                    vertexBuffer[n +  attr_offet['normal'] + o +2] = geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 2] * sz;
                
                    vertexBuffer[n +  attr_offet['uv'] + o +0] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 0] * uvScale.x;
                    vertexBuffer[n +  attr_offet['uv'] + o +1] = geo.attributes.uv.array[(grp.start + v + ids[i]) * 2 + 1] * uvScale.y;
                        
                    if(geo.attributes.uv2) {
                        vertexBuffer[n +  attr_offet['uv2'] + o +0] = geo.attributes.uv2.array[(grp.start + v + ids[i]) * 2 + 0];
                        vertexBuffer[n +  attr_offet['uv2'] + o +1] = geo.attributes.uv2.array[(grp.start + v + ids[i]) * 2 + 1];
                    }
                    
                    if((geo.attributes.uv3)&&(attr_offet['uv3'])){
                        vertexBuffer[n +  attr_offet['uv3'] + o +0] = geo.attributes.uv3.array[(grp.start + v + ids[i]) * 2 + 0] * uvScale.x;
                        vertexBuffer[n +  attr_offet['uv3'] + o +1] = geo.attributes.uv3.array[(grp.start + v + ids[i]) * 2 + 1] * uvScale.y;
                    }
                
                    vertexBuffer[n +  attr_offet['tangent'] + o +0] = geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 0] * sx;
                    vertexBuffer[n +  attr_offet['tangent']+ o +1] = geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 1] * sy;
                    vertexBuffer[n +  attr_offet['tangent']+ o +2] = geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 2] * sz;
                
                    const vn = new THREE.Vector3(geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 0], geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 1], geo.attributes.normal.array[(grp.start + v + ids[i]) * 3 + 2]);
                    const vt = new THREE.Vector3(geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 0], geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 1], geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 2]);
                    const vbn =new THREE.Vector3().crossVectors(vn, vt).multiplyScalar(geo.attributes.tangent.array[(grp.start + v + ids[i]) * 4 + 3]).normalize();

                    vertexBuffer[n +  attr_offet['binormal'] + o + 0] = vbn.x;
                    vertexBuffer[n +  attr_offet['binormal']+ o + 1] = vbn.y;
                    vertexBuffer[n +  attr_offet['binormal']+ o + 2] = vbn.z;
                

                
                    if(geo.attributes.color){
                        vertexBuffer[n +  attr_offet['color']+ o +0] = geo.attributes.color.array[(grp.start + v + ids[i]) * 4 + 0] / 65535;
                        vertexBuffer[n +  attr_offet['color']+ o +1] = geo.attributes.color.array[(grp.start + v + ids[i]) * 4 + 1] / 65535;
                        vertexBuffer[n +  attr_offet['color'] + o +2] = geo.attributes.color.array[(grp.start + v + ids[i]) * 4 + 2] / 65535;
                        vertexBuffer[n +  attr_offet['color']+ o +3] = geo.attributes.color.array[(grp.start + v + ids[i]) * 4 + 3] / 65535;
                    }
                    
                }
            }
        } else {

            for (let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 3, t+=3) {
            
                for(let i = 0; i < 3; i++) {

                    const o = i * this.vertexLayout.numFloats;

                        vertexBuffer[n + attr_offet['position'] + o + 0] = geo.attributes.position.array[(t + ids[i]) * 3 + 0]  * sx;
                        vertexBuffer[n + attr_offet['position'] + o + 1] = geo.attributes.position.array[(t + ids[i]) * 3 + 1]  * sy;
                        vertexBuffer[n +attr_offet['position'] + o + 2] = geo.attributes.position.array[(t + ids[i]) * 3 + 2]  * sz;
    
                        vertexBuffer[n + attr_offet['normal'] + o + 0] = geo.attributes.normal.array[(t + ids[i]) * 3 + 0] * sx;
                        vertexBuffer[n + attr_offet['normal'] + o + 1] = geo.attributes.normal.array[(t + ids[i]) * 3 + 1] * sy;
                        vertexBuffer[n + attr_offet['normal'] + o + 2] = geo.attributes.normal.array[(t + ids[i]) * 3 + 2] * sz;
                    
                
                        vertexBuffer[n + attr_offet['uv'] + o + 0] = geo.attributes.uv.array[(t + ids[i]) * 2 + 0] * uvScale.x;
                        vertexBuffer[n + attr_offet['uv'] + o + 1] = geo.attributes.uv.array[(t + ids[i]) * 2 + 1] * uvScale.y;
                        
                        if(geo.attributes.uv2) {
                            vertexBuffer[n + attr_offet['uv2'] + o + 0] = geo.attributes.uv2.array[(t + ids[i]) * 2 + 0];
                            vertexBuffer[n + attr_offet['uv2'] + o + 1] = geo.attributes.uv2.array[(t + ids[i]) * 2 + 1];
                        }
                         
                        if((geo.attributes.uv3)&&(attr_offet['uv3'])){
                            vertexBuffer[n + attr_offet['uv3'] + o + 0] = geo.attributes.uv3.array[(t + ids[i]) * 2 + 0] * uvScale.x;
                            vertexBuffer[n + attr_offet['uv3'] + o + 1] = geo.attributes.uv3.array[(t + ids[i]) * 2 + 1] * uvScale.y;

                        }
                        
                    
                        vertexBuffer[n + attr_offet['tangent'] + o + 0] = geo.attributes.tangent.array[(t + ids[i]) * 4 + 0] * sx;
                        vertexBuffer[n + attr_offet['tangent'] + o + 1] = geo.attributes.tangent.array[(t + ids[i]) * 4 + 1] * sy;
                        vertexBuffer[n + attr_offet['tangent'] + o + 2] = geo.attributes.tangent.array[(t + ids[i]) * 4 + 2] * sz;

                        const vn = new THREE.Vector3(geo.attributes.normal.array[(t + ids[i]) * 3 + 0], geo.attributes.normal.array[(t + ids[i]) * 3 + 1], geo.attributes.normal.array[(t + ids[i]) * 3 + 2]);
                        const vt = new THREE.Vector3(geo.attributes.tangent.array[(t + ids[i]) * 4 + 0], geo.attributes.tangent.array[(t + ids[i]) * 4 + 1], geo.attributes.tangent.array[(t + ids[i]) * 4 + 2]);
                        const vbn =  new THREE.Vector3().crossVectors(vn, vt).multiplyScalar(geo.attributes.tangent.array[(t + ids[i]) * 4 + 3]).normalize();

                        vertexBuffer[n + attr_offet['binormal'] + o + 0] = vbn.x;
                        vertexBuffer[n + attr_offet['binormal'] + o + 1] = vbn.y;
                        vertexBuffer[n + attr_offet['binormal'] + o + 2] = vbn.z;

                        if(geo.attributes.color){
                            vertexBuffer[n + attr_offet['color'] + o + 0] = geo.attributes.color.array[(t + ids[i]) * 4 + 0] / 65535;
                            vertexBuffer[n + attr_offet['color'] + o + 1] = geo.attributes.color.array[(t + ids[i]) * 4 + 1] / 65535;
                            vertexBuffer[n + attr_offet['color'] + o + 2] = geo.attributes.color.array[(t + ids[i]) * 4 + 2] / 65535;
                            vertexBuffer[n + attr_offet['color'] + o + 3] = geo.attributes.color.array[(t + ids[i]) * 4 + 3] / 65535;
                        }
                }
            }
        }
    }
}




    /*
    arrayStride: SHADER_VERTEX_ATTR_COUNT * 4, // 3 position 2 uv,
    attributes: [{
      shaderLocation: 0, //position
      offset: 0,
      format: 'float32x3'
    }, {
      shaderLocation: 1, //uv
      offset: 3 * 4,
      format: 'float32x2'
    }, {
      shaderLocation: 2, // customColor
      offset: 5 * 4,
      format: 'float32x3'
    }, {
      shaderLocation: 3, // customOpacity
      offset: 8 * 4,
      format: 'float32'
    }, {
      shaderLocation: 4, // customSize
      offset: 9 * 4,
      format: 'float32'
    }, {
      shaderLocation: 5, // customAngle
      offset: 10 * 4,
      format: 'float32'
    }, {
      shaderLocation: 6, // customVisible
      offset: 11 * 4,
      format: 'float32'
    }]
    */
class webGPUParticlePrimitive extends webGPUPrimitive{
    constructor() {
        super( 
        {
            attributes: 
            [
                {name : 'position', type: 'vec3', location : 0}, 
                {name : 'uv', type: 'vec2', location : 1}
            ], 
            uniforms: [{name: 'Texture', type:'tex', location : 1}], 
            pipeline : 'Particles'
        }, 
        2);

      
        
        this.particlesPts= [new THREE.Vector3(-0.5,-0.5,0),
                            new THREE.Vector3(-0.5,+0.5,0),
                            new THREE.Vector3(+0.5,-0.5,0),
                            new THREE.Vector3(+0.5,+0.5,0)];
                            
        this.particlesUv= [{u:0,v:0},{u:0, v:1},{u:1,v:0},{u:1,v:1}];
        this.particlesIdxs=[0,1,2,2,1,3];
        this.isBillBloard = true;


        this.texTransform = new Transformation2 ();

    }

    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {

        const p = new THREE.Vector3();
        const bt = new Transformation3();

        const attr_offet = {};

        for(let e of this.vertexLayout.entries) {

            if(e.name == 'position') {
                attr_offet['position'] = {offset :  e.offset};
            }else if(e.name == 'uv') {
                attr_offet['uv'] = {offset :  e.offset};
            }
        }

        for(let k=0, i=0; k<6; k++, i += this.vertexLayout.numFloats) {
         
            vertexBuffer[i + attr_offet['position'].offset + 0] = this.particlesPts[this.particlesIdxs[k]].x;
            vertexBuffer[i + attr_offet['position'].offset + 1] = this.particlesPts[this.particlesIdxs[k]].y;
            vertexBuffer[i + attr_offet['position'].offset + 2] = this.particlesPts[this.particlesIdxs[k]].z;

            vertexBuffer[i + attr_offet['uv'].offset + 0] = this.particlesUv[this.particlesIdxs[k]].u;
            vertexBuffer[i + attr_offet['uv'].offset + 1] = this.particlesUv[this.particlesIdxs[k]].v;
        }
           
    }

    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        for (let k in model) {
            
            if (k === 'texBox') {
                if(model[k] === 'vec4') {
                    uniformBuffer[ofs + 0] = 0.0;
                    uniformBuffer[ofs + 1] = 0.0;
                    uniformBuffer[ofs + 2] = 1.0;
                    uniformBuffer[ofs + 3] = 1.0;
                }

            }
            else if (k === 'texTransform') {
                if(model[k] === 'transform2') {
                    this.texTransform.toBuffer(uniformBuffer, ofs);
                }

            }
            else if (k ==='params') {
                uniformBuffer[ofs + 1] = this.material.alphaTest;
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 
  }

  class webGPURainPrimitive extends webGPUPrimitive{
    constructor(numDrops, size = 1) {
        super( 
        {
            attributes: 
            [
                {name : 'position', type: 'vec3', location : 0}, 
                {name : 'uv', type: 'vec2', location : 1},
            ], 
            uniforms: [ {name: 'modelColor', type:'vec4', location : 0},
                        {name: 'size', type:'vec4', location : 0}, 
                        {name: 'Texture Box', type:'vec4', location : 0},
                        {name: 'Texture Transform', type:'transform2', location : 0},
                        {name: 'Texture', type:'tex', location : 1}], 
            pipeline : 'Rain'
        }, 
        numDrops * 2);

        this.numDrops = numDrops;
        this.dropSize = size;
        

        this.dropPts= [ new THREE.Vector3(-0.5,-0.5,0),
                        new THREE.Vector3(-0.5,+0.5,0),
                        new THREE.Vector3(+0.5,-0.5,0),
                        new THREE.Vector3(+0.5,+0.5,0)];
                            
        this.dropUv= [{u:0,v:0},{u:0, v:1},{u:1,v:0},{u:1,v:1}];
        this.dropIdxs=[2,1,0,3,1,2];
        this.texTransform = new Transformation2 ();

    }

    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {

        const p = new THREE.Vector3();
        const bt = new Transformation3();
   
        for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats * 6 , t++) {
            
            const s = this.dropSize;

            this.dropPts[0].set(-0.5 * s, -0.5 * s,0);
            this.dropPts[1].set(-0.5 * s, +0.5 * s,0);
            this.dropPts[2].set(+0.5 * s, -0.5 * s,0);
            this.dropPts[3].set(+0.5 * s, +0.5 * s,0);

            p.set(geo.attributes.position.array[t * 3 + 0], geo.attributes.position.array[t * 3 + 1], geo.attributes.position.array[t * 3 + 2]);
            bt.set(p);
            bt.lookAt(transform.position);

            for(let e of this.vertexLayout.entries) {

                for(let k=0, i=0; k<6; k++, i += this.vertexLayout.numFloats) {
        
                    if(e.name == 'position') {

                        const tp = bt.applyV(this.dropPts[this.dropIdxs[k]]);
                       
                        vertexBuffer[n + i + e.offset + 0] = tp.x;
                        vertexBuffer[n + i + e.offset + 1] = tp.y;
                        vertexBuffer[n + i + e.offset + 2] = tp.z;
                    }
                    if(e.name == 'uv') {
                        vertexBuffer[n + i + e.offset + 0] = this.dropUv[this.dropIdxs[k]].u;
                        vertexBuffer[n + i + e.offset + 1] = this.dropUv[this.dropIdxs[k]].v;
                    }
                }
            }
        }
    }


    writeUniforms(uniformBuffer, model) {

        let ofs = 0;

        // { 'model' : 'transform'} 
        // { 'modelView': 'mat3'} 
        // { 'modelView': 'transform', 'diffuse' : 'vec4', 'ambiant' : 'vec4', 'specular' : 'vec4'}
        for(let k in model) {

            if (k === 'texBox') {
                if(model[k] === 'vec4') {
                    uniformBuffer[ofs + 0] = 0;
                    uniformBuffer[ofs + 1] = 0;
                    uniformBuffer[ofs + 2] = 1;
                    uniformBuffer[ofs + 3] = 1;
                }

            }
            else if (k === 'texTransform') {
                if(model[k] === 'transform2') {
                    this.texTransform.toBuffer(uniformBuffer, ofs);
                }
            }
            else if (k==='diffuse') {

                uniformBuffer[ofs + 0] = 1;
                uniformBuffer[ofs + 1] = 1;
                uniformBuffer[ofs + 2] = 1;
                uniformBuffer[ofs + 3] = 1;
            } else if (k ==='params') {

                uniformBuffer[ofs + 1] = 0.3;
                uniformBuffer[ofs + 3] = 0.0;
            }
            ofs += getTypeSize(model[k], 4);
        }

        return ofs;
    }
 }

class webGPUSkyBoxPrimitive extends webGPUPrimitive{

    constructor(nTriangles) {
        super( 
        {
            attributes: 
            [
                {name : 'position', type: 'vec3', location : 0}, 
                {name : 'uv', type: 'vec2', location : 1}
            ], 
            uniforms: [], 
            pipeline : 'Skybox'
        }, 
        nTriangles);
    }

    writeAttributes(vertexBuffer, geo, flip, uvScale = {x:1.0,y:1.0}, grp = null, transform = null) {

        
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        for(let n=0, t=0; t< this.nTriangles * 3; n+= this.vertexLayout.numFloats , t++) {
            
            for(let e of this.vertexLayout.entries) {
                if(e.name == 'position') {
                    vertexBuffer[n + e.offset + 0] = geo.attributes.position.array[t * 3 + 0] * sx;
                    vertexBuffer[n + e.offset + 1] = geo.attributes.position.array[t * 3 + 1] * sy;
                    vertexBuffer[n + e.offset + 2] = geo.attributes.position.array[t * 3 + 2] * sz;
                }
                if(e.name == 'uv') {
                    vertexBuffer[n + e.offset + 0] = geo.attributes.uv.array[t * 2 + 0];
                    vertexBuffer[n + e.offset + 1] = geo.attributes.uv.array[t * 2 + 1];
                }
            }
        }
    }
}

class webGPUSprite {
    totalTriangles;
    primitive;
    vertexNumFloats;

    constructor(tex) {
        
        this.primitive = new webGPUSpritePrimitive(tex)
        this.totalTriangles = this.primitive.nTriangles;
        this.vertexNumFloats = this.totalTriangles * 3 * this.primitive.vertexLayout.numFloats;
        this.primitive.material = {name : 'sprite '+tex.label};
    }   
}

class Bone{
    id;
    name;
    transforms;
    restTransform;
    length;
    children;

    constructor(id, name, nInstances=1) {

        this.id = id;
        this.name = name;
        this.transforms = new Array(nInstances);

        for(let n=0;n<nInstances;n++)
            this.transforms[n] = new Transformation3();
        
        this.restTransform = new Transformation3();
        this.tmpTransform =  new Transformation3();
        this.bonePos = new THREE.Vector3();
        this.children = [];
    }

    addChild(b){
        this.children.push(b);
    }

    findBone(name)
    {
        if(this.name === name)
            return this;

        for(let c of this.children) {
           const b = c.findBone(name);
           if(b !== null)
            return b;
        }

        return null;
    }
    getBoneByName(name){
        return this.findBone(name);
    }
    getBoneCnt(num = 0){

        let n = num + 1;

        for(let c of this.children) {
            n = c.getBoneCnt(n);
        }

        return n;
    }


    getArray(boneBuffer, offset , pt, wt, size, instanceID = 0){

        let t;
        
        if(this.id === 0){

            this.tmpTransform.identity();
            this.tmpTransform.toBuffer(boneBuffer, offset + this.id * boneSize);
            //t = new Transformation3(pt.position);
            this.tmpTransform.set(pt.position);

        }else{

            const q = {x:0, y:0, z:0, w:1};
            pt.mulquat(this.transforms[instanceID].quaternion, q);
            //t = new Transformation3(pt.position, q);

            this.tmpTransform.set(pt.position, q);
            this.tmpTransform.toBuffer(boneBuffer, offset + this.id * boneSize);

            this.bonePos= wt.applyV({x : pt.position.x * size, y : pt.position.y * size, z : pt.position.z * size});

            

            /*
            console.log('getArray trans '+this.name+' '+this.transform.position.x.toFixed(2)+' '+this.transform.position.y.toFixed(2)+' '+this.transform.position.z.toFixed(2));
            console.log('getArray pbone '+this.name+' '+pt.position.x.toFixed(2)+' '+pt.position.y.toFixed(2)+' '+pt.position.z.toFixed(2));
            */
        }

        if(this.hasOffset){
            this.tmpTransform.position.x += this.transforms[instanceID].position.x;
            this.tmpTransform.position.y += this.transforms[instanceID].position.y;
            this.tmpTransform.position.z += this.transforms[instanceID].position.z;
        }else{
            this.tmpTransform.position.x += this.transforms[instanceID].position.y * this.tmpTransform.m3[3];
            this.tmpTransform.position.y += this.transforms[instanceID].position.y * this.tmpTransform.m3[4];
            this.tmpTransform.position.z += this.transforms[instanceID].position.y * this.tmpTransform.m3[5];
        }
     

        /*     
        console.log('getArray bone '+this.name+' '+t.position.x.toFixed(2)+' '+t.position.y.toFixed(2)+' '+t.position.z.toFixed(2));
        console.log('getArray bone '+this.name+' '+t.quaternion.x.toFixed(2)+' '+t.quaternion.y.toFixed(2)+' '+t.quaternion.z.toFixed(2)+' '+t.quaternion.w.toFixed(2));
        console.log('getArray ibone '+this.name+' '+this.restTransform.position.x.toFixed(2)+' '+this.restTransform.position.y.toFixed(2)+' '+this.restTransform.position.z.toFixed(2));
        console.log('getArray ibone '+this.name+' '+this.restTransform.quaternion.x.toFixed(2)+' '+this.restTransform.quaternion.y.toFixed(2)+' '+this.restTransform.quaternion.z.toFixed(2)+' '+this.restTransform.quaternion.w.toFixed(2));
        */
 


        
        for(let c of this.children) {
            c.getArray(boneBuffer, offset, this.tmpTransform, wt, size, instanceID);
        }
    }

    getArrayRest(uniformBuffer){

        let t;
        
        if(this.id === 0){

            this.tmpTransform.identity();
            this.tmpTransform.toBuffer(uniformBuffer, this.id * boneSize);

        }else{
            this.restTransform.toBuffer(uniformBuffer, this.id * boneSize);
        }
        
        for(let c of this.children) {
            c.getArrayRest(uniformBuffer);
        }
    }
}



class webGPUMesh {

    transformations;
    tmpTransformation;
    totalTriangles;
    vertexNumFloats;
    boneBufferRange;
    primitives;
    size;
    children;
    rootBone;
    updateBones;

    constructor(nInstances = 1) {
        this.vertexNumFloats = 0;
        this.totalTriangles = 0;
        this.is_static = true;

        this.initialized = 0;
        this.nInstances = nInstances;

        this.transformations = new Array(this.nInstances);

        for(let n=0;n<this.nInstances;n++){
            this.transformations[n] = new Transformation3();
        }

        this.distances = new Uint32Array(this.nInstances);
        this.indices = new Uint32Array(this.nInstances);

        for(let n=0;n<this.nInstances;n++){
            this.indices[n] = n;

        }
             
        
        this.tmpTransformation = new Transformation3();
        this.size = 1.0;
        this.primitives = [];
        this.children = [];
        this.rootBone = new Bone(0, "root", nInstances);
        this.updateBones = true;
        this.lastBoneId = 1;
        this.boneBufferRange = null;
        this.boneGroups = [];
    }


    sortInstances(pos, minDist){

    
       
        for(let n=0;n<this.nInstances;n++){

            const dx = pos.x - this.transformations[this.indices[n]].position.x;
            const dz = pos.z - this.transformations[this.indices[n]].position.z;
            this.distances[this.indices[n]] = dx*dx + dz * dz;
        }

        const self = this;

        this.indices.sort(function(a,b){

            if(self.distances[a]>self.distances[b])
                return 1;

            if(self.distances[a]<self.distances[b])
                return -1;

            return 0;
        });

        const sqDist = minDist*minDist;
        for(let n=0;n<this.nInstances;n++){

            if(this.distances[this.indices[n]]>sqDist)
                return n;
        }

        return this.nInstances;



    }

    addPrimitive(p) {
        
        this.totalTriangles += p.nTriangles;
        this.vertexNumFloats += p.nTriangles * 3 * p.vertexLayout.numFloats;
        this.primitives.push(p);
    }

    addSkelBone(bone, plen, parent, pos, quat, size = 1, nInstances = 1){

        const bonePos = new THREE.Vector3(pos.x, pos.y, pos.z);
        const bq = new THREE.Quaternion(bone.quat.x, bone.quat.y, bone.quat.z, bone.quat.w);
        const boneQuat = new THREE.Quaternion(quat.x, quat.y, quat.z, quat.w).multiply(bq);
        const boneOffset = new THREE.Vector3(0,  bone.length * size  , 0);
        const boneRel = new THREE.Vector3(0,  plen  , 0);
        const boneVec = boneOffset.clone().applyQuaternion(boneQuat);
        const nextPos = bone.boneOffset ? new THREE.Vector3().addVectors(bonePos, bone.boneOffset) : new THREE.Vector3().addVectors(bonePos, boneVec);

        const newBone = new Bone(bone.id, bone.name, nInstances);

        newBone.restTransform.position.x = -bonePos.x;
        newBone.restTransform.position.y = -bonePos.y;
        newBone.restTransform.position.z = -bonePos.z;

        newBone.restTransform.quaternion.x = -boneQuat.x;
        newBone.restTransform.quaternion.y = -boneQuat.y;
        newBone.restTransform.quaternion.z = -boneQuat.z;
        newBone.restTransform.quaternion.w = boneQuat.w;
        newBone.restTransform.computeMatrix();

        //const t = new Transformation3(bonePos, boneQuat);
        //t.getViewTransform(newBone.restTransform);

        for(let n=0;n<nInstances;n++){
            newBone.transforms[n].set(bone.boneOffset ?  bone.boneOffset : boneOffset, bone.quat);
        }
        newBone.hasOffset = bone.boneOffset ? true : false;

        

        /*
        console.log('addSkelBone restTransform '+newBone.name+' '+ newBone.restTransform.position.x.toFixed(2)+' '+newBone.restTransform.position.y.toFixed(2)+' '+newBone.restTransform.position.z.toFixed(2));
        console.log('addSkelBone restTransform '+newBone.name+' '+ newBone.restTransform.quaternion.x.toFixed(2)+' '+newBone.restTransform.quaternion.y.toFixed(2)+' '+newBone.restTransform.quaternion.z.toFixed(2)+' '+newBone.restTransform.quaternion.w.toFixed(2));
        */
        /*
        console.log('addSkelBone '+newBone.name+' '+ boneQuat.x.toFixed(2)+' '+boneQuat.y.toFixed(2)+' '+boneQuat.z.toFixed(2)+' '+boneQuat.w.toFixed(2));
        console.log('addSkelBone transform '+newBone.name+' '+ newBone.transform.position.x.toFixed(2)+' '+newBone.transform.position.y.toFixed(2)+' '+newBone.transform.position.z.toFixed(2));
        console.log('addSkelBone transform '+newBone.name+' '+ newBone.transform.quaternion.x.toFixed(2)+' '+newBone.transform.quaternion.y.toFixed(2)+' '+newBone.transform.quaternion.z.toFixed(2)+' '+newBone.transform.quaternion.w.toFixed(2));
        */
        

        parent.addChild(newBone);

       for(let c of bone.children){
            this.addSkelBone(c, bone.length,  newBone, nextPos, boneQuat, size, nInstances);
        }

    }

    writeTransformAttributes(transformBuffer, modelTransform, size, offset = 0, instanceID = 0){
        modelTransform.toBufferPacked(transformBuffer, offset);
        transformBuffer[offset + 12] = size;
        transformBuffer[offset + 13] = this.rootBone.numBones;
        transformBuffer[offset + 14] = instanceID;
    }

    bindSkeleton(skeleton, mesh, size = 1){

        const equat = new THREE.Quaternion();
        const epos = new THREE.Vector3();

        for(let n=0;n<this.nInstances;n++){
            this.rootBone.transforms[n].set(epos, equat);
        }

        this.rootBone.root_pos= {x: skeleton.root.pos.x * size, y: skeleton.root.pos.y * size, z: skeleton.root.pos.z * size};
       
        for(let c of skeleton.root.children){
            this.addSkelBone(c, skeleton.root.pos.y * size, this.rootBone, this.rootBone.root_pos, skeleton.root.quat, size, this.nInstances);
        }
    }

    addBone(name, t, nInstances = 1){

        const newBone = new Bone(this.lastBoneId++, name, nInstances);
        newBone.restTransform.set(t.position, t.quaternion);

        for(let n=0;n<nInstances;n++){
            newBone.transforms[n].set(t.position, t.quaternion);
        }
        
        this.rootBone.addChild(newBone)
    }

    findBone(name){
        return this.rootBone.findBone(name);
    }

    writeBonesRest(uniformBuffer) {

        this.rootBone.getArrayRest(uniformBuffer);
        this.updateBones = false;
    }

    writeBones(uniformBuffer, offset = 0, instanceID = 0) {

        const t = new Transformation3( this.rootBone.root_pos)

        this.rootBone.getArray(uniformBuffer, offset , t , this.transformations[instanceID], this.size, instanceID);

       
        this.updateBones = false;
    }
}

class webGPUSkyBoxMesh extends webGPUMesh {

    constructor(nTriangles) {
        super();
        const newPrimitive = new webGPUSkyBoxPrimitive(nTriangles);
        this.addPrimitive(newPrimitive);
    }
}

class webGPUWorldUIMesh extends webGPUMesh {

    constructor(pos) {
        super();

        this.nInstances = 1;
        this.is_static = false;

        this.transformations[0].set(pos);
        this.size=  new THREE.Vector2()
        this.size.x = 1;   
        this.size.y = 1;
 

    }

    writeTransformAttributes(transformBuffer, modelTransform, size, offset = 0, instanceID = 0){
        modelTransform.toBufferPacked(transformBuffer, offset);
        transformBuffer[offset + 12] = this.size.x;
        transformBuffer[offset + 13] = this.size.y;
    }
}



class webGPUParticleMesh extends webGPUMesh {

    constructor(settings, pos, size = 0.1) {
        super();

        settings.positionBase = pos;

        this.particles = new ParticleEngine(pos);
        this.particles.setValues(settings);
        this.particles.initialize();


        this.nInstances = this.particles.particleCount;
        this.is_static = false;
        this.indices = [0];
        
        
        const newPrimitive = new webGPUParticlePrimitive();
        newPrimitive.material = { name: 'particle '+settings.name, disableShadow:false, tex : null, opacity : 1.0, transparent : true, alphaTest : 0.3};
        this.addPrimitive(newPrimitive);
    }

    sortInstances()
    {
        return this.nInstances;
    }

    writeTransformAttributes(transformBuffer, transform, size, offset = 0, instanceID = 0){

        const geo = this.particles.particleMesh.geometry;

        transformBuffer[offset +  9] = geo.attributes.position.array[instanceID * 3 + 0] + transform.position.x;
        transformBuffer[offset + 10] = geo.attributes.position.array[instanceID * 3 + 1] + transform.position.y;
        transformBuffer[offset + 11] = geo.attributes.position.array[instanceID * 3 + 2] + transform.position.z;

        transformBuffer[offset + 0] = geo.attributes.customColor.array[instanceID * 3 + 0];
        transformBuffer[offset + 1] = geo.attributes.customColor.array[instanceID * 3 + 1];
        transformBuffer[offset + 2] = geo.attributes.customColor.array[instanceID * 3 + 2];

        transformBuffer[offset + 3] = geo.attributes.customOpacity.array[instanceID];
        transformBuffer[offset + 4] = geo.attributes.customAngle.array[instanceID] * Math.PI / 180.0;
        transformBuffer[offset + 5] = geo.attributes.customVisible.array[instanceID];

        transformBuffer[offset + 6] = geo.attributes.customSize.array[instanceID];

    }
}


class webGPURainMesh extends webGPUMesh {

    constructor(numDrops, size = 0.1, tex) {
        super();
        const newPrimitive = new webGPURainPrimitive(numDrops, size);
        newPrimitive.material = { tex : tex, opacity : 1.0, transparent : true, alphaTest : 0.1};

      

        this.addPrimitive(newPrimitive);
    }

    update(vertexBuffer, geo, cameraTransform) {
        
        this.primitives[0].writeAttributes(vertexBuffer, geo, {x:false,y:false,z:false}, null, null, cameraTransform);
    }
}

class Light {
    type;
    vec;
    vec2;
    color;

    constructor(t,v, d, c, a, a2 = 8.0) {
        this.type = t;
        this.vec = v;
        this.vec2 = d;
        this.color = c;
        this.attenuation = {x:a, y:a2};
    }
}


export class EngineWebGPU extends EngineBase {

    vertexBufferSize;
    uniformBufferSize;
    canvas;
    ctx;
    lights;

    lightBuffer;
    cameraProj;
    shadowProj;
    shadowProjM;

    tmpTransformBuf;
    tmpMatrix4;
    
    GlobalFloats;
    shadowDepthTextureSize;
    renderPass;
    hiddenObjects;
    particles;

    constructor(audio = false, assetBase = '', w=100, h = 100) {

        super(audio, assetBase);

     
        this.canvas = document.createElement('canvas');
        if (!this.canvas) {
            throw new Error('Unable to create WEBGPU canvas');
        }

        this.canvas.width = w;
        this.canvas.height = h;

        this.pipelineCache = {};

        this.shadowBias = 0.00000001;
        this.shadowBlur = 2;


        this.useCameraAnchor = false;
     

        this.ctx = this.canvas.getContext('webgpu', {desynchronized : false});
        if (!this.ctx) {
            throw new Error('Unable get WEBGPU context');
        }
        
        this.vertexBufferSize = 256*1024*1024;
        this.uniformBufferSize = 0.5*1024*1024;
        this.transformBufferSize = 400 * 1024;
        this.boneBufferSize = 50 * 1024;

        const st = localStorage.getItem('shadowmaps');
        this.enableShadowMap = st? parseInt(st)> 0 : 1;


        this.camera = new THREE.PerspectiveCamera(this.cameraFOV, w / h, 0.01, 400);
        
        this.numLights = 16;
        
        this.lights = new Array(this.numLights);
        for (let n=0;n<this.numLights;n++) {
            this.lights[n] = new Light(0, {x:0,y:0,z:0}, {x:0,y:0,z:0}, {r:1,g:1,b:1}, 0)
        }

        this.n_bones = 32;
        this.shadowProjM = new THREE.Matrix4();
        this.lightBuffer = new Float32Array( this.numLights * 12);
        this.cameraProj = new Float32Array(16);
        this.shadowProj = new Float32Array(16);
        
        this.tmpTransformBuf = new Float32Array(16);
        this.tmpMatrix4 = new Float32Array(16);

        this.tmpTransform = new Transformation3();
        this.zeroQ = {x:0, y:0, z:0, w:1.0};

        const mat1 = new THREE.MeshPhysicalMaterial({ color: 0xffFFFF, transparent: true, transmission: 1.0, thickness:0.4, reflectivity: 0.3, metalness:0.0, ior: 3.0 });
        const mat2 = new THREE.MeshBasicMaterial({ color: 0xffFFFF, side: THREE.DoubleSide, depthWrite: true });

        this.matPool= new MaterialPool([mat1, mat2], [1, 2], 200 );

        this.sphereGeometry = new THREE.SphereGeometry(1, 8, 16);
        this.sphereObject1 = new THREE.Mesh(this.sphereGeometry, mat1);
        this.sphereObject2 = new THREE.Mesh(this.sphereGeometry, mat2);

        this.sphereObject1.name = 'sphere object 2';
        this.sphereObject2.name = 'sphere object 2';

                
        this.GlobalFloats = 16 /* projmat */ + 12 /* ViewMatrix */  + 4 /* ViewPos */ + 16 /* shadowViewProj */ + 16 /* fog params */ + 16 /* bump params */ + this.numLights * 12 /* lights */ + 4 /* shadow params */;
        this.GlobalUIFloats = 16 /* projmat */;
        this.bonesFloats = this.n_bones * boneSize ; /* 32 x 2 transform */
        this.shadowDepthTextureSize = 4096 * 2;
        this.renderPass = [];
        this.hiddenObjects = [];
        this.particles = [];
        this.skybox = null;
        this.tmpBuffers= [];
        this.cachedCyls = [];
        this.fogParams = new Float32Array(4);
        this.shadowParams = new Float32Array(4);
        
        this.particleId= 1;
        this.fogDensity = 0.0;
        this.fogColor = new THREE.Vector3(0.6, 0.6, 0.8);

        this.device = null;

        this.defaultScale = new THREE.Vector3(1,1,1);

        this.shadowTransform = new Transformation3();
    }

    createParticles(settings, pos, size = 0.1){

        const mesh = new webGPUParticleMesh(settings, pos, size)
        mesh.particles.pinned = false;
        this.addParticles(mesh);
        return mesh.particles;
     }
    
    dumpBones(bone, instanceID = 0){

        const t = bone.transforms[instanceID];

        console.log('bone' +bone.name +' '+t.position.x.toFixed(2)+' '+t.position.y.toFixed(2)+' '+t.position.z.toFixed(2) +' '+t.quaternion.x.toFixed(2)+' '+t.quaternion.y.toFixed(2)+' '+t.quaternion.z.toFixed(2)+' '+t.quaternion.w.toFixed(2));

        for(let b of bone.children){
            this.dumpBones(b, instanceID = 0);
        }

    }
    initScene(w=100, h =100) {

        this.hiddenObjects = [];
        this.particles = [];
        this.cachedCyls = [];
        this.curFrame = 0;
        

        this.camera = new THREE.PerspectiveCamera(this.cameraFOV, w / h, 0.01, 400);
        this.camera.projectionMatrix.toArray(this.cameraProj);
        
        this.viewTransform = new Transformation3();
        this.cameraTransform = new Transformation3(this.camera.position, this.camera.quaternion);
        this.cameraTransform.getInverse(this.viewTransform);

        //this.ShadowViewMat = new THREE.Matrix4();

        this.shadowTransform.set({x:2, y :2, z: 0});
        this.shadowTransform.lookAt({x:0, y :0, z: 0}, new THREE.Vector3(0, 0, 1 ));
       
        this.shadowProjM.makeOrthographic(-20 , 20 , -20, 20, -80, 80);
        //this.shadowProjM.makeOrthographic(-10 , 10 , -10, 10, -10, 10);
        this.shadowProjM.toArray(this.shadowProj);

        this.vertexAllocator.reset();
        this.transformAllocator.reset();
        this.uniformAllocator.reset();

        this.globalBuffer = this.uniformAllocator.getBufferRange(this.GlobalFloats);

        this.globalUniforms = [ { binding: 0, resource: { buffer: this.uniformAllocator.buffer, offset: this.globalBuffer.start, size: this.GlobalFloats * 4  } },
                                { binding: 1, resource: this.texSampler }, /* sampler */
                                { binding: 2, resource: this.emptyTex.createView()}, /* envmap */
                                { binding: 3, resource: this.texSamplerN }
                              ];

        if(this.enableShadowMap){
            this.globalUniforms.push({ binding: 4, resource: this.shadowDepthTextureView}); /* shadowmap */
            this.globalUniforms.push({ binding: 5, resource: this.shadowDepthTextureSampler }); /* shadow sampler */
        }

        for (let n=0; n<this.numLights; n++) {
            this.lights[n] = new Light(0, {x:0,y:0,z:0}, {x:0,y:0,z:0},  {r:0,g:0,b:0}, 0);
        }

        this.globalUIBuffer = this.uniformAllocator.getBufferRange(this.GlobalUIFloats);

        this.globalUIUniforms = [   { binding: 0, resource: { buffer: this.uniformAllocator.buffer, offset: this.globalUIBuffer.start, size: this.GlobalUIFloats * 4  } },
                                    { binding: 1, resource: this.texSampler }
                              ];
    }

    createSpriteVertexBuffer() {
        const encoder = this.device.createCommandEncoder();
        const n_tris = 2;
        const n_vtx = n_tris * 3;
        const vtx_sz = 4;

        this.UISpriteVertexBufferRange = this.vertexAllocator.getBufferRange( n_vtx * vtx_sz, 'sprites');

        const b = this.device.createBuffer({ size: n_vtx * vtx_sz * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const vertexBuffer =  new Float32Array(b.getMappedRange());
        vertexBuffer[0] = -0.5;
        vertexBuffer[1] = -0.5;
        vertexBuffer[2] =  0.0;
        vertexBuffer[3] =  0.0;

        vertexBuffer[4] =  0.5;
        vertexBuffer[5] = -0.5;
        vertexBuffer[6] =  1.0;
        vertexBuffer[7] =  0.0;

        vertexBuffer[8] =  0.5;
        vertexBuffer[9] =  0.5;
        vertexBuffer[10] =  1.0;
        vertexBuffer[11] =  1.0;

        vertexBuffer[12] = -0.5;
        vertexBuffer[13] = -0.5;
        vertexBuffer[14] =  0.0;
        vertexBuffer[15] =  0.0;

        vertexBuffer[16] =  0.5;
        vertexBuffer[17] =  0.5;
        vertexBuffer[18] =  1.0;
        vertexBuffer[19] =  1.0;

        vertexBuffer[20] = -0.5;
        vertexBuffer[21] =  0.5;
        vertexBuffer[22] =  0.0;
        vertexBuffer[23] =  1.0;

        b.unmap();
        encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, this.UISpriteVertexBufferRange.start, b.size);
        this.device.queue.submit([ encoder.finish()]);
        b.destroy();

    }
    /*
    createEmptyBoneBuffer(){
        const encoder = this.device.createCommandEncoder();
      
        this.emptyBoneBuffer = this.uniformAllocator.getBufferRange( this.n_bones * 32);
        this.emptyBonesEntry = { binding: 0, resource: { buffer: this.uniformAllocator.buffer, offset:  this.emptyBoneBuffer.start, size: this.bonesFloats * 4 }};

        const b = this.device.createBuffer({ size: this.n_bones * 32 * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const boneBuffer =  new Float32Array(b.getMappedRange());

        const t = new Transformation3();

        for(let n=0;n<this.n_bones;n++){
            t.toBuffer(boneBuffer, n * 32);
            t.toBuffer(boneBuffer, n * 32 + 16);
        }
        
        b.unmap();
        encoder.copyBufferToBuffer( b, 0, this.uniformAllocator.buffer, this.emptyBoneBuffer.start, b.size);
        this.device.queue.submit([ encoder.finish()]);
        b.destroy();
    }
    */
    
    async init() {

        if(this.device)
            return;

        if (!navigator.gpu) {
            throw new Error("WebGPU not supported on this browser.");
        }

        this.adapter = await navigator.gpu.requestAdapter();
        if (!this.adapter) {
            throw new Error("No appropriate GPUAdapter found.");
        }

        this.device = await this.adapter.requestDevice();
        this.device.lost.then((info) => {
            throw new Error('WEBGPU device lost '+info.message);
        });

        this.format =  navigator.gpu.getPreferredCanvasFormat();

        this.ctx.configure({device: this.device, format: this.format, alphaMode: 'premultiplied' });

        /*
        this.vertexBuffer = this.device.createBuffer({ size: this.vertexBufferSize, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });
        this.uniformBuffer = this.device.createBuffer({ size: this.uniformBufferSize, usage:  GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
        */
        
        this.vertexAllocator = new BufferAllocator(this.vertexBufferSize, 1, 0, this.device);
        this.uniformAllocator = new BufferAllocator(this.uniformBufferSize, UNIFORM_ALIGN, 1, this.device);
        
        this.transformAllocator = new BufferAllocator(this.transformBufferSize, 1, 0, this.device);
        //this.transformAllocator = this.vertexAllocator;
        
        this.boneAllocator = new BufferAllocator(this.boneBufferSize, UNIFORM_ALIGN, 3, this.device);

        if(this.enableShadowMap){
            this.shadowDepthTexture = this.device.createTexture({size: [this.shadowDepthTextureSize, this.shadowDepthTextureSize, 1], usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING, format: 'depth32float'});
            this.shadowDepthTextureView = this.shadowDepthTexture.createView();
            this.shadowDepthTextureSampler = this.device.createSampler({ compare: 'less'})
            this.depthTexture = this.device.createTexture({ size: [this.canvas.width, this.canvas.height], format: 'depth24plus-stencil8', usage: GPUTextureUsage.RENDER_ATTACHMENT });
        }else{
            this.depthTexture = this.device.createTexture({ size: [this.canvas.width, this.canvas.height], format: 'depth32float', usage: GPUTextureUsage.RENDER_ATTACHMENT });
        }

        this.texSampler = this.device.createSampler({ magFilter: 'linear', minFilter: 'linear', addressModeU: 'repeat', addressModeV: 'repeat' });
        this.texSamplerN = this.device.createSampler({ magFilter: 'nearest', minFilter: 'nearest', addressModeU: 'repeat', addressModeV: 'repeat' });

        this.emptyTex = this.create1PixelTextureRGBA({r:0, g:0, b:0});
        this.globalGroup = [];
       
        this.initScene();

        //this.createEmptyBoneBuffer();
        this.createSpriteVertexBuffer();
        await this.loadParticles();

        this.emptyBoneGroups = [];

        this.sphereGPU1 = this.addMesh(this.sphereObject1);
        this.sphereGPU2 = this.addMesh(this.sphereObject2);
    }

    enableRender(on, glDomRef) {
        if(glDomRef.current){

            if (on){

                if(glDomRef.current.children.length < 1){
                    glDomRef.current.appendChild(this.canvas);
                    this.resizeCanvasToDisplaySize(glDomRef.current.clientWidth, glDomRef.current.clientHeight);

                    /*
                    const devicePixelRatio = window.devicePixelRatio || 1;
                    this.canvas.width = this.canvas.clientWidth * devicePixelRatio;
                    this.canvas.height = this.canvas.clientHeight * devicePixelRatio;

                    this.camera = new THREE.PerspectiveCamera(this.cameraFOV, this.canvas.width / this.canvas.height, 0.01, 700);
                    this.camera.projectionMatrix.toArray(this.cameraProj);                
        
                    if(this.depthTexture)
                        this.depthTexture.destroy();

                    const format = this.enableShadowMap ? 'depth24plus-stencil8' : 'depth32float';
                    this.depthTexture = this.device.createTexture({ size: [this.canvas.width, this.canvas.height], format: format, usage: GPUTextureUsage.RENDER_ATTACHMENT });
                    */
                }           

                //
            }else{
             
                glDomRef.current.removeChild(curEngine.renderer.domElement);
            }
        }
    }

    /*
    enableRender(on) {
        var glDom = document.getElementById('gl-dom');
        if (glDom !== null) {
            glDom.innerHTML = '';

            if (on) {
                glDom.appendChild(this.canvas);
                
                const devicePixelRatio = window.devicePixelRatio || 1;
                this.canvas.width = this.canvas.clientWidth * devicePixelRatio;
                this.canvas.height = this.canvas.clientHeight * devicePixelRatio;

                this.camera = new THREE.PerspectiveCamera(this.cameraFOV, this.canvas.width / this.canvas.height, 0.01, 700);
                this.camera.projectionMatrix.toArray(this.cameraProj);                
       
                if(this.depthTexture)
                    this.depthTexture.destroy();

                const format = this.enableShadowMap ? 'depth24plus-stencil8' : 'depth32float';
                this.depthTexture = this.device.createTexture({ size: [this.canvas.width, this.canvas.height], format: format, usage: GPUTextureUsage.RENDER_ATTACHMENT });
            }
        }
    }
    */

    addRenderPass(name, func) {
        if(func)
            this.renderPass[name] = {drawFunc : func};
        else
            this.renderPass[name] = null;
            
    }

    enableLight(id, type, vec, vec2, color, attenuation, attenuation2) {
        this.lights[id].type = type;
        this.lights[id].vec = vec;
        this.lights[id].vec2 = vec2;
        this.lights[id].color = color;
        this.lights[id].attenuation = {x:attenuation, y:attenuation2};
    }

    updateLightUniforms() {

        for (let n=0;n<this.numLights;n++) {

            //const p = this.lights[n].type === 1 ? this.cameraTransform.applyQV(this.lights[n].vec) : this.cameraTransform.applyV(this.lights[n].vec);
            this.lightBuffer[n * 12 + 0] = this.lights[n].vec.x;
            this.lightBuffer[n * 12 + 1] = this.lights[n].vec.y;
            this.lightBuffer[n * 12 + 2] = this.lights[n].vec.z;
            this.lightBuffer[n * 12 + 3] = this.lights[n].attenuation.x;

            this.lightBuffer[n * 12 + 4] = this.lights[n].vec2.x;
            this.lightBuffer[n * 12 + 5] = this.lights[n].vec2.y;
            this.lightBuffer[n * 12 + 6] = this.lights[n].vec2.z;
            this.lightBuffer[n * 12 + 7] = this.lights[n].attenuation.y;

            this.lightBuffer[n * 12 + 8] = this.lights[n].color.r;
            this.lightBuffer[n * 12 + 9] = this.lights[n].color.g;
            this.lightBuffer[n * 12 + 10] = this.lights[n].color.b;
            this.lightBuffer[n * 12 + 11] = this.lights[n].type;
        }
    }
    
    addFog(density = 0.1) {
        
        this.fogDensity=density;
    }

    writeGlobalUniforms() {

        this.updateLightUniforms();

        this.fogParams[0] = this.fogDensity;
        this.fogParams[1] = this.fogColor.x;
        this.fogParams[2] = this.fogColor.y;
        this.fogParams[3] = this.fogColor.z;

        this.shadowParams[0] = this.shadowBias;
        this.shadowParams[1] = this.shadowBlur;
        this.shadowParams[2] = this.shadowDepthTextureSize;

        this.viewTransform.toBuffer(this.tmpTransformBuf);
        this.shadowTransform.getViewProj(this.shadowProj, this.tmpMatrix4);

        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalBuffer.start, this.cameraProj);
        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalBuffer.start + 64, this.tmpTransformBuf);
        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalBuffer.start + 64 + 64, this.tmpMatrix4);
        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalBuffer.start + 64 + 64 + 64, this.fogParams);
        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalBuffer.start + 64 + 64 + 64 + 16, this.lightBuffer); //8 * 12 * 4

        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalBuffer.start + 64 + 64 + 64 + 16 + this.lightBuffer.length * 4, this.shadowParams);
    }
    writeGlobalUIUniforms() {

        const UIProj = new Float32Array(this.cameraUI.projectionMatrix.clone().multiply(this.cameraUI.matrixWorldInverse).toArray());
        this.device.queue.writeBuffer(this.uniformAllocator.buffer, this.globalUIBuffer.start, UIProj);
    }
    /*
    resizeCanvasToDisplaySize(width, height) {
        const canvas = this.renderer.domElement;
        // look up the size the canvas is being displayed

        //const width = $('#gl-dom').innerWidth();
        //const height = $('#gl-dom').innerHeight();


        const dw = width;
        const dh = height; //width >= height ? height : width

        // adjust displayBuffer size to match
        if (canvas.width !== dw || canvas.height !== dh) {
            // you must pass false here or three.js sadly fights the browser

            this.renderer.setSize(dw, dh);
            this.camera.aspect = dw / dh;

            //this.camera = new THREE.PerspectiveCamera( 75, width / height, 0.1, 20000 );
            this.camera.updateProjectionMatrix();
            // update any render target sizes here
        }
    }
        */
    resizeCanvasToDisplaySize(width, height) {
        
        const canvas = this.canvas;
        // look up the size the canvas is being displayed
        //const width = $('#gl-dom').innerWidth();
        //const height = $('#gl-dom').innerHeight();

        // adjust displayBuffer size to match
        if (canvas.width !== width || canvas.height !== height) {
            // you must pass false here or three.js sadly fights the browser

            const devicePixelRatio = window.devicePixelRatio || 1;
            canvas.width = width;
            canvas.height = height;

            this.camera.aspect = canvas.width  /  canvas.height;
            this.camera.updateProjectionMatrix();
            this.camera.projectionMatrix.toArray(this.cameraProj);   

            if(this.depthTexture)
                this.depthTexture.destroy();

            const format = this.enableShadowMap ? 'depth24plus-stencil8' : 'depth32float';

            if(this.device){
                this.depthTexture = this.device.createTexture({ size: [canvas.width, canvas.height], format: format, usage: GPUTextureUsage.RENDER_ATTACHMENT });
                this.depthView = this.depthTexture.createView();
            }
        }
    }


    
    create1PixelTexture(value) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');

        const v = (value * 255);
        const hex =('00' + v.toString(16)).slice(-2);

        ctx.fillStyle = '#'+hex+hex+hex;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const texture = this.device.createTexture({ size: [canvas.width, canvas.height], format: "r8unorm", usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT });
        this.device.queue.copyExternalImageToTexture({ source: canvas }, { texture: texture }, [canvas.width, canvas.height]);
        return texture;
    }

    create1PixelTextureRGBA(color) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');

        const r = parseInt((color.r * 255));
        const g = parseInt((color.g * 255));
        const b = parseInt((color.b * 255));

        const rhex =('00' + r.toString(16)).slice(-2);
        const ghex =('00' + g.toString(16)).slice(-2);
        const bhex =('00' + b.toString(16)).slice(-2);
        

        ctx.fillStyle = '#'+rhex+ghex+bhex;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const texture = this.device.createTexture({ size: [canvas.width, canvas.height], format: "rgba8unorm", usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT });
        this.device.queue.copyExternalImageToTexture({ source: canvas }, { texture: texture }, [canvas.width, canvas.height]);
        return texture;
    }
    
    createGPUTextureFromImge(bitmap, is8bit = false, flip = true) {
        
        if(!bitmap){
            console.log('no bitmap');
            return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');

        ctx.clearRect(0, 0, bitmap.width, bitmap.height);
        ctx.scale(1, flip ? -1 : 1);
        ctx.drawImage(bitmap, 0, flip ? -bitmap.height : 0, bitmap.width, bitmap.height);

        const texture = this.device.createTexture( { size: [ bitmap.width, bitmap.height], 
                                                     format: is8bit?"r8unorm":'rgba8unorm', 
                                                     label: 'image '+bitmap.src,
                                                     usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT 
                                                    }
                                                 );


        this.device.queue.copyExternalImageToTexture({ source: canvas }, { texture: texture }, [bitmap.width, bitmap.height]);
    
        return texture;
    }

            destroyTex(texture){
        texture.destroy();
    }
    createTextureCanvas(canvas, flip = true) {
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const texture = this.device.createTexture( { size: [ canvas.width, canvas.height], 
                                                     format: 'rgba8unorm', 
                                                     usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT 
                                                    }
                                                 );
        texture.canvas = canvas;
        texture.isCanvas = true;

        this.device.queue.copyExternalImageToTexture({ source: texture.canvas }, { texture: texture }, [canvas.width, canvas.height]);
    
        return texture;
    }

    createGPUDataTexture(width, height, data) {
        
        const texture = this.device.createTexture( { size: [ width, height], 
                                                     format: "r8unorm", 
                                                     usage:  GPUTextureUsage.COPY_DST
                                                   }
                                                 );


        this.device.queue.writeTexture({texture}, data, {bytesPerRow : width}, { width: width, height: height });
        return texture;
    }


    writeToTex(pos, data, texture){

        this.device.queue.writeTexture({texture : texture, origin: { x: pos.x, y: pos.y, z: 0 }}, data, {bytesPerRow : this.bumptileW}, { width: this.bumptileW, height: this.bumptileH });
    }


    copyTexToTex(pos, src, dst){

        const copySource = {
            texture: src,
            origin: { x: 0, y: 0, z: 0 },  // Position de départ dans la texture source
        };

        const copyDestination = {
            texture: dst,
            origin: { x: pos.x, y: pos.y, z: 0 },  // Position de départ dans la texture destination
        };

        // 4. Créer un command encoder et un command buffer
        const commandEncoder = this.device.createCommandEncoder();
        commandEncoder.copyTextureToTexture(copySource, copyDestination, { width: src.width, height: src.height, depthOrArrayLayers: 1});
        this.device.queue.submit([ commandEncoder.finish()]);
    }
    
    matFromThree(mat, matType)
    {
        
        const spec = mat.specular ? mat.specular :  mat.specularColor ? mat.specularColor : {r:0, g:0, b:0};
        const emm =  mat.emissive ? {r: mat.emissive.r, g: mat.emissive.g, b: mat.emissive.b}: {r:0, g:0, b:0};

        const material = {  tex: null,
                            nmap: null,
                            rmap: null,
                            emap: null,
                            //bmap: null,
                            driftmap: null,
                            fxmap: null,
                            secmap: null,
                            secnmap: null,
                            name : mat.name,
                            disableShadow: mat.disableShadow ? true: false,
                            bumpScale : mat.bumpScale,
                            clearcoat : mat.clearcoat ? mat.clearcoat : 0.0,
                            clearcoatRoughness : mat.clearcoatRoughness ? mat.clearcoatRoughness: 0.02,
                            clearcoatNormalScale : mat.clearcoatNormalScale ? mat.clearcoatNormalScale: new THREE.Vector2(1,1),
                            reflectivity: mat.reflectivity? mat.reflectivity : 0.0,
                            transparent : mat.transparent ? true : false,
                            alphaTest: mat.alphaTest ? mat.alphaTest : 0.3,
                            diffuse: {r: mat.color.r, g: mat.color.g, b: mat.color.b},
                            emissive:{r: emm.r, g: emm.g, b: emm.b},
                            emissiveIntensity: mat.emissiveIntensity,
                            ambiant: {r: 0.0, g: 0.0, b: 0.0},
                            specular:{r: spec.r, g: spec.g, b: spec.b},
                            roughness:  mat.roughness ? mat.roughness : 0.8,
                            metalness : mat.metalness ? mat.metalness : 0.02,
                            opacity : mat.transmission ? 1.0 - mat.transmission * 0.5 : mat.opacity ? mat.opacity : 1.0
                        };
        /*
        if(mat.name.startsWith('Glass')) {
                material.metalness = 0.8;
                material.reflectivity = 1.0;
                material.roughness = 0.1;
        }else{
            if(matType == RT_SPEC) {
                material.metalness=1.0;
                material.reflectivity = 0.8;
            }

            if(matType == RT_REFR) {
                //material.roughness *= 0.2;
                material.metalness = 0.4;
                material.reflectivity = 0.2;
            }

            if(matType == RT_COAT) {
                material.clearcoat = 0.2;
                material.clearcoatRougness = 0.02;
                material.clearcoatNormalScale = new THREE.Vector2(0.2, 0.2);
                material.metalness= 0.1;
                material.reflectivity = 0.2;
            }
                
            if(matType == RT_CARCOAT) {
                material.clearcoat = 0.8;
                material.clearcoatRougness = 0.02;
                material.clearcoatNormalScale = new THREE.Vector2(0.8, 0.8);
                
                //material.metalness= 0.2;
                //material.roughness = 0.4;
                
                material.reflectivity = 0.6;
                
            }
            
            if(matType == RT_METALCOAT)  {
                material.clearcoat = 0.8;
                material.clearcoatRougness = 0.02;
                material.clearcoatNormalScale = new THREE.Vector2(0.2, 0.2);
                material.metalness= 0.8;
                material.reflectivity = 2.8;
                material.roughness = 0.001;
            }
        }
        */
        return material;
    }

    addUIMesh(meshGroup, matType = RT_DIFF, flip = {x: false,y: false,z: false}) {

        const self = this;
        const mesh = new webGPUMesh();
        const encoder = self.device.createCommandEncoder();
        const tmpB = [];

        //console.log('addUIMesh ', meshGroup);
        
        mesh.transformBufferRange = self.transformAllocator.getBufferRange(transformSize, 'mesh '+meshGroup.name+ ' transform');
        const b1 = self.device.createBuffer({ size:  mesh.transformBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const range1 = b1.getMappedRange();
        const transformBuffer = new Float32Array(range1);
        mesh.writeTransformAttributes(transformBuffer, mesh.transformations[0], mesh.size);
        b1.unmap();
        encoder.copyBufferToBuffer( b1, 0, self.transformAllocator.buffer,  mesh.transformBufferRange.start, b1.size);
        tmpB.push(b1);

        meshGroup.traverse(function (child)  {
            
            if (!child.isMesh) 
                return;

            const m = child;
            const g = m.geometry.index ? m.geometry.toNonIndexed(): m.geometry;
            const nMaterials = m.material.length ? m.material.length : 1;

            if(nMaterials > 1) {


                for (let n = 0; n < nMaterials; n++) {

                    const material = self.matFromThree(m.material[n], matType);

                    const grp = g.groups[n];
                    const primitive = new webGPUUIMeshPrimitive(grp.count / 3, material);
                    mesh.addPrimitive(primitive);
                    primitive.vertexBufferRange = self.vertexAllocator.getBufferRange(grp.count * primitive.vertexLayout.numFloats, 'UI '+child.name);

                    const b = self.device.createBuffer({ size:  primitive.vertexBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
                    const range = b.getMappedRange();
                    const vertexBuffer =  new Float32Array(range);
                    primitive.writeAttributes(vertexBuffer, g, flip, null, grp );

                    b.unmap();
                    encoder.copyBufferToBuffer( b, 0, self.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
                    tmpB.push(b);
                }

            } else {

                const material = self.matFromThree(m.material, matType);

                const primitive = new webGPUUIMeshPrimitive(g.attributes.position.array.length / 9, material);
                mesh.addPrimitive(primitive);
                primitive.vertexBufferRange = self.vertexAllocator.getBufferRange( (g.attributes.position.array.length / 3 ) * primitive.vertexLayout.numFloats, 'UI '+child.name);

                const b = self.device.createBuffer({ size:  (g.attributes.position.array.length / 3) * primitive.vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
                const range = b.getMappedRange();
                const vertexBuffer =  new Float32Array(range);
                primitive.writeAttributes(vertexBuffer, g, flip, null );

                b.unmap();
                encoder.copyBufferToBuffer( b, 0, self.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
                tmpB.push(b);
            }
        });

        this.device.queue.submit([ encoder.finish()]);
        
        for(let t of tmpB) {
            t.destroy();
        }
        
        /* setup uniform buffers & entries */
        for(let n=0;n<mesh.primitives.length;n++){

            if(mesh.primitives[n].uniformLayout) {

                const usize = Math.max(32, mesh.primitives[n].uniformLayout.numFloats);
                
                mesh.primitives[n].uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
                mesh.primitives[n].uniformEntries = [];
                    
                const offset = mesh.primitives[n].uniformBufferRange.start;

                for (const e of mesh.primitives[n].uniformLayout.entries) {

                    /* sum the size of all uniforms at the same location in a structure */
                    let fnd = false;
                    for (const ue of mesh.primitives[n].uniformEntries) {
                        if (ue.binding === e.location) {
                            ue.resource.size += e.getSize(4) * 4;
                            fnd = true;
                            break;
                        }
                    }

                    if(!fnd)
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                }
            }
        }

        //console.log('addUIMesh ' , mesh);
        return mesh;
    }

    addMeshChild(mesh, child, flip = {x: false, y: false, z: false}) {

        const m = mesh.meshGPU;
        //const c = child.meshGPU ? child.meshGPU : child;

        if(!child.meshGPU){

            if(child.isMesh)
                child.meshGPU = this.addMesh(child, RT_DIFF, new Transformation3(child.position, child.quaternion), flip);
            else
                child.meshGPU = this.addMeshGroup(child);
        }
        m.children.push(child.meshGPU);

        return child.meshGPU;
    }

    removeMeshChild(meshGPU, child) {

        const m = meshGPU.meshGPU ? meshGPU.meshGPU : meshGPU;
        const c = child.meshGPU ? child.meshGPU : child;

        let n=0;
        while(n<m.children.length){
            if(m.children[n] === c){
                m.children.splice(n, 1);
                return;
            }
            n++;
        }
    }

    splitBone(splitName, bone, ebone) {
        
        let newBone = null;
        
        for(let i =0; i<bone.children.length; i++) {

            const child = bone.children[i];
            const echild = ebone.children[i];

            if(child.name === splitName){
                  
                newBone =  new SimpleBone(child.name, new THREE.Quaternion(), child.length, child.radius, child.jradius, child.color.clone());
                newBone.copy(child);

                newBone.parentName = bone.name;
                
                newBone.pos = new THREE.Vector3();
             
                return newBone;
            }
            
            newBone = this.splitBone(splitName, child, echild);
            if(newBone != null)
                break;
        }
        return newBone;
    }


    splitBoneMesh(boneName, char){

        const newRootBone = this.splitBone(boneName, char.skeleton.root, char.avatarMesh.meshGPU.rootBone);
        this.resetSkel(newRootBone);

        const ids = this.getIds(newRootBone);
        const geo = char.avatarMesh.geometry;

        const newMesh = char.avatarMesh.clone();
        newMesh.geometry = geo.clone();
        newMesh.children = [];

     
        //const tbone = char.tskel.getBoneByName(newRootBone.name);
        const breakBone = char.avatarMesh.meshGPU.rootBone.findBone(newRootBone.name);

        const p = new THREE.Vector3(breakBone.restTransform.position.x, breakBone.restTransform.position.y, breakBone.restTransform.position.z);
        const q = new THREE.Quaternion(breakBone.restTransform.quaternion.x, breakBone.restTransform.quaternion.y, breakBone.restTransform.quaternion.z, breakBone.restTransform.quaternion.w)

        newMesh.geometry.translate(p);
        newMesh.geometry.applyQuaternion(q);
      
        
        const newIndexes1 = new Uint16Array(geo.index.count);
        const newIndexes2 = new Uint16Array(geo.index.count);

        let nIdx1 = 0;
        let nIdx2 = 0;

        for(let i =0; i<geo.index.count; i+=3){

            let hasBoneVtx = -1;
                       
            for(let v = 0;v<3;v++) {

                const idx = geo.index.array[i + v];

                for(let b = 0;b<4;b++) { 

                    if(ids.indexOf(geo.attributes.skinIndex.array[ idx * 4 + b])>=0){
                        hasBoneVtx= geo.attributes.skinIndex.array[ idx * 4 + b];
                        break;
                    }
                }

                if(hasBoneVtx >= 0)
                    break;
            }

            if(hasBoneVtx >= 0){
        
                for(let v = 0;v<3;v++) {

                    const vidx = geo.index.array[i + v];

                    for(let n=0;n<4;n++){
                        
                        if(newMesh.geometry.attributes.skinWeight.array[vidx * 4 + n] > 0){
                           newMesh.geometry.attributes.skinIndex.array[vidx * 4 + n] = Math.max(0.0, geo.attributes.skinIndex.array[vidx * 4 + n] - newRootBone.idx);
                        }
                    }
                    newIndexes2[nIdx2++] = vidx;
                }
            }else{
                for(let v = 0;v<3;v++) {
                    newIndexes1[nIdx1++] = geo.index.array[i + v];
                }
            }
        }


        geo.setIndex(new THREE.Uint16BufferAttribute(newIndexes1, 1));
        char.avatarMesh.geometry.drawRange.count = nIdx1;



        newMesh.geometry.setIndex(new THREE.Uint16BufferAttribute(newIndexes2, 1));
        newMesh.geometry.drawRange.count = nIdx2;
        newMesh.position.set(0, 0, 0);
        newMesh.quaternion.set(0, 0, 0, 1.0);
        newMesh.updateMatrixWorld(true);

        newMesh.geometry.computeBoundingBox();

        const newGeo = geo.clone().toNonIndexed();
        this.updatePrimitives( char.avatarMesh.meshGPU,  newGeo);

        const newSkeleton = new SimpleSkeleton(new THREE.Vector3(), new THREE.Quaternion());
        newSkeleton.root.children = newRootBone.children;
        newSkeleton.root.idx = newRootBone.idx;
        newSkeleton.root.id = newRootBone.id;
        newMesh.meshGPU = this.addMesh(newMesh, RT_DIFF, new Transformation3(), {x:false, y: false, z: false}, true, {skeleton: null, skale : char.skelScale}, 1); //{skeleton: newSkeleton, skale: char.skelScale}
        newMesh.skeleton = newSkeleton;

        char.splitMesh.push({ skel : newSkeleton, mesh : newMesh});

        return newMesh;
    }


    addMeshInstance(meshGPU, transformation, cloneMat = false) {

        meshGPU= meshGPU.meshGPU ? meshGPU.meshGPU:meshGPU;

        const newMesh = new webGPUMesh();

        newMesh.transformations[0].set(transformation.position, transformation.quaternion);

        if(meshGPU.boneBufferRange)
            newMesh.boneBufferRange = this.boneAllocator.addRef(meshGPU.boneBufferRange);


        const encoder = this.device.createCommandEncoder();
        newMesh.transformBufferRange = this.transformAllocator.getBufferRange(transformSize, 'mesh instance transform');
        const b1 = this.device.createBuffer({ size:  newMesh.transformBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const range1 = b1.getMappedRange();
        const transformBuffer = new Float32Array(range1);
        newMesh.writeTransformAttributes(transformBuffer, newMesh.transformations[0], newMesh.size);
        b1.unmap();
        encoder.copyBufferToBuffer( b1, 0, this.transformAllocator.buffer,  newMesh.transformBufferRange.start, b1.size);
        this.device.queue.submit([ encoder.finish()]);
        b1.destroy();
        
        newMesh.boneEntry = meshGPU.boneEntry;
        newMesh.boneRestEntry = meshGPU.boneRestEntry;
        newMesh.updateBones = false;

        newMesh.boneGroups = meshGPU.boneGroups;
        newMesh.is_static = meshGPU.is_static;

        for(let n=0;n<meshGPU.primitives.length;n++) {

            const newPrimitive = meshGPU.primitives[n].clone(cloneMat);
            newPrimitive.vertexBufferRange = this.vertexAllocator.addRef(meshGPU.primitives[n].vertexBufferRange);
           

            if(newPrimitive.uniformLayout) {

                const usize = newPrimitive.uniformLayout.numFloats;
                    
                newPrimitive.uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
                newPrimitive.uniformEntries = [];
                
                const offset = newPrimitive.uniformBufferRange.start;
    
                for (const e of newPrimitive.uniformLayout.entries) {
    
                    if (e.type == 'tex') {
                        newPrimitive.uniformEntries.push({ binding: e.location, resource: newPrimitive.material.tex.createView() });
                    } else if (e.type == 'normalmap') {
                        newPrimitive.uniformEntries.push({ binding: e.location, resource: newPrimitive.material.nmap.createView() });
                    } else if (e.type == 'roughnessmap') {
                        newPrimitive.uniformEntries.push({ binding: e.location, resource: newPrimitive.material.rmap.createView() });
                    }else if (e.type == 'emissivemap') {
                        newPrimitive.uniformEntries.push({ binding: e.location, resource: newPrimitive.material.emap.createView() });
                    } else {
    
                            /* sum the size of all uniforms at the same location in a structure */
                            let fnd = false;
                            for (const ue of newPrimitive.uniformEntries) {
                                if (ue.binding === e.location) {
                                    ue.resource.size += e.getSize(4) * 4;
                                    fnd = true;
                                    break;
                                }
                            }
    
                            if(!fnd)
                                newPrimitive.uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                    }
                }
            }
            newMesh.addPrimitive(newPrimitive);
        }

        return newMesh;
    }

    addMeshGroup(grp){
        const gpugrp = new webGPUMesh();

        gpugrp.transformations[0].set(grp.position, grp.quaternion);
        gpugrp.children = grp.children;

        return gpugrp;

    }


    updateCameraSpaceMesh(mesh, pos){

        const m = mesh.meshGPU ? mesh.meshGPU : mesh;

        if(!m.transformations)
            return;

        const encoder = this.device.createCommandEncoder();

        const rfov = this.camera.fov * Math.PI / 180.0;
        const fovf = 2 * Math.tan(rfov / 2);

        const offset = new THREE.Vector3(pos.x * fovf * this.camera.aspect + mesh.ox* fovf , pos.y * fovf + mesh.oy* fovf, pos.z).applyQuaternion(this.cameraAnchor.quaternion);
        offset.add(this.cameraAnchor.position);

        m.transformations[0].set(offset, this.cameraAnchor.quaternion);


        mesh.position.set(m.transformations[0].position.x, m.transformations[0].position.y, m.transformations[0].position.z);
        mesh.quaternion.set(m.transformations[0].quaternion.x, m.transformations[0].quaternion.y, m.transformations[0].quaternion.z, m.transformations[0].quaternion.w);


        /*
        m.primitives[0].size.x = mesh.sx;
        m.primitives[0].size.y = mesh.sy;*/



        /*
        mesh.scale.set(mesh.sx * fovf, mesh.sy * fovf, 1.0);
        mesh.position.set(offset.x, offset.y, offset.z);
        mesh.quaternion.set(this.cameraAnchor.quaternion.x, this.cameraAnchor.quaternion.y, this.cameraAnchor.quaternion.z, this.cameraAnchor.quaternion.w);
        */

        /*
        const offset = new THREE.Vector3(pos.x , pos.y , pos.z).applyQuaternion(this.cameraAnchor.quaternion);
        offset.add(this.cameraAnchor.position);
        m.transformations[0].set(offset, this.cameraAnchor.quaternion);
        */
    }

    addCameraSpaceMesh(meshGroup, pos, camSpace = true){

        const self = this;
        const mesh = this.addMesh(meshGroup);

        if(camSpace === true){
            const offset = new THREE.Vector3(pos.x , pos.y , pos.z).applyQuaternion(this.cameraAnchor.quaternion);
            offset.add(this.cameraAnchor.position);
            mesh.transformations[0].set(offset, this.cameraAnchor.quaternion);
        }else{
            mesh.transformations[0].set(pos);
        }
        meshGroup.ox = 0;
        meshGroup.oy = 0;
        meshGroup.sx = 1;
        meshGroup.sy = 1;

        /*
        if(!meshGroup.isMesh)
            return mesh;
       

        const m = meshGroup;
        const g = m.geometry.index ? m.geometry.toNonIndexed(): m.geometry;



        const material = self.matFromThree(m.material, 1);

        const hasTex = (m.material.map !== null) && (m.material.map.image !== null);

        if(hasTex) {
            material.tex = self.createGPUTextureFromImge(m.material.map.image, false, m.material.map.flipY);
        }else{
            material.tex = self.create1PixelTextureRGBA({r:1, g:1, b:1});
        }

        material.disableShadow = true;
            
        const uvScale = {x:1.0,y:1.0};

        const primitive = new webGPUSimpleScreenTexturedPrimitive(g.attributes.position.array.length / 9, material) 

        if((hasTex)&&(primitive.material.texBox)){
            primitive.material.texBox.z = m.material.map.repeat.x;
            primitive.material.texBox.w = m.material.map.repeat.y;
        }

        mesh.addPrimitive(primitive);
            
        primitive.vertexBufferRange = self.vertexAllocator.getBufferRange( (g.attributes.position.array.length / 3 ) * primitive.vertexLayout.numFloats, 'dlg '+m.name);
        const b = self.device.createBuffer({ size:  (g.attributes.position.array.length / 3) * primitive.vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const range = b.getMappedRange();

        const vertexBuffer =  new Float32Array(range);
        primitive.writeAttributes(vertexBuffer, g, {x: false, y: false, z: false}, uvScale );

        b.unmap();
        encoder.copyBufferToBuffer( b, 0, self.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
        this.device.queue.submit([ encoder.finish()]);
        b.destroy();

        mesh.boneBufferRange = this.uniformAllocator.addRef(this.emptyBoneBuffer);
        mesh.boneEntry = { binding: 0, resource: { buffer: this.uniformAllocator.buffer, offset:  mesh.boneBufferRange.start, size: this.bonesFloats * 4 }};

        // setup uniform buffers & entries 
        for(let n=0;n<mesh.primitives.length;n++){

            if(mesh.primitives[n].uniformLayout) {
                
                mesh.primitives[n].uniformBufferRange = this.uniformAllocator.getBufferRange(mesh.primitives[n].uniformLayout.numFloats);
                mesh.primitives[n].uniformEntries = [];
                    
                const offset = mesh.primitives[n].uniformBufferRange.start;

                for (const e of mesh.primitives[n].uniformLayout.entries) {

                    if (e.type == 'tex') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.tex.createView() });
                    } else {

                        // sum the size of all uniforms at the same location in a structure 
                        let fnd = false;
                        for (const ue of mesh.primitives[n].uniformEntries) {
                            if (ue.binding === e.location) {
                                ue.resource.size += e.getSize(4) * 4;
                                fnd = true;
                                break;
                            }
                        }

                        if(!fnd)
                            mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                    }
                }
            }
        }

        */


        return mesh;
    }
    
    updatePrimitives(mesh, g){

        const tmpB =[];
        const encoder = this.device.createCommandEncoder();
    
        for(let n=0;n<mesh.primitives.length;n++){

            const primitive = mesh.primitives[n];

            this.vertexAllocator.unrefRange(primitive.vertexBufferRange);

            primitive.vertexBufferRange = this.vertexAllocator.getBufferRange( g.groups[n].count * primitive.vertexLayout.numFloats, 'xmesh - '+primitive.material.name);

            const b = this.device.createBuffer({ size:  primitive.vertexBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
            const range = b.getMappedRange();
            const vertexBuffer =  new Float32Array(range);
            primitive.writeAttributes(vertexBuffer, g, {x: false, y: false, z:false }, {x:1, y:1}, g.groups[n] );
    
            if(primitive.hasSkinning){
                const ivertexBuffer =  new Uint8Array(range);
                primitive.writeSkinnedAttributes(ivertexBuffer, g, {x: false, y: false, z:false },  g.groups[n], mesh.bones);
            }

            primitive.nTriangles = g.groups[n].count / 3;
    
            b.unmap();
            encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
            tmpB.push(b);
        }
        
        this.device.queue.submit([ encoder.finish()]);
        
        for(let t of tmpB) {
            t.destroy();
        }
    }

    
    addWorldUIMesh(pos, g, color){
        const encoder = this.device.createCommandEncoder();
        const mesh = new webGPUWorldUIMesh(pos);
        const tmpB = [];

        const geo= g.clone().toNonIndexed();
        const nTriangles = geo.attributes.position.count / 3;

        const primitive = new webGPUWorldUIPrimitive(nTriangles, {diffuse : color, opacity : 1.0});
        mesh.addPrimitive(primitive);

        mesh.transformBufferRange = this.transformAllocator.getBufferRange(transformSize * mesh.nInstances, 'world ui mesh  transform');
        const b1 = this.device.createBuffer({ size:  mesh.transformBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const transformBuffer = new Float32Array( b1.getMappedRange());
        mesh.writeTransformAttributes(transformBuffer, mesh.transformations[0], mesh.size);
        b1.unmap();
        encoder.copyBufferToBuffer( b1, 0, this.transformAllocator.buffer,  mesh.transformBufferRange.start, b1.size);
        tmpB.push(b1);

        primitive.vertexBufferRange = this.vertexAllocator.getBufferRange(nTriangles * 3 * primitive.vertexLayout.numFloats, 'world ui mesh  ');

        const b = this.device.createBuffer({ size:  primitive.vertexBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const range = b.getMappedRange();

        const vertexBuffer =  new Float32Array(range);
        primitive.writeAttributes(vertexBuffer, geo, {x:false, y:false, z:false} );
        b.unmap();
        encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
        tmpB.push(b);

        mesh.rootBone.numBones = 1;

        this.device.queue.submit([ encoder.finish()]);
        
        for(let t of tmpB) {
            t.destroy();
        }

        /* setup uniform buffers & entries */
        for(let n=0;n<mesh.primitives.length;n++){

            if(mesh.primitives[n].uniformLayout) {

                const usize = Math.max(32, mesh.primitives[n].uniformLayout.numFloats);
                
                mesh.primitives[n].uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
                mesh.primitives[n].uniformEntries = [];
                    
                const offset = mesh.primitives[n].uniformBufferRange.start;

                for (const e of mesh.primitives[n].uniformLayout.entries) {

                    /* sum the size of all uniforms at the same location in a structure */
                    let fnd = false;
                    for (const ue of mesh.primitives[n].uniformEntries) {
                        if (ue.binding === e.location) {
                            ue.resource.size += e.getSize(4) * 4;
                            fnd = true;
                            break;
                        }
                    }

                    if(!fnd)
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                }
            }
        }   

        return mesh;
    }


    addMesh(meshGroup, matType = RT_DIFF, transformation = new Transformation3(), flip = {x: false,y: false,z: false}, useTex = true, skelinfos = null, nInstances= 1) {

        const self = this;
        const skeleton = skelinfos ? skelinfos.skeleton : null;
        const skelscale= skelinfos ? skelinfos.skale : 1.0;
        const nInst = Math.max(1, nInstances);
        const mesh = new webGPUMesh(nInst);
        const encoder = this.device.createCommandEncoder();
        const tmpB = [];

        //console.log('addMesh ', meshGroup);

        mesh.transformations[0].set(transformation.position, transformation.quaternion);
        mesh.size =skelscale;   

        mesh.transformBufferRange = this.transformAllocator.getBufferRange(transformSize * nInst, 'mesh '+meshGroup.name+ 'transform');
        const b1 = this.device.createBuffer({ size:  mesh.transformBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const transformBuffer = new Float32Array( b1.getMappedRange());
        for(let n=0;n<nInst;n++){
            mesh.writeTransformAttributes(transformBuffer, mesh.transformations[n], mesh.size, n * nInst);
        }
        b1.unmap();
        encoder.copyBufferToBuffer( b1, 0, this.transformAllocator.buffer,  mesh.transformBufferRange.start, b1.size);
        tmpB.push(b1);

        const hasSkinning =  skeleton ? true: false;


        function parseMesh(child)  {
            
            if (!child.isMesh) 
                return;

            /*
            if(child.name.length === 0){
                console.log('empty name');
            }
            */

            const m = child;
            const g = m.geometry.index ? m.geometry.toNonIndexed(): m.geometry;
            //g.scale(skelscale,skelscale,skelscale);
            const nMaterials = m.material.length ? m.material.length : 1;

            /*
            const mat = new THREE.Matrix4().compose(child.position, child.quaternion, child.scale);
            g.applyMatrix4(mat);
            */

            if(nMaterials > 1) {

                if(child.isSkinnedMesh) {

                    for (let n = 1; n < child.skeleton.bones.length; n++) {
                        const t = new Transformation3(child.skeleton.bones[n].position);
                        mesh.addBone(child.skeleton.bones[n].name, t, nInst);
                    }
                }

                for (let n = 0; n < nMaterials; n++) {

                    if( (m.material[n].name.startsWith('center'))||
                        (m.material[n].name.startsWith('bone'))||
                        (m.material[n].name.startsWith('ccube'))||
                        (m.material[n].name.startsWith('Anchor')))
                        continue;
                        

                    const material = self.matFromThree(m.material[n], matType);

                    const isToon = m.material[n].name.startsWith('Toon') ? true: false;
                    const hasTex = useTex && (m.material[n].map !== null) && (m.material[n].map.image !== null);
                    const hasEmm = useTex && (m.material[n].emissiveMap !== null)&& (m.material[n].emissiveMap !== undefined) && (m.material[n].emissiveMap.image !== null);
                    const hasNormalMap = hasTex && (m.material[n].normalMap && m.material[n].normalMap.image);
                    const hasColor = m.geometry.attributes.color !== undefined ? true : false;

                    const hasDriftMap =m.material[n].driftMap ? true : false;
                    const hasFxMap =m.material[n].fxMap ? true : false;
                    const hasSecMap =m.material[n].secMap ? true : false;
                    const hasSecNMap =m.material[n].normalMap2 ? true : false;

                    if(hasColor){
                        console.log('hasColor '+child.name);
                    }

                    if(isToon){
                        material.tex = self.create1PixelTextureRGBA(material.diffuse); //self.createGPUTextureFromImge(m.material[n].toonMap.image);
                        material.reflectivity = 0.0;
                    }
                    else if(hasTex) {
                        material.tex = (m.material[n].map.isCanvas !== true ) ? self.createGPUTextureFromImge(m.material[n].map.image, false, m.material[n].map.flipY) : m.material[n].map;
                    }

                    material.disableShadow = child.castShadow ? false:  true;
                        
                    
                    const uvScale = /*hasTex? m.material[n].map.repeat :*/ {x:1.0,y:1.0};

                    if(hasNormalMap) {

                        if(m.material[n].normalMap)
                            material.nmap = self.createGPUTextureFromImge(m.material[n].normalMap.image, false, m.material[n].map.flipY);
                        
                        /*
                        if(m.material[n].bumpMap)
                            material.bmap = self.createGPUDataTexture(self.bumpTexWidth, self.bumpTexHeight, self.roadMapArray);
                        */
                    }

                    if(( m.material[n].roughnessMap)&&(m.material[n].roughnessMap.image)) 
                        material.rmap = self.createGPUTextureFromImge(m.material[n].roughnessMap.image, true, m.material[n].roughnessMap.flipY);
                    else 
                        material.rmap = self.create1PixelTexture(m.material[n].roughness);


                    if(hasSecMap) {
                        material.secmap = self.createGPUTextureFromImge(m.material[n].secMap.image, false, m.material[n].map.flipY);
                    }

                    if(hasSecNMap) {
                        material.secnmap = self.createGPUTextureFromImge(m.material[n].normalMap2.image, false, m.material[n].map.flipY);
                    }

                    if(hasDriftMap) {
                        material.driftmap = m.material[n].driftMap;
                    }

                    if( m.material[n].emissiveMap) {

                        if(m.material[n].emissiveMap.image)
                            material.emap = self.createGPUTextureFromImge(m.material[n].emissiveMap.image, false, m.material[n].emissiveMap.flipY);
                        else
                            material.emap = m.material[n].emissiveMap;

                        if(!hasTex){
                            material.tex = self.create1PixelTextureRGBA(material.diffuse);
                        }
                    }else if (hasTex){
                        material.emap = self.create1PixelTextureRGBA(material.emissive);
                    }
                    const grp = g.groups[n];
                    const primitive = isToon ? new webGPUToonPrimitive(grp.count / 3, material, hasSkinning) :  hasNormalMap ? new webGPUPixelLightingPrimitive(grp.count / 3, material, hasColor) : (hasTex || hasEmm) ? m.material[n].isMeshBasicMaterial  ?new webGPUSimpleTexturedPrimitive(grp.count / 3, material) : new webGPUTexturedPrimitive(grp.count / 3, material, hasSkinning) : new webGPUSimplePrimitive(grp.count / 3, material, hasSkinning);
                    
                    if((hasTex || hasEmm)&&(primitive.material.texBox) && ((m.material[n].map == null ) || (m.material[n].map.isCanvas !== true ))){

                        if(m.material[n].map){
                            primitive.material.texBox.z = m.material[n].map.repeat.x;
                            primitive.material.texBox.w = m.material[n].map.repeat.y;
                        }else{
                            primitive.material.texBox.z = m.material[n].emissiveMap.repeat ? m.material[n].emissiveMap.repeat.x : 1.0;
                            primitive.material.texBox.w = m.material[n].emissiveMap.repeat ? m.material[n].emissiveMap.repeat.y : 1.0;
                        }
                    }

                    mesh.addPrimitive(primitive);

                    primitive.vertexBufferRange = self.vertexAllocator.getBufferRange(grp.count * primitive.vertexLayout.numFloats, 'mesh '+child.name+ ' - '+m.material[n].name);

                    const b = self.device.createBuffer({ size:  primitive.vertexBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
                    const range = b.getMappedRange();

                    const vertexBuffer =  new Float32Array(range);

                    primitive.writeAttributes(vertexBuffer, g, flip, uvScale, grp );

                    if(hasSkinning){
                        const ivertexBuffer =  new Uint8Array(range);
                        primitive.writeSkinnedAttributes(ivertexBuffer, g, flip, grp, mesh.bones);
                    }

                    b.unmap();
                    encoder.copyBufferToBuffer( b, 0, self.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
                    tmpB.push(b);
                }
            } else {

                const material = self.matFromThree(m.material, matType);

                const isToon = m.material.name.startsWith('Toon') ? true : false;
                const hasTex = useTex && (m.material.map !== null) && (m.material.map.image !== null);
                const hasEmm = useTex && (m.material.emissiveMap !== null) && (m.material.emissiveMap !== undefined) && (m.material.emissiveMap.image !== null);
                const hasNormalMap = hasTex && (m.material.normalMap && m.material.normalMap.image);
                 const hasColor = m.geometry.attributes.color !== undefined ? true : false;

                const hasDriftMap =m.material.driftMap ? true : false;
                const hasFxMap =m.material.fxMap ? true : false;
                const hasSecMap =m.material.secMap ? true : false;
                const hasSecNMap =m.material.secNMap ? true : false;

                if(isToon){
                    material.reflectivity = 0.0;
                    
                    if((m.material.toonMap)&&(m.material.toonMap.image))
                        material.tex = self.createGPUTextureFromImge(m.material.toonMap.image);
                    else if(hasTex) 
                        material.tex = self.createGPUTextureFromImge(m.material.map.image, false, m.material.map.flipY);
                    else
                        material.tex = self.create1PixelTextureRGBA(m.material.color);
                }
                else if(hasTex) 
                        material.tex = self.createGPUTextureFromImge(m.material.map.image, false, m.material.map.flipY);

                if(( m.material.emissiveMap)&&(m.material.emissiveMap.image)) {
                    material.emap = self.createGPUTextureFromImge(m.material.emissiveMap.image, false, m.material.emissiveMap.flipY);

                    if(!hasTex){
                        material.tex = self.create1PixelTextureRGBA(material.diffuse);
                    }
                }else if (hasTex){
                    material.emap = self.create1PixelTextureRGBA(material.emissive);
                }

                material.disableShadow = child.castShadow ? false:  true;
                
                const uvScale =  /*hasTex ? m.material.map.repeat :*/ {x:1.0,y:1.0};

                if(hasNormalMap) {
                    
                    if(m.material.normalMap)
                        material.nmap = self.createGPUTextureFromImge(m.material.normalMap.image, false, m.material.map.flipY);
                    
                    /*
                    if(m.material.bumpMap)
                        material.bmap = self.createGPUDataTexture(self.bumpTexWidth, self.bumpTexHeight, self.roadMapArray);
                    */
                }

                if(( m.material.roughnessMap)&&(m.material.roughnessMap.image))
                    material.rmap = self.createGPUTextureFromImge(m.material.roughnessMap.image, true, m.material.roughnessMap.flipY);
                else 
                    material.rmap = self.create1PixelTexture(m.material.roughness);

                

              

                if(hasDriftMap) {
                    material.driftmap = m.material.driftMap;

                    if(hasSecMap) 
                       material.secmap = self.createGPUTextureFromImge(m.material.secMap.image, false, m.material.map.flipY);
                    else
                       material.secmap = self.create1PixelTextureRGBA({r: 255, g:255, b:255});

                    if(hasSecNMap) 
                        material.secnmap = self.createGPUTextureFromImge(m.material.secNormalMap.image, false, m.material.map.flipY);
                    else
                        material.secnmap = self.create1PixelTextureRGBA({r: 0, g:0, b:0});

                }else if(hasSecMap) {
                    material.secmap = self.createGPUTextureFromImge(m.material.secMap.image, false, m.material.map.flipY);

                    if(hasSecNMap) 
                        material.secnmap = self.createGPUTextureFromImge(m.material.secNormalMap.image, false, m.material.map.flipY);
                }

                if( m.material.emissiveMap) {

                    if(m.material.emissiveMap.image)
                        material.emap = self.createGPUTextureFromImge(m.material.emissiveMap.image, false, m.material.emissiveMap.flipY);
                    else
                        material.emap = m.material[n].emissiveMap;

                    if(!hasTex){
                        material.tex = self.create1PixelTextureRGBA(material.diffuse);
                    }
                }else if (hasTex){
                    material.emap = self.create1PixelTextureRGBA(material.emissive);
                }

                const primitive = isToon ? new webGPUToonPrimitive(g.attributes.position.array.length / 9, material, hasSkinning) : 
                                    hasNormalMap ? new webGPUPixelLightingPrimitive(g.attributes.position.array.length / 9, material, hasColor) : 
                                        (hasTex || hasEmm) ? 
                                            m.material.isMeshBasicMaterial ? 
                                                new webGPUSimpleTexturedPrimitive(g.attributes.position.array.length / 9, material) : 
                                                    new webGPUTexturedPrimitive(g.attributes.position.array.length / 9, material, hasSkinning) : 
                                                        new webGPUSimplePrimitive(g.attributes.position.array.length / 9, material, hasSkinning) ;


                                                        
                if((hasTex || hasEmm)&&(primitive.material.texBox)){
                    if(m.material.map){
                        primitive.material.texBox.z = m.material.map.repeat ? m.material.map.repeat.x : 1.0;
                        primitive.material.texBox.w = m.material.map.repeat ? m.material.map.repeat.y : 1.0;
                    }else{
                        primitive.material.texBox.z = m.material.emissiveMap.repeat ? m.material.emissiveMap.repeat.x : 1.0;
                        primitive.material.texBox.w = m.material.emissiveMap.repeat ? m.material.emissiveMap.repeat.y : 1.0;
                    }
                }

                mesh.addPrimitive(primitive);
                
                primitive.vertexBufferRange = self.vertexAllocator.getBufferRange( (g.attributes.position.array.length / 3 ) * primitive.vertexLayout.numFloats, 'mesh '+child.name+ ' - '+m.material.name);

                const b = self.device.createBuffer({ size:  (g.attributes.position.array.length / 3) * primitive.vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
                const range = b.getMappedRange();

                const vertexBuffer =  new Float32Array(range);

                
                primitive.writeAttributes(vertexBuffer, g, flip, uvScale );

                if(hasSkinning){
                    const ivertexBuffer =  new Uint8Array(range);
                    primitive.writeSkinnedAttributes(ivertexBuffer, g, flip);
                }
                                
                b.unmap();
                encoder.copyBufferToBuffer( b, 0, self.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
                tmpB.push(b);
            }
        }

        if(meshGroup.children.length>0)
            meshGroup.traverse(parseMesh);
        else
            parseMesh(meshGroup);
      
        if(hasSkinning){
            mesh.bindSkeleton(skeleton, null, 1.0);
            mesh.boneRestBufferRange = this.uniformAllocator.getBufferRange(this.bonesFloats);

            const b2 = this.device.createBuffer({ size:  mesh.boneRestBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
            const boneRestBuffer = new Float32Array( b2.getMappedRange());
            mesh.writeBonesRest(boneRestBuffer);
            b2.unmap();
            encoder.copyBufferToBuffer( b2, 0, this.uniformAllocator.buffer,  mesh.boneRestBufferRange.start, b2.size);
            tmpB.push(b2);

            mesh.rootBone.numBones = mesh.rootBone.getBoneCnt()

            const bone_cnt = mesh.rootBone.numBones;
            mesh.boneBufferRange = this.boneAllocator.getBufferRange(bone_cnt * boneSize * nInst);
            mesh.boneRestEntry = { binding: 0, resource: { buffer: this.uniformAllocator.buffer, offset:  mesh.boneRestBufferRange.start, size: this.bonesFloats * 4 }};
            mesh.boneEntry = { binding: 1, resource: { buffer: this.boneAllocator.buffer, offset: mesh.boneBufferRange.start, size: bone_cnt * boneSize * nInst * 4  }};
        }else
            mesh.rootBone.numBones = 1;

        this.device.queue.submit([ encoder.finish()]);
        
        for(let t of tmpB) {
            t.destroy();
        }


        /* setup uniform buffers & entries */
        for(let n=0;n<mesh.primitives.length;n++){

            if(mesh.primitives[n].uniformLayout) {

                const usize = Math.max(32, mesh.primitives[n].uniformLayout.numFloats);
                
                mesh.primitives[n].uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
                mesh.primitives[n].uniformEntries = [];
                    
                const offset = mesh.primitives[n].uniformBufferRange.start;

                for (const e of mesh.primitives[n].uniformLayout.entries) {

                    if (e.type == 'tex') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.tex.createView() });
                    } else if (e.type == 'normalmap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.nmap.createView() });
                    } else if (e.type == 'roughnessmap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.rmap.createView() });
                    }else if (e.type == 'emissivemap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.emap.createView() });
                    }/*else if (e.type == 'bumpmap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.bmap.createView() });                        
                    }*/else if (e.type == 'secmap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.secmap.createView() });                        
                    }else if (e.type == 'secnmap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.secnmap.createView() });                        
                    }else if (e.type == 'driftmap') {
                        mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: mesh.primitives[n].material.driftmap.createView() });                        
                    } else {

                        /* sum the size of all uniforms at the same location in a structure */
                        let fnd = false;
                        for (const ue of mesh.primitives[n].uniformEntries) {
                            if (ue.binding === e.location) {
                                ue.resource.size += e.getSize(4) * 4;
                                fnd = true;
                                break;
                            }
                        }

                        if(!fnd)
                            mesh.primitives[n].uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                    }
                }
            }
        }

        //console.log('addmesh ' , mesh);
        return mesh;
    }
    
    addUIButtons(controller){

        if(controller.cam)
            controller.cam.spriteGPU = this.addSprite(controller.cam);

        if(controller.wheel)
            controller.wheel.spriteGPU = this.addSprite(controller.wheel);

        if(controller.nitro)
            controller.nitro.spriteGPU = this.addSprite(controller.nitro);

        if(controller.left)
            controller.left.spriteGPU = this.addSprite(controller.left);

        if(controller.right)
            controller.right.spriteGPU = this.addSprite(controller.right);
    
        controller.accel.spriteGPU = this.addSprite(controller.accel);
        controller.brake.spriteGPU = this.addSprite(controller.brake);
    }

    addRpmController(c){
        c.rpm1.spriteGPU = this.addSprite(c.rpm1);
        c.rpm2.spriteGPU = this.addSprite(c.rpm2);
    }

    addNitroController(c){
        c.nitro1.spriteGPU = this.addSprite(c.nitro1);
        c.nitro2.spriteGPU = this.addSprite(c.nitro2);
        c.nitro3.spriteGPU = this.addSprite(c.nitro3);
    }
    addDamageController(c) {
        c.dmg1.spriteGPU = this.addSprite(c.dmg1);
        c.dmg2.spriteGPU = this.addSprite(c.dmg2);
    }

    addLoadingController(c) {
        c.loading1.spriteGPU = this.addSprite(c.loading1);
        c.loading2.spriteGPU = this.addSprite(c.loading2);
        c.txt.meshGPU = this.addUIMesh(c.txt);
    }

    addDigitFont(){

        this.digits[0].spriteGPU = this.addSprite(this.digits[0]);
        this.digits[1].spriteGPU = this.addSprite(this.digits[1]);
        this.digits[2].spriteGPU = this.addSprite(this.digits[2]);
    }

    addSprite(sprite) {

        const encoder = this.device.createCommandEncoder();

        //console.log('addSprite ', sprite);

        const hasTex = (sprite.material.map !== null) && (sprite.material.map.image !== null);
        const tex = hasTex ? this.createGPUTextureFromImge(sprite.material.map.image): this.create1PixelTextureRGBA(sprite.material.color);
        const spriteGPU = new webGPUSprite(tex);

        spriteGPU.primitive.vertexBufferRange = this.vertexAllocator.addRef(this.UISpriteVertexBufferRange);
        
        if(spriteGPU.primitive.uniformLayout) {

            const usize = Math.max(32, spriteGPU.primitive.uniformLayout.numFloats);
               
            spriteGPU.primitive.uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
            spriteGPU.primitive.uniformEntries = [];
                    
            const offset = spriteGPU.primitive.uniformBufferRange.start;

            for (const e of spriteGPU.primitive.uniformLayout.entries) {

                if (e.type == 'tex') {
                    spriteGPU.primitive.uniformEntries.push({ binding: e.location, resource: spriteGPU.primitive.tex.createView() });
                } else {
                    /* sum the size of all uniforms at the same location in a structure */
                    let fnd = false;
                    for (const ue of spriteGPU.primitive.uniformEntries) {
                        if (ue.binding === e.location) {
                            ue.resource.size += e.getSize(4) * 4;
                            fnd = true;
                            break;
                        }
                    }

                   if(!fnd)
                        spriteGPU.primitive.uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                }
            }
        }

        //console.log('addSprite ' , spriteGPU);
        return spriteGPU;
    }

    
    setEmission(mesh, name, r, g, b, i = null){ 

        if((!mesh.meshGPU)&&(!mesh.parent.meshGPU))
            return null;
        
        const m = mesh.meshGPU ? mesh.meshGPU : mesh.parent.meshGPU;


        for(let n=0; n<m.primitives.length;n++ ){
            if(m.primitives[n].material.name === name){

                if(r !== null)
                    m.primitives[n].material.emissive.r = r;
                
                if(g !== null)
                    m.primitives[n].material.emissive.g = g;

                if(b !== null)
                    m.primitives[n].material.emissive.b = b;

                if(b !== null)
                    m.primitives[n].material.emissiveIntensity = i;

                m.primitives[n].needsUpdate = true;
            }
        }
    }

    getColor(mesh, name){ 
        
        if((!mesh.meshGPU)&&(!mesh.parent.meshGPU))
            return null;
        
        const m = mesh.meshGPU ? mesh.meshGPU : mesh.parent.meshGPU;

        for(let n=0; n<m.primitives.length;n++ ){
            if(m.primitives[n].material.name === name){
                return m.primitives[n].material.diffuse;
            }
        }

        return null;
    }

    setTex(mesh, name, tex){ 

        if((!mesh.meshGPU)&&((mesh.parent === null) || (!mesh.parent.meshGPU)))
            return null;
        
        const m = mesh.meshGPU ? mesh.meshGPU : mesh.parent.meshGPU;

        for(let n=0; n<m.primitives.length;n++ ){

            const primitive = m.primitives[n];
            if(primitive.material.name === name){

                if(tex.image.complete){
                    primitive.material.tex =  this.createGPUTextureFromImge(tex.image, false, tex.flipY);
                    primitive.uniformEntries[1].resource = primitive.material.tex.createView();
                    primitive.uniformGroups = [];
                    primitive.needsUpdate = true;
                }else{
                    const self = this;
                    tex.image.onload = function(){
                        primitive.material.tex =  self.createGPUTextureFromImge(tex.image, false, tex.flipY);
                        primitive.uniformEntries[1].resource = primitive.material.tex.createView();
                        primitive.uniformGroups = [];
                        primitive.needsUpdate = true;
                    }
                }
                /*
                for (const e of primitive.uniformLayout.entries) {
                    if (e.type == 'tex') {
                        primitive.uniformEntries.push({ binding: e.location, resource: primitive.material.tex.createView() });
                    }
                }
                */
                
            }
        }

    }
    updateTex(mesh, name, canvas = null){ 

        if(!mesh.meshGPU)
          return;  
        
        const m = mesh.meshGPU;

        for(let n=0; n<m.primitives.length;n++ ){
            if(m.primitives[n].material.name === name){

                const tex = m.primitives[n].material.tex;

                if(tex&&((tex.canvas)||canvas)){

                    const c =tex.canvas ? tex.canvas : canvas;
                    this.device.queue.copyExternalImageToTexture({ source:  c}, { texture: tex }, [c.width, c.height]);
                }
            }
        }
    }

    updateETex(mesh, name, canvas = null){ 
        if(!mesh.meshGPU)
            return;  
            
        const m = mesh.meshGPU;

        for(let n=0; n<m.primitives.length;n++ ){
            if(m.primitives[n].material.name === name){

                const tex = m.primitives[n].material.emap;

                if(tex&&((tex.canvas)||canvas)){

                    const c =tex.canvas ? tex.canvas : canvas;
                    this.device.queue.copyExternalImageToTexture({ source:  c}, { texture: tex }, [c.width, c.height]);
                }
            }
        }
    }


    setColor(mesh, name, r, g, b){ 


        if((!mesh.meshGPU)&&((mesh.parent === null) || (!mesh.parent.meshGPU)))
            return null;
        
        const m = mesh.meshGPU ? mesh.meshGPU : mesh.parent.meshGPU;


        for(let n=0; n<m.primitives.length;n++ ){
            if(m.primitives[n].material.name === name){

                if(r !== null)
                    m.primitives[n].material.diffuse.r = r;
                
                if(g !== null)
                    m.primitives[n].material.diffuse.g = g;

                if(b !== null)
                    m.primitives[n].material.diffuse.b = b;

                m.primitives[n].needsUpdate = true;
            }
        }
    }

    setTextureOffset(mesh, name, offsetx = null, offsety = null, sizex = null, sizey = null){


        if((!mesh.meshGPU)&&(!mesh.parent.meshGPU))
            return null;
        
        const m = mesh.meshGPU ? mesh.meshGPU : mesh.parent.meshGPU;


        for(let n=0; n<m.primitives.length;n++ ){
            if((name === null) || (m.primitives[n].material.name.startsWith(name))){

                if(offsetx !== null)
                    m.primitives[n].material.texBox.x = offsetx;
                
                if(offsety !== null)
                    m.primitives[n].material.texBox.y = offsety;

                if(sizex !== null)
                    m.primitives[n].material.texBox.z = sizex;
                
                if(sizey !== null)
                    m.primitives[n].material.texBox.w = sizey;

                m.primitives[n].needsUpdate = true;
            }
        }
     
    }
    updateSprite(sprite) {

        const spriteGPU = sprite.spriteGPU;
        if(!spriteGPU)
            return;

        spriteGPU.primitive.rotation = sprite.material.rotation;
        spriteGPU.primitive.scale.x = sprite.scale.x;
        spriteGPU.primitive.scale.y = sprite.scale.y;
        
        spriteGPU.primitive.pos.x = sprite.position.x;
        spriteGPU.primitive.pos.y = sprite.position.y;

        if(sprite.material.map)
        {
            spriteGPU.primitive.texScale.x = sprite.material.map.repeat.x;
            spriteGPU.primitive.texScale.y = sprite.material.map.repeat.y;
    
            spriteGPU.primitive.texPos.x = sprite.material.map.offset.x;
            spriteGPU.primitive.texPos.y = sprite.material.map.offset.y;

            if(spriteGPU.currentMap != sprite.material.map.image.src) {
                spriteGPU.primitive.tex =  this.createGPUTextureFromImge(sprite.material.map.image);

                for (const e of spriteGPU.primitive.uniformLayout.entries) {

                    if (e.type == 'tex') {
                        spriteGPU.primitive.uniformEntries[1] = { binding: e.location, resource: spriteGPU.primitive.tex.createView() };
                    }
                }
                spriteGPU.primitive.uniformGroups = [];
            }
            spriteGPU.currentMap = sprite.material.map.image.src;
        }

        spriteGPU.primitive.needsUpdate = true;

    }

    setUIPos(m, x, y){

        if(!m.meshGPU)
            return;
        if(m.meshGPU.primitives.length <1)
            return;

        m.meshGPU.primitives[0].pos.x = x;
        m.meshGPU.primitives[0].pos.y = y;

        m.meshGPU.primitives[0].needsUpdate = true;
    }

    enableCameraAnchor(on){

        if(on)
            this.useCameraAnchor = true;
        else
            this.useCameraAnchor = false;
    }

    render(dt) {

        const encoder = this.device.createCommandEncoder();
        const copyEncoder = this.device.createCommandEncoder();

        this.uniformAllocator.compact(copyEncoder);
        this.vertexAllocator.compact(copyEncoder);
        
        this.uniformAllocator.initializeStagingBuffer(this.uniformAllocator.size / 8, copyEncoder);
        //this.transformAllocator.initializeStagingBuffer(600 * 1024, copyEncoder);

        
        this.transformStagging = this.device.createBuffer({ size: this.transformAllocator.size,  usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.MAP_WRITE, mappedAtCreation: true});
        this.transformBuffer = new Float32Array(this.transformStagging.getMappedRange());
        

        this.bonesStagging = this.device.createBuffer({ size: this.boneAllocator.size,  usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.MAP_WRITE, mappedAtCreation: true});
        this.boneBuffer = new Float32Array(this.bonesStagging.getMappedRange());

        if(this.useCameraAnchor){
            this.camera.position.set(this.cameraAnchor.position.x, this.cameraAnchor.position.y, this.cameraAnchor.position.z);
            this.camera.quaternion.set(this.cameraAnchor.quaternion.x, this.cameraAnchor.quaternion.y, this.cameraAnchor.quaternion.z, this.cameraAnchor.quaternion.w);
        }

        this.camera.updateMatrix();
        this.camera.updateProjectionMatrix();

        this.cameraUI.updateMatrix();
        this.cameraUI.updateProjectionMatrix();

        this.cameraTransform.set(this.camera.position, this.camera.quaternion);
        this.cameraTransform.getInverse(this.viewTransform);

        for(let n=0;n<this.particles.length;n++) {
            this.particles[n].particles.update(dt, this.cameraTransform);

             if ((! this.particles[n].particles.emitterAlive) && ( this.particles[n].particles.pinned !== true) && ( this.particles[n].particles.aliveParticles < 1)) {
                this.removeMesh(this.particles[n]);
                this.particles.splice(n, 1);
            } else
                n++

            //this.updateParticle(p, dt, copyEncoder);
        }
        
        
        if(this.camLight){
            this.shadowTransform.getInverse(this.viewTransform);
            this.shadowProjM.toArray(this.cameraProj);
        }
        
        this.writeGlobalUniforms();
        this.writeGlobalUIUniforms();

        if(this.renderMainScene) {

            if(this.enableShadowMap){

                if(this.renderPass['shadow']) {
                    const shadowPassEncoder = encoder.beginRenderPass({
                        colorAttachments: [],
                        depthStencilAttachment: {
                        view: this.shadowDepthTextureView,
                        depthClearValue: 1.0,
                        depthLoadOp: 'clear',
                        depthStoreOp: 'store',
                        },
                    })

                    shadowPassEncoder.label = 'shadow pass';
        
                    this.currentPassEncoder = shadowPassEncoder;
                    this.transparentObjects = null;
                    this.opacityThresold    = 0.6;
                    this.renderPass['shadow'].drawFunc('shadow');
                    shadowPassEncoder.end();
                }
            }

            

            const passDef = {
                colorAttachments: [{
                view: this.ctx.getCurrentTexture().createView(),
                clearValue: { r: 0.5, g: 0.5, b: 0.5, a: 1.0 },
                loadOp: 'clear',
                storeOp: 'store'
                }],
                depthStencilAttachment: {
                view: this.depthTexture.createView(),
                depthClearValue: 1.0,
                depthLoadOp: 'clear',
                depthStoreOp: 'store'
                }
            }

            if(this.enableShadowMap){
                passDef.depthStencilAttachment.stencilClearValue = 0;
                passDef.depthStencilAttachment.stencilLoadOp = 'clear';
                passDef.depthStencilAttachment.stencilStoreOp = 'store';
            }

            const passEncoder = encoder.beginRenderPass(passDef);

            passEncoder.label = 'main pass';

            this.currentPassEncoder = passEncoder;
            this.transparentObjects = [];
            this.cameraObjects = [];
            this.opacityThresold = 0.0;
            
            if(this.skybox)
                this.drawMesh(this.skybox, null);

            if(this.renderPass['render']) {

                this.renderPass['render'].drawFunc(null);

                const vt = this.viewTransform;

                this.transparentObjects.sort(function(a,b){

                    const v1 =vt.applyV(a.t.position);
                    const v2 =vt.applyV(b.t.position);

                    if(v1.z>v2.z)
                        return -1;
                    else if(v1.z==v2.z)
                        return 0;
                    else
                        return 1;
                });

                let o;
                while ((o = this.transparentObjects.pop())) {

                    const primitive = o.p;
                    const pipeline = primitive.pipeline;
                    const tr = o.tr;

                    const pl = this.getPipeLine(pipeline, primitive);
                    const p = pl.p;
                    passEncoder.setPipeline(p);
                    passEncoder.setBindGroup(0, this.globalGroup[pipeline]);
                    
                    if(primitive.uniformLayout ) 
                        passEncoder.setBindGroup(1, primitive.uniformGroups[pipeline]);

                    if(pl.isSkinned) 
                        passEncoder.setBindGroup(2,  o.g[pipeline]);

                    passEncoder.setVertexBuffer(0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, primitive.vertexBufferRange.size);

                    if(tr)
                        passEncoder.setVertexBuffer(1, this.transformAllocator.buffer, tr.start, transformSize  * o.nInstances * 4);

                    passEncoder.draw(primitive.nTriangles * 3, o.nInstances);
                }

                while ((o = this.cameraObjects.shift())) {

                    const primitive = o.p;
                    const pipeline = primitive.pipeline;
                    const tr = o.tr;

                    const pl = this.getPipeLine(pipeline, primitive);
                    const p = pl.p;
                    passEncoder.setPipeline(p);
                    passEncoder.setBindGroup(0, this.globalGroup[pipeline]);
                    
                    if(primitive.uniformLayout ) 
                        passEncoder.setBindGroup(1, primitive.uniformGroups[pipeline]);

                    if(pl.isSkinned) 
                        passEncoder.setBindGroup(2,  o.g[pipeline]);

                    passEncoder.setVertexBuffer(0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, primitive.vertexBufferRange.size);

                    if(tr)
                        passEncoder.setVertexBuffer(1, this.transformAllocator.buffer, tr.start, transformSize  * 4);

                    passEncoder.draw(primitive.nTriangles * 3);
                }
            }
            passEncoder.end();
        }

        if(this.renderPass['ui']) {
            const passEncoderUI = encoder.beginRenderPass({
                colorAttachments: [{
                  view: this.ctx.getCurrentTexture().createView(),
                  clearValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 },
                  loadOp: this.renderMainScene ? 'load' : 'clear',
                  storeOp: 'store'
                }]
            });

            passEncoderUI.label = 'ui pass';
            this.currentPassEncoder = passEncoderUI;


            this.renderPass['ui'].drawFunc(null);

            passEncoderUI.end();
        }


        this.uniformAllocator.stagingBuffer.unmap();
        //this.transformAllocator.stagingBuffer.unmap();
        this.transformStagging.unmap();
        this.bonesStagging.unmap();
        

        copyEncoder.copyBufferToBuffer(this.transformStagging, 0,  this.transformAllocator.buffer, 0, this.transformAllocator.size);
        copyEncoder.copyBufferToBuffer(this.bonesStagging, 0,  this.boneAllocator.buffer, 0, this.boneAllocator.size);
        

        this.device.queue.submit([copyEncoder.finish(), encoder.finish()]);

        this.uniformAllocator.stagingBuffer.destroy();
        //this.vertexAllocator.stagingBuffer.destroy();
        this.transformStagging.destroy();
        this.bonesStagging.destroy();

        let b;
        while(b = this.tmpBuffers.pop()){
            b.destroy();
        };

        this.curFrame ++;
    }

    

    getPipeLine(pipelineName, primitive) {

        if(this.pipelineCache[pipelineName])
            return this.pipelineCache[pipelineName];

        const bufferDesc = primitive.getBufferDescription();

        for(let c=0;c<2;c++){

            for(let p of pipelines) {

                if((p.p !== null)&&(p.d.label == pipelineName))
                    return p;

                if(pipelineName.startsWith('Shadow ')||pipelineName.startsWith('AlphaShadow '))
                    continue;

                if(p.d.label == pipelineName) 
                {
                    if (p.d.vertex) {
                
                        let v = parseShadow(0, this.enableShadowMap, p.v);
                        v = parseVColor(0, primitive.hasColor, v);
                        v = parseSecMap(0, primitive.material && primitive.material.secmap ? true:false, v);
                        v = parseDriftMap(0, primitive.material && primitive.material.driftmap ? true:false, v);
                        v = parseSkinning(0, primitive.hasSkinning, v);


                        const vtx = p.isUI ? p.v:SHADER_GLOBAL + p.d.inc + v;

                        if(p.hasTransform)
                            p.d.vertex.buffers = bufferDesc;
                        else
                            p.d.vertex.buffers = [bufferDesc[0]];

                        p.d.vertex.module = this.device.createShaderModule({ code: vtx });
                    }
                    
                    if (p.d.fragment) {

                        let f = parseShadow(1,this.enableShadowMap, p.f);

                        f = parseVColor (1, primitive.hasColor, f);
                        f = parseSecMap (1, primitive.material && primitive.material.secmap ? true:false, f);
                        f = parseDriftMap(1, primitive.material && primitive.material.driftmap ? true:false, f);

                        f = parseParallax(1, p.d.isParallax, f);

                        const frag = p.isUI ? p.f: SHADER_GLOBAL + p.d.inc + f;

                        p.d.fragment.targets[0].format = this.format;
                        p.d.fragment.module = this.device.createShaderModule({ code:frag });

                        if(p.d.depthStencil)
                            p.d.depthStencil.format = this.enableShadowMap ? 'depth24plus-stencil8' : 'depth32float';

                        p.p = this.device.createRenderPipeline(p.d);

                        p.uSize = 0;
                        for(let k in p.model) {            
                            p.uSize += getTypeSize(p.model[k], 4);
                        }
                        //console.log('created pipeline '+pipelineName);
                        /*console.log(p);*/
                    }

                    this.pipelineCache[pipelineName] = p;
                    return p;
                }
            }

            if(primitive.hasSkinning){

                for(let p of pipelines) {

                    const prefix = pipelineName.substr(0, pipelineName.length -5);
        
                    if( p.d.label.startsWith(prefix) )  {
    
                        const newSkinnedPipelineDesc = { label: pipelineName, 
                                                        layout:'auto', 
                                                        vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                                        fragment: p.d.fragment,
                                                        inc: p.d.inc,
                                                        primitive: p.d.primitive, 
                                                        depthStencil : p.d.depthStencil 
                                                        };
                
                        const newSkinnedPipeLine = { d: newSkinnedPipelineDesc, 
                                                    hasUniforms : p.hasUniforms, 
                                                    isParallax: p.isParallax, 
                                                    hasTransform: p.hasTransform, 
                                                    isUI : p.isUI, 
                                                    useTex: p.useTex, 
                                                    isSkinned : true, 
                                                    p : null, 
                                                    v: p.v, 
                                                    f: p.f, 
                                                    
                                                    model: p.model 
                                                };

                        pipelines.push(newSkinnedPipeLine);
                        break;
                    }
                }

            }
    
        }
        
        
        if(!this.enableShadowMap)
            throw new Error('EngineGPU::setPipeLine Pipeline '+ pipelineName+' not found');

        if(pipelineName.startsWith('Shadow ')) {

            bufferDesc[0].attributes= primitive.hasSkinning ? bufferDesc[0].attributes.slice(0, 3) : bufferDesc[0].attributes.slice(0, 1);

            bufferDesc[1].attributes[0].shaderLocation = 3;
            bufferDesc[1].attributes[1].shaderLocation = 4;
            bufferDesc[1].attributes[2].shaderLocation = 5;
            bufferDesc[1].attributes[3].shaderLocation = 6;
            bufferDesc[1].attributes[4].shaderLocation = 7;

            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            primitive: shadowPipelineDesc.primitive, 
                                            depthStencil : shadowPipelineDesc.depthStencil };

            const v = parseSkinning(0, primitive.hasSkinning, SHADER_GLOBAL + shadowPipeline.v);
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code:  v});
           
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : false, isSkinned: primitive.hasSkinning, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: shadowPipeline.useTex, v: shadowPipeline.v, f: shadowPipeline.f, model: shadowPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            /*console.log(newShadowPipeLine);*/

            this.pipelineCache[pipelineName] = newShadowPipeLine;
            return newShadowPipeLine;
        }

        if(pipelineName.startsWith('BillboardShadow ')) {

            bufferDesc[0].attributes= bufferDesc[0].attributes.slice(0, 1);

            bufferDesc[1].attributes[0].shaderLocation = 3;
            bufferDesc[1].attributes[1].shaderLocation = 4;
            bufferDesc[1].attributes[2].shaderLocation = 5;
            bufferDesc[1].attributes[3].shaderLocation = 6;
            bufferDesc[1].attributes[4].shaderLocation = 7;

            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            primitive: shadowBillboardPipelineDesc.primitive, 
                                            depthStencil : shadowBillboardPipelineDesc.depthStencil };

            const v = parseSkinning(0, primitive.hasSkinning, SHADER_GLOBAL + shadowBillboardPipeline.v);
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code:  v});
           
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : false, isSkinned: false, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: shadowBillboardPipeline.useTex, v: shadowBillboardPipeline.v, f: shadowBillboardPipeline.f, model: shadowBillboardPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            /*console.log(newShadowPipeLine);*/

            this.pipelineCache[pipelineName] = newShadowPipeLine;
            return newShadowPipeLine;
        }


        if(pipelineName.startsWith('BillboardAlphaShadow ')) {

            bufferDesc[0].attributes = bufferDesc[0].attributes.slice(0, 2);

            bufferDesc[0].attributes[1].shaderLocation = 3;

            bufferDesc[1].attributes[0].shaderLocation = 4;
            bufferDesc[1].attributes[1].shaderLocation = 5;
            bufferDesc[1].attributes[2].shaderLocation = 6;
            bufferDesc[1].attributes[3].shaderLocation = 7;
            bufferDesc[1].attributes[4].shaderLocation = 8;
   
            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            fragment: {entryPoint: 'main', targets: []}, 
                                            primitive: alphaShadowillboardPipelineDesc.primitive, 
                                            depthStencil : alphaShadowillboardPipelineDesc.depthStencil };

            const v = parseSkinning(0, false, SHADER_GLOBAL + alphaShadowBillboardPipeline.v);
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code: v });
            newShadowPipeLineDesc.fragment.module = this.device.createShaderModule({ code: SHADER_GLOBAL + alphaShadowBillboardPipeline.f });
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : true, isSkinned : false, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: alphaShadowBillboardPipeline.useTex, v: alphaShadowBillboardPipeline.v, f: alphaShadowBillboardPipeline.f, model: alphaShadowBillboardPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);

            this.pipelineCache[pipelineName] = newShadowPipeLine;

            return newShadowPipeLine;
        }

        if(pipelineName.startsWith('AlphaShadow ')) {


            bufferDesc[0].attributes = primitive.hasSkinning ? bufferDesc[0].attributes.slice(0, 4) : bufferDesc[0].attributes.slice(0, 2);

            bufferDesc[0].attributes[1].shaderLocation = 3;

            bufferDesc[1].attributes[0].shaderLocation = 4;
            bufferDesc[1].attributes[1].shaderLocation = 5;
            bufferDesc[1].attributes[2].shaderLocation = 6;
            bufferDesc[1].attributes[3].shaderLocation = 7;
            bufferDesc[1].attributes[4].shaderLocation = 8;
   
            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            fragment: {entryPoint: 'main', targets: []}, 
                                            primitive: alphaShadowPipelineDesc.primitive, 
                                            depthStencil : alphaShadowPipelineDesc.depthStencil };

            const v = parseSkinning(0, primitive.hasSkinning, SHADER_GLOBAL + alphaShadowPipeline.v);
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code: v });
            newShadowPipeLineDesc.fragment.module = this.device.createShaderModule({ code: SHADER_GLOBAL + alphaShadowPipeline.f });
           
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : true, isSkinned : primitive.hasSkinning, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: alphaShadowPipeline.useTex, v: alphaShadowPipeline.v, f: alphaShadowPipeline.f, model: alphaShadowPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            /*console.log(newShadowPipeLine);*/

            this.pipelineCache[pipelineName] = newShadowPipeLine;
            return newShadowPipeLine;
        }


        /*
        if(pipelineName.startsWith('Shadow ')) {

            bufferDesc[0].attributes= bufferDesc[0].attributes.slice(0, 3);

            bufferDesc[1].attributes[0].shaderLocation = 3;
            bufferDesc[1].attributes[1].shaderLocation = 4;
            bufferDesc[1].attributes[2].shaderLocation = 5;
            bufferDesc[1].attributes[3].shaderLocation = 6;
            bufferDesc[1].attributes[4].shaderLocation = 7;

            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            primitive: shadowPipelineDesc.primitive, 
                                            depthStencil : shadowPipelineDesc.depthStencil };

            const v = parseSkinning(0, true, SHADER_GLOBAL + shadowPipeline.v);
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code:  v});
           
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : false, isSkinned: true, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: shadowPipeline.useTex, v: shadowPipeline.v, f: shadowPipeline.f, model: shadowPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            //console.log(newShadowPipeLine);

            return newShadowPipeLine;
        }

        if(pipelineName.startsWith('AlphaSkinnedShadow ')) {

            bufferDesc[0].attributes=  bufferDesc[0].attributes.slice(0, 4);

            bufferDesc[1].attributes[0].shaderLocation = 4;
            bufferDesc[1].attributes[1].shaderLocation = 5;
            bufferDesc[1].attributes[2].shaderLocation = 6;
            bufferDesc[1].attributes[3].shaderLocation = 7;
            bufferDesc[1].attributes[4].shaderLocation = 8;
   
            const newShadowPipeLineDesc = { label: pipelineName+' Bone', 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            fragment: {entryPoint: 'main', targets: []}, 
                                            primitive: alphaShadowPipelineDesc.primitive, 
                                            depthStencil : alphaShadowPipelineDesc.depthStencil };

            const v = parseSkinning(0, true, SHADER_GLOBAL + alphaShadowPipeline.v);
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code: v });
            newShadowPipeLineDesc.fragment.module = this.device.createShaderModule({ code: SHADER_GLOBAL + alphaShadowPipeline.f });
           
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : true, isSkinned : true, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: alphaShadowPipeline.useTex, v: alphaShadowPipeline.v, f: alphaShadowPipeline.f, model: alphaShadowPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            //console.log(newShadowPipeLine);

            return newShadowPipeLine;
        }
        */

        /*

        if(pipelineName.startsWith('SkinnedShadow ')) {

            bufferDesc[0].attributes=bufferDesc[0].attributes.slice(0, 3);

            bufferDesc[1].attributes[0].shaderLocation = 3;
            bufferDesc[1].attributes[1].shaderLocation = 4;
            bufferDesc[1].attributes[2].shaderLocation = 5;
            bufferDesc[1].attributes[3].shaderLocation = 6;
            bufferDesc[1].attributes[4].shaderLocation = 7;

            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            primitive: shadowPipelineDesc.primitive, 
                                            depthStencil : shadowPipelineDesc.depthStencil 
                                          };
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code: SHADER_GLOBAL + shadowSkinnedPipeline.v });
           
            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : false, isSkinned : true, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: shadowSkinnedPipeline.useTex, v: shadowPipeline.v, f: shadowPipeline.f, model: shadowPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            //console.log(newShadowPipeLine);

            return newShadowPipeLine;
        }
        if(pipelineName.startsWith('AlphaSkinnedShadow ')) {

            bufferDesc[0].attributes=bufferDesc[0].attributes.slice(0, 4);
            
            bufferDesc[1].attributes[0].shaderLocation = 4;
            bufferDesc[1].attributes[1].shaderLocation = 5;
            bufferDesc[1].attributes[2].shaderLocation = 6;
            bufferDesc[1].attributes[3].shaderLocation = 7;
            bufferDesc[1].attributes[4].shaderLocation = 8;

            const newShadowPipeLineDesc = { label: pipelineName, 
                                            layout:'auto', 
                                            vertex: { entryPoint: 'main', buffers: bufferDesc}, 
                                            fragment: {entryPoint: 'main', targets: []}, 
                                            primitive: alphaShadowSkinnedPipelineDesc.primitive, 
                                            depthStencil : alphaShadowSkinnedPipelineDesc.depthStencil 
                                          };
            
            newShadowPipeLineDesc.vertex.module = this.device.createShaderModule({ code: SHADER_GLOBAL + alphaSkinnedPipeline.v });
            newShadowPipeLineDesc.fragment.module = this.device.createShaderModule({ code: SHADER_GLOBAL + alphaSkinnedPipeline.f });

            const newShadowPipeLine = { d: newShadowPipeLineDesc, hasUniforms : true, isSkinned : true, p : this.device.createRenderPipeline(newShadowPipeLineDesc), hasTransform:true, useTex: alphaSkinnedPipeline.useTex, v: alphaSkinnedPipeline.v, f: alphaSkinnedPipeline.f, model: alphaSkinnedPipeline.model };

            newShadowPipeLine.uSize = 0;
            for(let k in newShadowPipeLine.model) {            
                newShadowPipeLine.uSize += getTypeSize(newShadowPipeLine.model[k], 4);
            }

            pipelines.push(newShadowPipeLine);
            
            //console.log('created shadow pipeline '+pipelineName);
            //console.log(newShadowPipeLine);

            return newShadowPipeLine;
        }
        */

        throw new Error('EngineGPU::setPipeLine Pipeline '+ pipelineName+' not found');
    }

    setMeshStatic(mesh, st){
        if(!mesh.meshGPU)
            return;
        mesh.meshGPU.is_static = st;

    }

    drawPrimitive(primitive, t, transformBufferRange, pipe, boneGroups = null, boneRestEntry = null, boneEntry = null, nInstances = 1) {
        let pipeline=null;

        if((primitive.uniformLayout !== null) &&( primitive.uniformBufferRange === null))
            return;

        if(pipe === 'shadow'){

            if(primitive.material.disableShadow)
                return;

             if((primitive.material.transparent)&&(primitive.material.tex)){

                if(primitive.isBillBloard)
                    pipeline = 'BillboardAlphaShadow '+primitive.pipeline;
                else
                    pipeline = 'AlphaShadow '+primitive.pipeline;
             } else {

                if(primitive.isBillBloard)
                    pipeline = 'BillboardShadow '+primitive.pipeline;
                else
                    pipeline = 'Shadow '+primitive.pipeline;
             }
                
        }else{
            pipeline = primitive.pipeline;
        } 

        if(primitive.hasSkinning)
            pipeline += ' Bone';

        const pl = this.getPipeLine(pipeline, primitive);
        const p = pl.p;
        this.currentPassEncoder.setPipeline(p);
        
        if(primitive.hasSkinning === true) {

            if(!boneGroups[pipeline]) {
                boneGroups[pipeline] = this.device.createBindGroup({ layout: p.getBindGroupLayout(2), entries: [boneRestEntry, boneEntry], label: 'bones uniforms '+primitive.material.name+' '+pipe });
            }
        }
        
        /*
        if((primitive.material)&&(primitive.material.bmap)){
            const texture = primitive.material.bmap;
            this.device.queue.writeTexture({texture}, this.roadMapArray, {bytesPerRow : this.bumpTexWidth}, { width: this.bumpTexWidth, height: this.bumpTexHeight });
        }
        */
        
    
        if (!this.globalGroup[pipeline]) {

            const entries = [];

            if(pipeline.startsWith('ui')) {

                entries[0] = this.globalUIUniforms[0]; // projMat

                if(pl.useTex.modelTex)
                    entries[1] = this.globalUIUniforms[1]; // tex sampler

            }else{

                entries[0] = this.globalUniforms[0]; //global uniforms

                if(pl.useTex.useSampler){
                    entries.push(this.globalUniforms[1]); // linear tex sampler
                }

                if(pl.useTex.envMap){
                    entries.push(this.globalUniforms[2]); // envmap
                }

                if(pl.useTex.useSamplerN){
                    entries.push(this.globalUniforms[3]); // nearest tex sampler
                }

                if((this.enableShadowMap)&&(pl.useTex.shadowMap)){
                    entries.push(this.globalUniforms[4]); // shadowmap tex
                    entries.push(this.globalUniforms[5]); // shadowmap sampler
                }
            }

            this.globalGroup[pipeline] = this.device.createBindGroup({ layout: p.getBindGroupLayout(0), entries: entries, label: 'global '+pipeline+' for '+p.label });
        }

        if(primitive.uniformLayout ) {

             if(pipe !== 'shadow')
            {
                if((primitive.needsUpdate)||(primitive.initialized<2))
                {
                    //const usize = Math.max(32, pl.uSize);
                    if(pl.uSize>0){
                        const uniformBuffer = this.uniformAllocator.getStagingRange(pl.uSize);
                        const us = primitive.writeUniforms( uniformBuffer, pl.model);
                        this.uniformAllocator.commitStagingRange(primitive.uniformBufferRange, us, pl.uSize);
                    }
                    primitive.initialized++;
                    primitive.needsUpdate=false;
                }
            }


            if((pl.hasUniforms) &&(!primitive.uniformGroups[pipeline])) {
                const entries=[];
                let curEntry = 0;

                if(primitive.uniformEntries[0].binding === 0){

                    if(pipe !== 'shadow')
                        entries.push(primitive.uniformEntries[curEntry]); /* first entries for model uniforms */

                    curEntry++
                }

                if(pl.isUI) {

                    if(pl.useTex.modelTex){
                        entries.push(primitive.uniformEntries[curEntry]); /* second entries for model texture */
                        curEntry++;
                    }
                        

                }else if(pl.useTex.modelTex) {

                    entries.push(primitive.uniformEntries[curEntry]); /* second entries for model texture */
                    curEntry++

                    if(pipe !== 'shadow'){
                        if(primitive.uniformEntries.length>2){
                            entries.push(primitive.uniformEntries[curEntry]); /* third entries for model normal Map */
                            curEntry++;
                        }
                            
                        if(primitive.uniformEntries.length>3){
                            entries.push(primitive.uniformEntries[curEntry]); /* fourth entries for model roughness Map */
                            curEntry++;
                        }
                            
                        if(primitive.uniformEntries.length>4){
                            entries.push(primitive.uniformEntries[curEntry]); /* fourth entries for model roughness Map */
                            curEntry++;
                        }
                            
                        if(primitive.uniformEntries.length>5){
                            entries.push(primitive.uniformEntries[curEntry]); /* fourth entries for model sec Map */     
                            curEntry++;
                        }
                        
                        if(primitive.uniformEntries.length>6){
                            entries.push(primitive.uniformEntries[curEntry]); /* fourth entries for model sec normal Map */     
                            curEntry++;
                        }
                            
                        if(primitive.uniformEntries.length>7){
                            entries.push(primitive.uniformEntries[curEntry]); /* fourth entries for model sec normal Map */     
                            curEntry++
                        }
                    }
                }

                const label = primitive.material ? 'material '+primitive.material.name :'no material';
                primitive.uniformGroups[pipeline] = this.device.createBindGroup({ layout: p.getBindGroupLayout(1), entries:entries, label : label +' pipeline '+pipeline});
            }
        }


        if(primitive.isScreenSpace === true){
            this.cameraObjects.push({ p: primitive, t:t, tr:transformBufferRange, g: boneGroups});
            return;
        }else if((primitive.material)&&((primitive.material.opacity<1.0)||(primitive.material.transparent))) {

            if(this.transparentObjects) {
                this.transparentObjects.push({ p: primitive, t:t, tr:transformBufferRange, g: boneGroups, nInstances: nInstances});
                return;
            } 
            else if(primitive.material.opacity < this.opacityThresold)
                return;
        }

        this.currentPassEncoder.setBindGroup(0, this.globalGroup[pipeline]);

        if(primitive.uniformLayout )
            this.currentPassEncoder.setBindGroup(1,  primitive.uniformGroups[pipeline]);

        if(pl.isSkinned) 
            this.currentPassEncoder.setBindGroup(2,  boneGroups[pipeline]);            

        this.currentPassEncoder.setVertexBuffer(0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, primitive.vertexBufferRange.size);
        this.currentPassEncoder.draw(primitive.nTriangles * 3, nInstances);
           
    }

    drawSprite(sprite) {

        if(!sprite)
            return;

        this.drawPrimitive(sprite.primitive, null, null, 'ui');

    }

    drawMesh(mesh, pipe = null, parentTransform = null, parentSize = null, forceUpdate = false) {
        if((mesh === null)||(mesh === undefined))
            return;

        if(mesh.size<=0)
            return;

        const size = parentSize ? mesh.size * parentSize : mesh.size;
        let t;

        if(mesh.primitives.length>0){

            const bone_cnt = mesh.rootBone.numBones;

            if(bone_cnt > 1){

                if(mesh.lastUpdateFrame !== this.curFrame){
                    if(mesh.boneBufferRange) {
                    
                        const boneArraySize = bone_cnt * boneSize;
        
                        for(let i=0;i<mesh.nInstances;i++){
                            mesh.writeBones(this.boneBuffer, (mesh.boneBufferRange.start / 4) + i * boneArraySize, i);
                        }
                    }
                }
            
            }
            
            if(mesh.nInstances<2)
                mesh.nDrawInstances =1;
            else if(mesh.lastUpdateFrame !== this.curFrame)
                mesh.nDrawInstances = mesh.sortInstances(this.cameraAnchor.position, 500);
            
            //mesh.nDrawInstances = mesh.nInstances;
            

            if(mesh.transformBufferRange) {

                if(mesh.lastUpdateFrame !== this.curFrame){
                    //if((!mesh.is_static) || (mesh.needsUpdate) || (forceUpdate))
                    {
                        //const totalSize = transformSize * mesh.nInstances;
                        //const transformBuffer = this.transformAllocator.getStagingRange(totalSize);
                        for(let i=0;i<mesh.nDrawInstances;i++){

                            if(parentTransform !== null) {
                                t = new Transformation3();
                                parentTransform.multiply2(mesh.transformations[mesh.indices[i]], t);
                            }else   
                                t = i<mesh.transformations.length ? mesh.transformations[mesh.indices[i]] : mesh.transformations[0];
                
                            mesh.writeTransformAttributes( this.transformBuffer, t, mesh.size, (mesh.transformBufferRange.start / 4)+  transformSize * i, mesh.indices[i]);
                            //mesh.writeTransformAttributes( transformBuffer, t, size, transformSize * i, i);
                        }
                        //this.transformAllocator.commitStagingRange(mesh.transformBufferRange, totalSize);
                    }
                }

                if(mesh.nDrawInstances>0)
                    this.currentPassEncoder.setVertexBuffer(1, this.transformAllocator.buffer, mesh.transformBufferRange.start, transformSize * mesh.nDrawInstances * 4);

                if(mesh.nDrawInstances>0){
                    for(let n=0;n< mesh.primitives.length;n++) {
                        this.drawPrimitive(mesh.primitives[n], mesh.transformations[0], mesh.transformBufferRange, pipe, mesh.boneGroups, mesh.boneRestEntry, mesh.boneEntry, mesh.nDrawInstances);
                    }
                }
            }
        }else{
            if(parentTransform !== null) {
                t = new Transformation3();
                parentTransform.multiply2(mesh.transformations[0], t);
            }else   
                t = mesh.transformations[0];
            
        }

        for(let c of mesh.children){
            this.drawMesh(c, pipe, t, size, mesh.needsUpdate);
        }

        
        mesh.lastUpdateFrame = this.curFrame;
        mesh.needsUpdate=false;
    }

    postRender(world) { }
    compileShaders() { }
    initVR (){}

    updateTransform(m, t, index = 0) {

        if(m.meshGPU){
            m.meshGPU.transformations[index].set(t.position, t.quaternion);
            m.meshGPU.needsUpdate = true;
        }
            
    }


    updateBoneTransform(m, boneName, q, instanceID = 0) {
        if(!m.meshGPU)
            return;

        const bone = m.meshGPU.findBone(boneName);
        if(bone){

            //q.invert();
            /*
            if(!bone.oq){
                bone.oq= new THREE.Quaternion(bone.transforms[instanceID].quaternion.x, bone.transforms[instanceID].quaternion.y, bone.transforms[instanceID].quaternion.z, bone.transforms[instanceID].quaternion.w );
            }

            bone.transforms[instanceID].quaternion = bone.oq.clone().multiply(q);
            bone.transforms[instanceID].computeMatrix();
            */

            bone.transforms[instanceID].set(null, q);
            /*
            bone.transforms[instanceID].quaternion.x = q.x;
            bone.transforms[instanceID].quaternion.y = q.y;
            bone.transforms[instanceID].quaternion.z = q.z;
            bone.transforms[instanceID].quaternion.w = q.w;
            bone.transforms[instanceID].computeMatrix();
            */
            
            m.meshGPU.updateBones = true;
        }
    }

    findCyl(req) {

        for(let cyl of this.cachedCyls){
            
            if( (cyl.height === req.height) &&
                ((Math.abs(cyl.radius - req.radius))<0.1) &&
                (cyl.type === req.type)){

                return cyl;
            }
        }

        return null;
    }

    clearCachedCyls(){

        for(let c of this.cachedCyls){
            this.removeMesh(c);
        }

        this.cachedCyls=[];
    }

 
    getMeshPos(mesh, id, m){

        return mesh.transformations[id].position;
    }

    addGrass(grass){

        grass.mesh = new THREE.Mesh(grass.geo, grass.mat);
        grass.mesh.meshGPU = this.addMesh(grass.mesh, RT_DIFF, new Transformation3(), {x: false, y :false, z: false}, true, null, grass.nGrass); //new THREE.InstancedMesh(grass.geo, grass.mat, grass.nGrass);

        for(let n=0;n<grass.nGrass;n++){
            this.updateTransform(grass.mesh, grass.transforms[n], n);
        }
    }
    
    addCylinder(c, radius, position, height, quat, color, roughness, type, tex = null, norm = null) {

        let matType;
        if (type == RT_REFR) {
            matType = 2;
        } else if(norm) {
            matType = 1;
        } else {
            matType = 0;
        }
        const reqCyl = {radius : radius, height: height, type: matType};
        const myCyl = this.findCyl(reqCyl);

        if(myCyl === null){

            reqCyl.geom = new THREE.CylinderGeometry(radius, radius, height, 24, 16);
            reqCyl.geom.rotateZ(Math.PI/2)
            
            if (matType == 2) {
                reqCyl.mat = new THREE.MeshPhysicalMaterial({ color: 0xffFFFF, transparent: true, transmission: 1.0, thickness:0.4, reflectivity: 0.3, map: tex, normalMap: norm, metalness:0.0, ior: 3.0 }); //new THREE.MeshPhysicalMaterial({ color: color, map: tex, normalMap: norm, transparent: true });
            } else if (matType == 1) {
                reqCyl.mat = new THREE.MeshPhongMaterial({ color: color, map: tex, normalMap: norm, depthWrite: true });
            } else if (matType == 0) {
                reqCyl.mat = new THREE.MeshBasicMaterial({ color: color, map: tex, side: THREE.DoubleSide, depthWrite: true });
            }
            reqCyl.object = new THREE.Mesh(reqCyl.geom, reqCyl.mat);

            reqCyl.object.name = 'cylinder';

            reqCyl.meshGPU = this.addMesh(reqCyl.object);
            reqCyl.meshGPU.transformations[0].set(position, quat);
            c.meshGPU = reqCyl.meshGPU;
            this.cachedCyls.push(reqCyl);
        }else{
            this.tmpTransform.set(position, quat);
            c.meshGPU = this.addMeshInstance(myCyl.meshGPU, this.tmpTransform, true);
            c.object = myCyl.object;
        }

        c.meshGPU.primitives[0].material.diffuse.r = color.x;
        c.meshGPU.primitives[0].material.diffuse.g = color.y;
        c.meshGPU.primitives[0].material.diffuse.b = color.z;
        c.meshGPU.primitives[0].material.roughness = roughness;
    }

    setCylinderPos(c, radius, center, height, quat) {

        if(!c.meshGPU)
            return;

        c.meshGPU.transformations[0].set(center, quat);
         
    }

    
    addSphereInstance(d, c, x, y, z) {

        /*
        d.mat = c.mat;
        d.object = c.object.clone();
        d.object.position.set(x, y, z);

        this.addMesh(d.object);
        */
        d.meshGPU = this.addMeshInstance(c.meshGPU, new Transformation3({x:x, y:y, z:z}));
        d.meshGPU.size = c.meshGPU.size;
        d.meshGPU.is_static = c.meshGPU.is_static;

        
    }

    addSphere(c, radius, position, emissive, color, roughness, type) {
        
        this.tmpTransform.set(position, this.zeroQ);

        if (type == RT_REFR) {
            c.meshGPU = this.addMeshInstance(this.sphereGPU1, this.tmpTransform, true);
        } else {
            c.meshGPU = this.addMeshInstance(this.sphereGPU2, this.tmpTransform, true);
        }
        this.setMeshStatic(c, false);

        c.meshGPU.primitives[0].material.roughness = roughness;
        c.meshGPU.primitives[0].material.diffuse.r = color.x;
        c.meshGPU.primitives[0].material.diffuse.g = color.y;
        c.meshGPU.primitives[0].material.diffuse.b = color.z;

        c.meshGPU.primitives[0].material.emissive.r = emissive.x;
        c.meshGPU.primitives[0].material.emissive.g = emissive.y;
        c.meshGPU.primitives[0].material.emissive.b = emissive.z;
        c.meshGPU.primitives[0].material.emissiveIntensity = 1.0;

        c.meshGPU.size = radius;

    }


    moveSpherePosition(c, x, y, z, r) {

        if(x)
            c.meshGPU.transformations[0].position.x+=x;
        if(y)
            c.meshGPU.transformations[0].position.y+=y;
        if(z)
            c.meshGPU.transformations[0].position.z+=z;
        if(r)
            c.meshGPU.size = r;
    }

    setSpherePosition(c, x, y, z, r) {

        if(c.meshGPU) {
            c.meshGPU.transformations[0].setPos(x, y, z);
            c.meshGPU.size = r;
            c.meshGPU.needsUpdate = true;
        }

    }

    setSphereColor(c, r, g, b, a = null) {
        
        if(c.meshGPU) {
            if(r)
                c.meshGPU.primitives[0].material.diffuse.r = r;

            if(g)
                c.meshGPU.primitives[0].material.diffuse.g = g;
            
            if(b)
                c.meshGPU.primitives[0].material.diffuse.b = b;

            if(a){
                c.meshGPU.primitives[0].material.opacity = a;
            }

            c.meshGPU.primitives[0].needsUpdate = true;
                
        }
    }

    setSphereEmissive(c, r, g, b) {

        if(c.meshGPU) {
            if(r !== null)
                c.meshGPU.primitives[0].material.emissive.r = r;

            if(g !== null)
                c.meshGPU.primitives[0].material.emissive.g = g;
            
            if(b !== null)
                c.meshGPU.primitives[0].material.emissive.b = b;

            c.meshGPU.primitives[0].material.emissiveIntensity = 1.0;

            c.meshGPU.primitives[0].needsUpdate = true;
        }
    }

    addBox(c, size, position, color, roughness, type, emissive = {x:0,y:0,z:0}) {
        c.geom = new THREE.BoxGeometry(size.x, size.y, size.z, 1, 1, 1);
        if (type == RT_REFR) {
            c.mat = new THREE.MeshPhysicalMaterial({ color: color, transparent: true });
            c.mat.transmission = 1.0;
            c.mat.thickness = 0.1;
            c.mat.roughness = roughness;
            c.mat.reflectivity = 0.1;
            c.mat.metalness = 0.0;
            c.mat.ior = 3;
        } else {
            c.mat = new THREE.MeshBasicMaterial({ color: color, transparent: true });
        }

        c.object = new THREE.Mesh(c.geom, c.mat);
        c.object.position.set(position.x, position.y, position.z);

        c.meshGPU = this.addMesh(c.object);
        c.meshGPU.transformations[0].set(position);

        
        c.meshGPU.primitives[0].material.roughness = roughness;
        c.meshGPU.primitives[0].material.diffuse.r = color.x;
        c.meshGPU.primitives[0].material.diffuse.g = color.y;
        c.meshGPU.primitives[0].material.diffuse.b = color.z;

        c.meshGPU.primitives[0].material.emissive.r = emissive.x;
        c.meshGPU.primitives[0].material.emissive.g = emissive.y;
        c.meshGPU.primitives[0].material.emissive.b = emissive.z;

        c.meshGPU.primitives[0].material.emissiveIntensity = 1.0;
    }

    removeBox(c) {

        if (c.meshGPU){
            this.removeMesh(c);
        }
            
        if (c.geom)
            c.geom.dispose();

        if (c.mat)
            c.mat.dispose();
    }

    removeSphere(c) {
        if (c.meshGPU) {
            this.removeMesh(c);
        }
    }

    removeCylinder(c) {

        if (c.meshGPU){
            this.removeMesh(c);
        }
    }


    removeSprite(m) {

        if(m === null)
            return;

        const mesh = m.spriteGPU ? m.spriteGPU : m;
        if(!mesh)
            return;

        /*
        console.log('removeMesh');
        console.log(mesh);
        */

        if(mesh.primitive.vertexBufferRange)
            this.vertexAllocator.unrefRange(mesh.primitive.vertexBufferRange);

        if(mesh.primitive.uniformBufferRange)
            this.uniformAllocator.unrefRange(mesh.primitive.uniformBufferRange);

        mesh.primitive.vertexBufferRange = null;
        mesh.primitive.uniformBufferRange = null;

        if(mesh.transformBufferRange)
            this.transformAllocator.unrefRange(mesh.transformBufferRange);
            
        mesh.transformBufferRange= null;

        if(mesh.boneBufferRange){
            this.boneAllocator.unrefRange(mesh.boneBufferRange);
            mesh.boneBufferRange = null;
        }
        
        m.meshGPU = null;

    }

    removeMesh(m) {

        if((m === null)||(m === undefined))
            return;
        

        const mesh = m.meshGPU ? m.meshGPU : m;
        if(!mesh)
            return;

        if(!mesh.primitives)
            return;
        /*
        console.log('removeMesh');
        console.log(mesh);
        */

        for(let n=0;n<mesh.primitives.length;n++) {

            if(mesh.primitives[n].vertexBufferRange)
                this.vertexAllocator.unrefRange(mesh.primitives[n].vertexBufferRange);

            if(mesh.primitives[n].uniformBufferRange)
                this.uniformAllocator.unrefRange(mesh.primitives[n].uniformBufferRange);
            
            mesh.primitives[n].vertexBufferRange = null;
            mesh.primitives[n].uniformBufferRange = null;
        }

        if(mesh.boneBufferRange)
            this.boneAllocator.unrefRange(mesh.boneBufferRange);

        if(mesh.transformBufferRange)
            this.transformAllocator.unrefRange(mesh.transformBufferRange);
            
        mesh.transformBufferRange= null;
        mesh.boneBufferRange = null;
        //m.meshGPU = null;
    }

    removeUIMesh(im){
        if((!im)||(!im.meshGPU))
            return;
        //const m = im.meshGPU ? im.meshGPU: im;
        this.removeMesh(im.meshGPU);
    }


    async loadPod() {
        await super.loadPod();
        this.podMesh.meshGPU = this.addMesh(this.podMesh, RT_METALCOAT, new Transformation3());
        
    }

    async removePod() {
        this.removeMesh(this.podMesh.meshGPU);
    }


    removeAllParticles() {

        while(this.particles.length>0)
        {
            const p = this.particles.pop();
            this.removeMesh({ meshGPU : p});
        }
    }

    removeParticles(p) {

        let n=0;

        while(n<this.particles.length)
        {

            if(p.id == this.particles[n].id) {

                this.removeMesh({ meshGPU : this.particles[n]});
                this.particles.splice(n, 1);
                return;
            }
            n++;
        }
    }

    addParticles(p) {

        const primitive = p.primitives[0];
        const g = p.particles.particleMesh.geometry;
        const particles = p.particles;
        const encoder = this.device.createCommandEncoder();

        primitive.material.tex = this.createGPUTextureFromImge(particles.particleTexture.image);

        primitive.vertexBufferRange = this.vertexAllocator.getBufferRange( 6 * primitive.vertexLayout.numFloats, 'particle');
        const b = this.device.createBuffer({ size:  6 * primitive.vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const vertexBuffer =  new Float32Array(b.getMappedRange());
        primitive.writeAttributes(vertexBuffer, g, {x: false, y: false, z: false}, {x:1.0, y:1.0}, null, this.cameraTransform);
        b.unmap();
        encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);

        p.transformBufferRange = this.transformAllocator.getBufferRange(transformSize * particles.particleCount, 'particle '+p.name+ ' transform');
        const b1 = this.device.createBuffer({ size:  p.transformBufferRange.size, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const range1 = b1.getMappedRange();
        const transformBuffer = new Float32Array(range1);
        for(let n=0;n<particles.particleCount;n++){
            p.writeTransformAttributes(transformBuffer, p.transformations[0], 1.0, n * transformSize, n);
        }
        b1.unmap();
        encoder.copyBufferToBuffer( b1, 0, this.transformAllocator.buffer,  p.transformBufferRange.start, b1.size);

        this.device.queue.submit([ encoder.finish()]);

        b.destroy();
        b1.destroy();
      

        if(primitive.uniformLayout) {

            const usize = Math.max(32, primitive.uniformLayout.numFloats);
            
            primitive.uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
            primitive.uniformEntries = [];
                
            const offset = primitive.uniformBufferRange.start;

            for (const e of primitive.uniformLayout.entries) {

                if (e.type == 'tex') {
                    primitive.uniformEntries.push({ binding: e.location, resource: primitive.material.tex.createView() });
                } else {

                    /* sum the size of all uniforms at the same location in a structure */
                    let fnd = false;
                    for (const ue of primitive.uniformEntries) {
                        if (ue.binding === e.location) {
                            ue.resource.size += e.getSize(4) * 4;
                            fnd = true;
                            break;
                        }
                    }

                    if(!fnd){
                        primitive.uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                    }
                        
                }
            }

            primitive.uniformEntries[0].resource.size = Math.max(128, primitive.uniformEntries[0].resource.size);
        }

        p.id = this.particleId++;
        this.particles.push(p);
    }

    addParticle(params, pos){

        const particles = new webGPUParticleMesh(params, pos);
        this.addParticles(particles);
        return particles;
    }
 

    updateRain(time, dt, roadObj){

        super.updateRain(time, dt, roadObj);

        const encoder = this.device.createCommandEncoder();

        const primitive = this.rainMesh.meshGPU.primitives[0];
        const b = this.device.createBuffer({ size:  (primitive.nTriangles * 3 ) * primitive.vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const vertexBuffer =  new Float32Array(b.getMappedRange());
        this.rainMesh.meshGPU.update(vertexBuffer, this.rainMesh.geometry, this.cameraTransform);
        b.unmap();
        encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
        this.device.queue.submit([encoder.finish()]);
        b.destroy();
    }
    
    addFire(pos) {

        const particles = new webGPUParticleMesh(candle_params, pos);
        this.addParticles(particles);
        return particles;
    }

    addSand(pos) {
        const particles =  new webGPUParticleMesh(sand_params, pos);
        this.addParticles(particles);
        return particles;
    }

    addFirework(pos) {

        const particles = new webGPUParticleMesh(firework_params, pos);
        this.addParticles(particles);
        return particles;
    }

    addDronePart (pt, params){

        const particles = this.createParticles(params, pt)

        params.positionBase = pt;
        particles.name = 'drone-parts';
        particles.emitterAlive = false;
        particles.pinned = true;
        particles.setValues(params);
        particles.initialize();
        particles.particleMesh.meshGPU = this.addMesh(particles.particleMesh, RT_DIFF, new Transformation3(particles.particleMesh.position, particles.particleMesh.quaternion));
        
        return particles;
    }

    addRain(){
        this.fogDensity = 0.2;
        this.fogColor = new THREE.Vector3(0.6,0.6,0.8);

        this.rainTexGPU = this.createGPUTextureFromImge(this.rainTex.image);

        this.rainMesh.meshGPU = new webGPURainMesh(this.numDrops, 1.0, this.rainTexGPU);

        const primitive = this.rainMesh.meshGPU.primitives[0];
        const g= this.rainMesh.geometry;

        primitive.vertexBufferRange = this.vertexAllocator.getBufferRange( (primitive.nTriangles * 3 ) * primitive.vertexLayout.numFloats, 'rain');

        const encoder = this.device.createCommandEncoder();
        const b = this.device.createBuffer({ size:  (primitive.nTriangles * 3 ) * primitive.vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
        const vertexBuffer =  new Float32Array(b.getMappedRange());
        primitive.writeAttributes(vertexBuffer, g, {x: false, y: false, z: false}, {x:1.0, y:1.0}, null, this.cameraTransform);
        b.unmap();
        encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, primitive.vertexBufferRange.start, b.size);
        this.device.queue.submit([ encoder.finish()]);
        b.destroy();

        
        if(primitive.uniformLayout) {
            
            const usize = Math.max(32, primitive.uniformLayout.numFloats);
            primitive.uniformBufferRange = this.uniformAllocator.getBufferRange(usize);
            primitive.uniformEntries = [];
                
            const offset = primitive.uniformBufferRange.start;

            for (const e of primitive.uniformLayout.entries) {

                if (e.type == 'tex') {
                    primitive.uniformEntries.push({ binding: e.location, resource: primitive.material.tex.createView() });
                } else {

                    /* sum the size of all uniforms at the same location in a structure */
                    let fnd = false;
                    for (const ue of primitive.uniformEntries) {
                        if (ue.binding === e.location) {
                            ue.resource.size += e.getSize(4) * 4;
                            fnd = true;
                            break;
                        }
                    }

                    if(!fnd)
                        primitive.uniformEntries.push({ binding: e.location, resource: { buffer: this.uniformAllocator.buffer, offset: offset + e.location * UNIFORM_ALIGN, size: e.getSize(4) * 4 } });
                }
            }
        }

        
    }

    async addSkyBox(skyboxImage) {


        const loader = new THREE.TextureLoader();
         const url = this.hdSkybox ? "/assets/skybox/" + skyboxImage: "/assets/skybox/small/" + skyboxImage

        this.skyTexture = await loader.loadAsync(this.assetBase+url);
        this.skyTexture.flipY = true;
        this.skyTexture.generateMipmaps = false;

        this.envMap = await this.createGPUTextureFromImge(this.skyTexture.image);

        if(this.skybox === null){
            const g = new THREE.SphereGeometry(50, 60, 40).toNonIndexed();
            const flip = {x:true,y:false,z:false};
            const nTris = g.attributes.position.array.length / 9;
            const skybox = new webGPUSkyBoxMesh(nTris);
            skybox.primitives[0].vertexBufferRange = this.vertexAllocator.getBufferRange(nTris * 3 * skybox.primitives[0].vertexLayout.numFloats ,'skybox');
            skybox.primitives[0].material = {name :'sky box material'}  ;
            const encoder = this.device.createCommandEncoder();

            const b = this.device.createBuffer({ size:  nTris * 3 * skybox.primitives[0].vertexLayout.numFloats * 4, usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC, mappedAtCreation: true});
            const vertexBuffer =  new Float32Array(b.getMappedRange());
            skybox.primitives[0].writeAttributes(vertexBuffer, g, flip );
            b.unmap();
            encoder.copyBufferToBuffer( b, 0, this.vertexAllocator.buffer, skybox.primitives[0].vertexBufferRange.start, b.size);
            this.device.queue.submit([ encoder.finish()]);
            b.destroy();
            /*
            console.log('addSkybox final:');
            console.log(skybox);
            */
            this.skybox = skybox;
        
        }

        this.globalUniforms[2].resource = this.envMap.createView();

        for (let p of pipelines) {
            this.globalGroup[p.d.label] = null;
        }
        
    }



    initPostProcessing(enable) {
        
    }
  
    resetAnimation(char){

  
    }

    applyAnimation(char, animation, t, instanceID = 0){

        if(!animation)
            return;

        for(let n=0;n<animation.bones.length;n++){

            const bone = char.avatarMesh.meshGPU.findBone(animation.bones[n].name);
            if(bone){
                const q = animation.bones[n].getValue(t);
                bone.transforms[instanceID].set(null, {x: q.x, y: q.y, z: q.z, w: q.w} );
                
            }
        }

        char.avatarMesh.meshGPU.updateBones = true;
    }

    setupCarMat(mat) {
        return;
        switch (this.MatType) {
            default:
            case 0:
                //mat.envMap = await this.generator.fromEquirectangular(this.skyTexture).texture;
                mat.envMap = this.skyTexture;
                mat.envMapIntensity = 0.4;
                mat.needsUpdate = true;
                break;
            case 1:
                mat.envMapIntensity = 8;
                mat.metalness = 1.0;
                mat.bumpScale = 0.01;
                mat.reflectivity = 0.9;
                mat.sheen = 1;
                mat.sheenRoughness = 0.0;
                mat.roughness = 0.0;
                mat.Clearcoat = 0.2;
                mat.needsUpdate = true;
                break;
        }
    }

    setShadowBias (bias){

        this.shadowBias = bias;


    }
    
    setShadowProj(x, y, z){

               
        this.shadowProjM.makeOrthographic(-x , x , -y, y, -z, z);
        this.shadowProjM.toArray(this.shadowProj);

        /*
        glOrtho(this.shadowProj, -x,x, -y, y, -z, z);
        this.shadowProjM.fromArray(this.shadowProj);
        */
        
        
        /*
        const cam = new THREE.OrthographicCamera(-x,x, -y, y, 0.1, 340);
        cam.updateProjectionMatrix();
        this.shadowProjM = cam.projectionMatrix;
        this.shadowProjM.toArray(this.shadowProj);
        */
    }

    setShadowSource(position, target) {
        
        /*
        this.ShadowViewMat.identity();
        this.ShadowViewMat.lookAt(new THREE.Vector3(position.x, position.y, position.z ), new THREE.Vector3(target.x, target.y, target.z ), new THREE.Vector3(0, 0, 1 ));
        this.ShadowViewMat.setPosition(position.x, position.y, position.z);
        */

        this.shadowTransform.set(position);

        if(target)
            this.shadowTransform.lookAt({x:target.position.x, y : target.position.y, z: target.position.z}, new THREE.Vector3(0, 0, 1 ));
        else
            this.shadowTransform.lookAt({x:0, y : 0, z: 0}, new THREE.Vector3(0, 0, 1 ));
    }

    setSunLight(pos, target) {
     
        const p = target ? {x: pos.x - target.position.x , y: pos.y - target.position.y , z: pos.z - target.position.z } : {x: pos.x, y: pos.y, z: pos.z };
        this.enableLight(0, 1,  new THREE.Vector3(p.x, p.y, p.z).normalize(), new THREE.Vector3(0, 0, 0), {r: 0.8, g: 0.6, b: 0.2}, 0, 0);

        this.shadowTransform.set(pos);

        if(target)
            this.shadowTransform.lookAt({x:target.position.x, y : target.position.y,z: target.position.z}, new THREE.Vector3(0, 0, 1 ));
        else
            this.shadowTransform.lookAt({x:0, y : 0,z: 0}, new THREE.Vector3(0, 0, 1 ));

        //this.scene.add(this.directionalLight);
    }

    addDirLight(sp, dir, color) {

        const c = new THREE.Color().setRGB(color.x,color.y, color.z);
        c.multiplyScalar(2.5);


        for(let n=1;n<this.numLights;n++) {
            if(this.lights[n].type === 0 ) {
                this.enableLight(n, 1 , dir, {x:0,y:0,z:0}, c, 0, 0);

                sp.lightID = n;
                return n;
            }
        }
    }

    setLightIntensity(sp, i) {

        if(sp.lightID>=0)
            this.lights[sp.lightID].attenuation.x =  1.0 / (i * 100);
    }
    
    removeLight(sp){

        if(sp.lightID >= 0){
            this.lights[sp.lightID].type = 0;
            sp.lightID = -1;
        }

    }
    addRectLight(sp, pos, target, color, width, height, i) {

        for(let n=1;n<8;n++) {
            if(this.lights[n].type === 0 ) {
                this.enableLight(n, 2, pos , {x:0,y:0,z:0}, {r:color.x, g:color.y, b:color.z }, 1.0 / (i * 100), 0);
                sp.lightID = n;
                return n;
            }
        }
    }

    addPointLight(sp, pos, color, radius) {

        for(let n=1;n<this.numLights;n++) {
            if(this.lights[n].type === 0 ) {
                
                this.enableLight(n, 2, pos , {x:0,y:0,z:0}, {r:color.x, g:color.y, b:color.z }, 1 / (radius * radius), 0);

                sp.lightID = n;
                return n;
            }
        }
    }

    addSpotLight(sp, pos, dir, color, radius, aa) {

        for(let n=1;n<this.numLights;n++) {
            if(this.lights[n].type === 0 ) {
                this.enableLight(n, 3, pos , dir, {r:color.x, g:color.y, b:color.z }, 1 / (radius * radius), aa);
                sp.lightID = n;
                return n;
            }
        }
    }
    setLightPosition(c, x, y, z, r) {

        if(c.lightID) {

            if(x!=null)
                this.lights[c.lightID].vec.x=x;

            if(y!=null)

                this.lights[c.lightID].vec.y=y;
                
            if(z!=null)
                this.lights[c.lightID].vec.z=z;            
            
            this.lights[c.lightID].attenuation.x = r != 0.0 ? 1.0 / ( r * r): 0.0;
        }
    }
    setLightColor(c, r, g, b) {

        if(c.lightID) {

            if((r+b+g)<0.1){
                this.lights[c.lightID].type = 0;
            }else{
                this.lights[c.lightID].type = 3;
                this.lights[c.lightID].color.r = r;
                this.lights[c.lightID].color.g = g;
                this.lights[c.lightID].color.b = b;
            }

        }
    }

    setLightDir(c, x, y, z, a) {

        if(c.lightID) {
            this.lights[c.lightID].vec2.set(x, y, z);
            this.lights[c.lightID].attenuation.y = a;
        }
    }

    removeCar(car) {

        if (car === null)
            return;
        
        for (let n = 0; n < car.skidPlanes.length; n++) {
            this.removeCylinder(car.skidPlanes[n]);
        }

        car.skidPlanes = [];
        

        this.removeMesh(car.ChassisMesh);
        for (let n = 0; n < car.wheels.length; n++) {

            if(car.wheels[n].mesh)
                this.removeMesh(car.wheels[n].mesh);
        }

        this.removeTireBox();
        this.removeMotorBox();
        this.removeChassisBox();

        if(car.exhaustParticles)
            this.removeParticles(car.exhaustParticles);
    }

    removeRain(){

    }

    destroyObj(m){

        if(m === null)
            return;
    
        const mesh = m.meshGPU ? m.meshGPU : m;
        if(!mesh)
            return;

        if(!mesh.primitives)
            return;

        for(let p of mesh.primitives) {

            if(p.tex)
                p.tex.destroy();

            if(p.rmap)
                p.rmap.destroy();

            if(p.emap)
                p.emap.destroy();

            if(p.secmap)
                p.secmap.destroy();

            if(p.secnmap)
                p.secnmap.destroy();

            /*
            if(p.bmap)
                p.bmap.destroy();
            */
        }
        
    }

    destroyTrack(track){

        /*
        this.destroyObj(track.terrain.meshGPU);
        this.destroyObj(track.terrain.groundGPU);
        */
        this.destroyObj(track.bonusMeshGPU);

        if (track.roadObjGPU !== null) {
            this.destroyObj(track.roadObjGPU);
        }
        for (let n = 0; n < track.walls.length; n++) {

            if(track.walls[n].meshGPU)
                this.destroyObj(track.walls[n].meshGPU);
        }

        if (this.trackSpehres) {
            for (let n = 0; n < this.trackSpehres.length; n++) {
                this.destroyObj(this.trackSpehres[n]);
            }
        }

        if (track.woods) {
            for (let n = 0; n < track.woods.length; n++) {
                for (let i = 0; i < track.woods[n].objects.length; i++) {
                    this.destroyObj(track.woods[n].objects[i]);
                }
            }
        }

        if (track.bonuses) {
            for (let n = 0; n < track.bonuses.length; n++) {
                this.destroyObj(track.bonuses[n].object);
            }
        }


        if (track.interestPoints) {

            for (let n = 0; n < track.interestPoints.length; n++) {
                this.destroyObj(track.interestPoints[n].lmesh);

                if(track.interestPoints[n].wireframe) {
                    this.destroyObj(track.interestPoints[n].wireframe);
                }
            }
        }

        if (track.turrets) {
            for (let n = 0; n < track.turrets.length; n++) {
                this.destroyObj(track.turrets[n].head);

                for (let i = 0; i < track.turrets[n].shots.length; i++) {
                    this.destroyObj(track.turrets[n].shots[i]);
                }

                if(track.turrets[n].shotObject) {
                    this.destroyObj(track.turrets[n].shotObject);
                }
            }
        }

        if (track.drones) {

            for (let n = 0; n < track.drones.length; n++) {
                this.destroyObj(track.drones[n].object);

                for(let pale of track.drones[n].pales) {
                    this.destroyObj(pale.mesh);
                }
                for(let wing of track.drones[n].wings) {
                    this.destroyObj(wing);
                }

                for (let i = 0; i < track.drones[n].shots.length; i++) {
                    this.destroyObj(track.drones[n].shots[i]);
                }
            }
        }

        if (track.springs) {
            for (let n = 0; n < track.springs.length; n++) {
                this.destroyObj(track.springs[n].obj.object);
                this.destroyObj(track.springs[n].anchor.object);
            }
        }

        if (track.ropes) {
            for (let n = 0; n < track.ropes.length; n++) {
                this.destroyObj(track.ropes[n].obj.object);
                this.destroyObj(track.ropes[n].anchor.object);
            }
        }

        for(let l of track.lights){
            this.destroyObj(l);
        }

        for(let m of track.terrain.movingTerrain){
            this.destroyObj(m);
        }
        for(let m of track.terrain.waterFlows){
            this.destroyObj(m.object);
            m.object.remove(m.positionalAudio);
        }

        if(track.terrainGroup){
            this.destroyObj(track.terrainGroup);
        }

    }

    removePod() {
        /*
        if (this.podMesh)
            this.scene.remove(this.podMesh);
        */
    }

    addCarObj(car) {

        this.sc = false;
        
        const mi = car.getMeshInfos();

        car.ChassisMesh.quaternion.set(car.quat.x, car.quat.y, car.quat.z, car.quat.w);
        car.ChassisMesh.position.set(car.pos.x, car.pos.y + mi.chassisOffset, car.pos.z);
        car.ChassisMesh.meshGPU = this.addMesh(car.ChassisMesh, RT_CARCOAT, new Transformation3(car.ChassisMesh.position, car.ChassisMesh.quaternion));

        this.setMeshStatic(car.ChassisMesh, false);

        car.ChassisMesh.castShadow = true;
        car.ChassisMesh.receiveShadow = true;

        car.target = {id : this.targetID++, type : 'car', name : car.name ? car.name: 'car-'+car.id, subsystems: [], pos: { x: car.ChassisMesh.position.x, y: car.ChassisMesh.position.y, z : car.ChassisMesh.position.z}, txt: null};
        const geo = new TextGeometry(car.target.name, { font: this.impactFont, size: 0.1, height: 0.1, curveSegments: 4});
        geo.computeBoundingSphere();
        geo.translate(-geo.boundingSphere.radius, 0, 0);
        car.target.txt = new THREE.Mesh(geo, this.targetMat);
        

        for (let n = 0; n < car.wheels.length; n++) {

            const s =  {x: car.wheels[n].pos.x < 0 ? false : true, y: false, z : false};
            car.wheels[n].mesh.scale.set(car.wheels[n].mesh.scale.x,1,1);
            car.wheels[n].mesh.meshGPU = this.addMesh(car.wheels[n].mesh, RT_DIFF, new Transformation3(car.wheels[n].pos, car.wheels[n].quat), s);

            this.setMeshStatic(car.wheels[n].mesh, false);
        }

        const t = new Transformation3();


        if(car.weapon){
            const weapon = car.weapon;

            
            
            weapon.canon.position.sub(weapon.head.position);
            t.set(weapon.canon.position);
            this.updateTransform(weapon.canon, t);

            weapon.head.position.sub(weapon.body.position);
            t.set(weapon.head.position);
            this.updateTransform(weapon.head, t);
            

            this.addMeshChild(car.ChassisMesh, weapon.body);
            this.addMeshChild(weapon.body, weapon.head);
            this.addMeshChild(weapon.head, weapon.canon);
            

            car.weapon.shotSphere = {};
            
            this.addSphere(car.weapon.shotSphere,
                car.weapon.projSize,
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(0.2, 0.8, 0.8),
                new THREE.Vector3(0.2, 0.8, 0.8),
                0.2,
                RT_SPEC
            );
        }

        if(car.radar){

            const radar = car.radar;
            //radar.meshGPU = this.addMesh(radar, RT_DIFF, new Transformation3(radar.position));
            t.set(car.radar.position);
            this.updateTransform(car.radar, t);

            this.addMeshChild(car.ChassisMesh, radar);
        }

        return true;
    }

    async addCar(myCar, mode, carColor = 0) {

        await this.loadCar(myCar, mode);
        await this.addCarObj(myCar);


        const geo = new TextGeometry(myCar.target.name, { font: this.speedFont, size: 0.8, height: 0.1, curveSegments: 4});
        geo.computeBoundingSphere();
        geo.translate(-geo.boundingSphere.radius, 0, 0);

        myCar.color = new THREE.Color().setHex(carColors[carColor]);

        myCar.nameMat = new THREE.MeshPhongMaterial({color : myCar.color.clone(), emissive: myCar.color.clone(), emissiveIntensity : 4.0})
        myCar.nameMesh = new THREE.Mesh(geo, myCar.nameMat);

        myCar.nameMesh.meshGPU = this.addMesh(myCar.nameMesh);

        this.setMeshStatic(myCar.nameMesh,false);

        const epos = new THREE.Vector3();
        myCar.getExhaustPos(epos)

        myCar.exhaustParticles = new webGPUParticleMesh(smoke_params, epos , 0.2);
        myCar.exhaustParticles.particles.pinned = true; 
        this.addParticles(myCar.exhaustParticles);

          for(let w of myCar.wheels){
            w.particleMesh = new webGPUParticleMesh(drift_params, w.pos , 0.2); //new ParticleEngine(w.pos);
            w.parts = w.particleMesh.particles;
            w.parts.name = 'parts-wheel';
            w.parts.pinned = true;

             this.addParticles(w.particleMesh);

            //this.particles.push(w.parts);
        }

        this.addSpotLight(myCar.CarLight,
            new THREE.Vector3(myCar.pos.x, myCar.pos.y, myCar.pos.z),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(0.8, 0.8, 0.0).multiplyScalar(1),
            32.5,
            1.0);
        
    }

    async setCar(myCar, mode) {

        await this.loadCar(myCar, mode);
        await this.addCarObj(myCar);

        const epos = new THREE.Vector3();
        myCar.getExhaustPos(epos)

        myCar.exhaustParticles = new webGPUParticleMesh(smoke_params, epos, 0.2);
        this.addParticles(myCar.exhaustParticles);

        if((this.podMesh)&&(this.podMesh.meshGPU))
            this.podMesh.meshGPU.transformations[0].position.y = myCar.wheels[0].pos.y - myCar.wheelRadius - 0.25;
    }


    interpolateRagDoll(skel, bone, lerper, time, instanceID = 0){

        const mybone = this.getCharBone(skel, bone.name);
        if(mybone){
            if(lerper != null){
                
                lerper.getInterpolatedValue(bone.idx, time, mybone.transforms[instanceID]);
                mybone.transforms[instanceID].computeMatrix();
                bone.life = mybone.transforms[instanceID].life;

            } else { 

                if(bone.pos)
                    mybone.transforms[instanceID].position.set(bone.pos.x, bone.pos.y, bone.pos.z);
                
                mybone.transforms[instanceID].quaternion.set(bone.rot.x, bone.rot.y, bone.rot.z, bone.rot.w);
            }
        }

        for(let n=0;n<bone.children.length;n++){
            this.interpolateRagDoll(skel, bone.children[n], lerper, time, instanceID);
        }

        return;
    }



    addCharObj(char) {

        char.target = {id : this.targetID++, type : 'npc', name : 'npc-'+char.id, subsystems: [], pos: { x: char.pos.x, y: char.pos.y, z :char.pos.z}};

        if(char.skeleton){
         
            char.originalMesh = char.avatarMesh.clone();

            const geo = this.doMeshSkinning(char.originalMesh.geometry, char.skeleton);
            const newMesh = new THREE.Mesh(geo, char.originalMesh.material);

            newMesh.name = char.originalMesh.name;
            newMesh.castShadow = char.originalMesh.castShadow;
            char.avatarMesh = newMesh;
            char.avatarMesh.meshGPU = this.addMesh(newMesh, RT_DIFF, new Transformation3(), {x: false, y: false, z: false}, true, {skeleton : char.skeleton, skale : char.skelScale}, char.nInstances);
            char.avatarMesh.meshGPU.updateBones = true;
            this.setMeshStatic(char.avatarMesh, false);
        }else{
            char.avatarMesh.meshGPU = this.addMesh(char.avatarMesh, RT_DIFF, new Transformation3(char.pos, char.rot), {x: false, y: false, z: false}, true, null, char.nInstances);
            this.setMeshStatic(char.avatarMesh, false);
        }

        if((char.subsystemsDef)&&(char.skeleton)){
            this.findSubsystemsBone(char.skeleton.root, char.subsystemsDef, char.target.subsystems);
            for(let s of char.target.subsystems){

                this.addDMGBar(s.dmgBar);

                //s.DMGBar1.meshGPU = this.addMesh(s.DMGBar1);
                //s.DMGBar2.meshGPU = this.addMesh(s.DMGBar2);
            }
        }
        
    }

    removeChar(char) {

        if (char === null)
            return;

        this.removeMesh(char.avatarMesh);
    }
    
    async addChar(myChar){

        await this.loadCharacter(myChar);
        this.addCharObj(myChar);
    }

    async setChar(myChar){

        await this.loadCharacter(myChar);
        this.addCharObj(myChar);
    }
    scene;
    camFollowCar;
}